import minimatch from 'minimatch';
import _, { merge as merge$1, capitalize } from 'lodash';
import moment from 'moment';
import numeral from 'numeral';
import hr from 'hex-rgb';
import fs from 'fs';
import axios from 'axios';

// const iso8601check = /^([\\+-]?\d{4}(?!\d{2}\b))((-?)((0[1-9]|1[0-2])(\3([12]\d|0[1-9]|3[01]))?|W([0-4]\d|5[0-2])(-?[1-7])?|(00[1-9]|0[1-9]\d|[12]\d{2}|3([0-5]\d|6[1-6])))([T\s]((([01]\d|2[0-3])((:?)[0-5]\d)?|24:?00)([.,]\d+(?!:))?)?(\17[0-5]\d([.,]\d+)?)?([zZ]|([\\+-])([01]\d|2[0-3]):?([0-5]\d)?)?)?)?$/
/**
 * Reasons behind this small snippet:
 *  - backend is using uint64 as prefered type for handling CortezaID (of all things)
 *  - JavaScript can not (without external help) deal with uint64
 *  - Backend's JSON marshaller converts uint64 to string
 *  - Backend's JSON unmarshaller raises error when given anything but string with a number inside
 *
 *  Until this last thing is fixed or has a proper workaround, we're stuck with this.
 */
const NoID = '0';
function ISO8601Date(ts) {
    if (ts instanceof Date) {
        return ts;
    }
    if (typeof ts === 'string' || typeof ts === 'number') {
        return new Date(ts);
    }
    return undefined;
}
/**
 * Tests if a given value looks like corteza ID
 * @param ID
 * @constructor
 */
function IsCortezaID(ID) {
    if (typeof ID !== 'string') {
        return false;
    }
    if (!/^\d+$/.test(ID)) {
        return false;
    }
    return true;
}
/**
 * @return {string}
 */
function CortezaID(value) {
    if (!value) {
        return NoID;
    }
    if (typeof value === 'number') {
        return value.toString();
    }
    if (IsCortezaID(value)) {
        return value;
    }
    throw new Error('Invalid CortezaID value');
}
/**
 * Apply takes all given props, their values (from src) and assignes them to props (on dst)
 *
 * A casting function can be used (see ApplyCaster) to modify the values before assigning them
 */
function Apply(dst, src, cast, ...props) {
    if (typeof cast !== 'function') {
        // Handle case where we do not use caster
        props.unshift(cast);
        // and use String as a caster, effectively forcing
        // cast-to-string on all applied values to
        cast = String;
    }
    if (typeof src !== 'object') {
        return;
    }
    props.forEach(prop => {
        // prop must exist on dst
        if (!Object.prototype.hasOwnProperty.call(dst, prop)) {
            return;
        }
        // prop must exist on src
        if (!Object.prototype.hasOwnProperty.call(src, prop)) {
            return;
        }
        // sProp is prop from source
        const sProp = prop;
        // value on src should be defined
        if (src[sProp] === undefined || src[sProp] === null) {
            return;
        }
        // Cast value from src to type of value from prop on dst
        let val = src[sProp];
        // Run value through cast fn
        val = cast(val);
        if (val === undefined) {
            return;
        }
        // Assign (valid ony) value to dst
        dst[prop] = val;
    });
}
function ApplyWhitelisted(dst, src, whitelist, ...props) {
    if (typeof src !== 'object') {
        return;
    }
    props.forEach(prop => {
        // prop must exist on dst
        if (!Object.prototype.hasOwnProperty.call(dst, prop)) {
            return;
        }
        // prop must exist on src
        if (!Object.prototype.hasOwnProperty.call(src, prop)) {
            return;
        }
        // sProp is prop from source
        const sProp = prop;
        // value on src should be defined
        if (src[sProp] === undefined) {
            return;
        }
        // Cast value from src to type of value from prop on dst
        const val = src[sProp];
        if (whitelist.includes(val)) {
            dst[prop] = val;
        }
    });
}

const IsOf = (v, ...props) => {
    if (!v || typeof v !== 'object') {
        return false;
    }
    for (const prop of props) {
        if (!Object.prototype.hasOwnProperty.call(v, prop)) {
            return false;
        }
    }
    return true;
};
// eslint-disable-next-line valid-typeof
const every = (a, t) => Array.isArray(a) && a.every(i => typeof i === t);
const AreStrings = (a) => every(a, 'string');
const AreObjects = (a) => every(a, 'object');
function AreObjectsOf(a, ...props) {
    if (!a || !Array.isArray(a)) {
        return false;
    }
    if (a.length === 0) {
        return true;
    }
    return AreObjects(a) && a.every(i => IsOf(i, ...props));
}

class Equal {
    constructor(name, vv, not = false) {
        this.name = name;
        this.values = vv;
        this.not = not;
    }
    Name() {
        return this.name;
    }
    Values() {
        return this.values;
    }
    Match(value) {
        for (const v of this.values) {
            if (value === v) {
                return !this.not;
            }
        }
        return this.not;
    }
}
/**
 * Handle glob-like pattern matching
 *
 * See: https://github.com/isaacs/minimatch
 */
class Like extends Equal {
    constructor(name, vv, not = false) {
        super(name, vv.map(v => v.replace('%', '*').replace('_', '?')), not);
    }
    Match(value) {
        for (const v of this.values) {
            if (minimatch(value, v)) {
                return !this.not;
            }
        }
        return this.not;
    }
}
/**
 * Regex matcher
 */
class Match extends Equal {
    constructor(name, vv, not = false) {
        super(name, vv, not);
        this.re = vv.map(v => new RegExp(v));
    }
    Match(value) {
        for (const re of this.re) {
            if (re.test(value)) {
                return !this.not;
            }
        }
        return this.not;
    }
}
function ConstraintMaker(c) {
    if (!IsOf(c, 'value')) {
        throw new Error('invalid constraint input');
    }
    const { name = '', op = '', value } = c;
    switch (op.toLowerCase()) {
        case '':
        case 'eq':
        case '=':
        case '==':
        case '===':
            return new Equal(name, value);
        case 'not eq':
        case 'ne':
        case '!=':
        case '!==':
            return new Equal(name, value, true);
        case 'like':
            return new Like(name, value);
        case 'not like':
            return new Like(name, value, true);
        case '~':
            return new Match(name, value);
        case '!~':
            return new Match(name, value, true);
        default:
            throw new Error('unsupported constraint operator');
    }
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

class Handler {
    constructor(h, t) {
        this.handle = h;
        this.eventTypes = t.eventTypes;
        this.resourceTypes = t.resourceTypes;
        this.weight = t.weight || 0;
        // @todo parse constraints to constraint matchers
        this.constraints = t.constraints ? t.constraints.map(ConstraintMaker) : [];
        this.scriptName = t.scriptName;
    }
    /**
     * Match this handler with a given event - type, resource, constraints + scriptName when ManualEvent
     *
     * @param {Event} ev
     * @return bool
     */
    Match(ev, script) {
        if (!this.eventTypes.includes(ev.eventType)) {
            return false;
        }
        if (!this.resourceTypes.includes(ev.resourceType)) {
            return false;
        }
        if (script && this.scriptName !== script) {
            return false;
        }
        if (ev.match) {
            // Event should match all trigger's constraints
            for (const c of this.constraints) {
                if (!ev.match(c)) {
                    return false;
                }
            }
        }
        return true;
    }
    Handle(ev) {
        return this.handle(ev);
    }
}

const onManual = 'onManual';
function scriptSorter(a, b) {
    return a.weight - b.weight;
}
function GenericEventMaker(t, eventType, match, args) {
    return {
        resourceType: t.resourceType,
        eventType,
        match,
        args,
    };
}

/**
 * EventBus handles all Corteza events on browser (!! not on corredor server !!)
 *
 * Flow #1
 *  1. Corredor prepares a bundle that is loaded on a client
 *  2. Bundle provides a "callback" function that accepts EventBus object +
 *     all context information and configuration that is needed for
 *     handler registration
 * 3a. When a "Corteza event" is dispatched (Dispatch())
 *     event-bus searches for handler and passes on event information
 * 3b. When manual event is executed (Exec())
 *     event-bus searches for handler and passes on event information
 *
 * Flow #2
 *   1. When web application is initialized, it should register all
 *      explicit server scripts
 *   2. These server scripts are wrapped with a handlerFn that forwards call
 *      to the API (there, request is passed to the Corredor where it's executed)
 *
 */
/**
 * EventBus for event dispatching and handling
 *
 * Since we have much shorter execution path here than we have in case of server scripts,
 * we can afford some optimisation (in comparison to backend's pkg/eventbus)
 */
class EventBus {
    constructor(opt) {
        var _a, _b, _c;
        this.handlers = [];
        this.pairs = ((_a = opt) === null || _a === void 0 ? void 0 : _a.pairs) || {};
        this.strict = !!((_b = opt) === null || _b === void 0 ? void 0 : _b.strict);
        this.verbose = !!((_c = opt) === null || _c === void 0 ? void 0 : _c.verbose);
    }
    /**
     * Dispatches event and sequentially calls all handlers.
     *
     * Handling handler results works a bit different then on backend.
     * Scripts executed with handlers have DIRECT access to values passed (by reference)
     * as arguments via event so there's no need to do an explicit return
     *
     * @param {Event} ev Event to dispatch
     */
    Dispatch(ev, script) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.verbose)
                console.debug('EventBus: event dispatched', { ev, script });
            if (script) {
                if (ev.eventType !== onManual) {
                    console.warn('EventBus: explicit events require onManual event type', ev);
                    return null;
                }
            }
            else {
                if (ev.eventType === onManual) {
                    console.warn('EventBus: implicit events can not define onManual event type', ev);
                    return null;
                }
            }
            this.checkPairs([ev.resourceType], [ev.eventType]);
            const matched = this.find(ev, script);
            if (matched.length === 0) {
                if (this.verbose)
                    console.debug('EventBus: no handlers found', { ev, script, registeredHandlers: this.handlers.length });
                return null;
            }
            if (script) {
                // When executing a specific script,
                // make sure we do not run it multiple times.
                matched.splice(1);
            }
            try {
                for (const t of matched) {
                    if (this.verbose)
                        console.debug('EventBus: handling event', { ev, trigger: t, script });
                    const result = yield t.Handle(ev);
                    if (result === false) {
                        return Promise.reject(new Error('aborted'));
                    }
                }
            }
            catch (err) {
                return Promise.reject(err);
            }
            return null;
        });
    }
    /**
     * Filters and sorts all handlers by event & constraints
     */
    find(ev, script) {
        return this.handlers
            .filter(t => t.Match(ev, script))
            .sort(scriptSorter);
    }
    /**
     * Registers Event handler
     *
     * @param handler Handler function
     * @param trigger Trigger definition
     */
    Register(handler, trigger) {
        if (this.verbose)
            console.debug('EventBus: event handler registration for', trigger.scriptName, { trigger });
        this.handlers.push(new Handler(handler, trigger));
        return this;
    }
    /**
     * Unregisters all handlers
     */
    UnregisterAll() {
        this.handlers = [];
        return this;
    }
    checkPairs(resourceTypes, eventTypes) {
        if (this.pairs === undefined || !this.strict) {
            return;
        }
        resourceTypes.forEach(resourceType => {
            const wket = this.pairs[resourceType];
            if (wket === undefined) {
                throw new TypeError('unknown resource type "' + resourceType + "'");
            }
            eventTypes.forEach(eventTypes => {
                if (!wket.includes(eventTypes)) {
                    throw new TypeError('unknown event type "' + eventTypes + '" for "' + resourceType + '" resource type');
                }
            });
        });
    }
}



var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  ConstraintMaker: ConstraintMaker,
  EventBus: EventBus
});

/**
 * Script executor
 *
 * @param script - Script to be executed
 * @param args - Arguments for the script
 * @param ctx - Exec context (exec function's 2nd param)
 */
function Exec(script, args, ctx) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Wrap exec() with Promise.resolve - we do not know if function is async or not.
            return Promise.resolve(script.exec(args, ctx)).then((rval) => {
                let result = {};
                if (rval === false) {
                    // Abort when returning false!
                    throw new Error('Aborted');
                }
                if (typeof rval === 'object' && rval && rval.constructor.name === 'Object') {
                    // Expand returned values into result if function returned a plain javascript object
                    result = Object.assign({}, rval);
                }
                else if (rval !== undefined) {
                    // If anything usable was returned, stack it under 'result' property
                    result = { result: rval };
                }
                // Wrap returning value
                return result;
            });
        }
        catch (e) {
            return Promise.reject(e);
        }
    });
}

function kv(a) { return a; }
/**
 * Extracts ID-like (numeric) value from string or object
 *
 * @param value - that stores ID in some way
 * @param prop - possible key lookup
 */
function extractID(value, prop) {
    if (value && typeof value === 'object') {
        if (!prop || !Object.prototype.hasOwnProperty.call(value, prop)) {
            return NoID;
        }
        value = value[prop];
    }
    return CortezaID(value);
}
function isFresh(ID) {
    return !ID || ID === NoID;
}
function genericPermissionUpdater(API, rules) {
    const g = rules.reduce((acc, p) => {
        if (!acc[p.role.roleID]) {
            acc[p.role.roleID] = [];
        }
        acc[p.role.roleID].push({
            resource: p.resource.resourceID,
            operation: p.operation,
            access: p.access,
        });
        return acc;
    }, {});
    // @todo should return promise and stack all these into Promise.all()
    Object.keys(g).forEach((roleID) => __awaiter(this, void 0, void 0, function* () {
        // permissions grouped per role
        yield API.permissionsUpdate({ roleID, rules: g[roleID] });
    }));
}

class User {
    constructor(u) {
        this.userID = NoID;
        this.handle = '';
        this.username = '';
        this.email = '';
        this.name = '';
        this.emailConfirmed = false;
        this.labels = {};
        this.meta = {
            preferredLanguage: 'en',
            securityPolicy: {
                mfa: {
                    enforcedEmailOTP: false,
                    enforcedTOTP: false,
                },
            },
        };
        this.createdAt = undefined;
        this.updatedAt = undefined;
        this.deletedAt = undefined;
        this.suspendedAt = undefined;
        this.apply(u);
    }
    apply(u) {
        var _a;
        Apply(this, u, CortezaID, 'userID');
        Apply(this, u, String, 'handle', 'username', 'email', 'name');
        Apply(this, u, ISO8601Date, 'createdAt', 'updatedAt', 'deletedAt', 'suspendedAt');
        Apply(this, u, Boolean, 'emailConfirmed');
        if ((_a = u) === null || _a === void 0 ? void 0 : _a.roles) {
            this.roles = [];
            if (AreStrings(u.roles)) {
                this.roles = u.roles;
            }
        }
        if (IsOf(u, 'meta')) {
            this.meta = Object.assign({}, u.meta);
        }
        if (IsOf(u, 'labels')) {
            this.labels = Object.assign({}, u.labels);
        }
    }
    /**
     * Returns resource ID
     */
    get resourceID() {
        return `${this.resourceType}:${this.userID}`;
    }
    /**
     * Resource type
     */
    get resourceType() {
        return 'system:user';
    }
    get fts() {
        return [
            this.name,
            this.username,
            this.handle,
            this.email,
            this.userID,
        ].join(' ').toLocaleLowerCase();
    }
}

const defaultMeta = {
    description: '',
    context: {
        resourceTypes: [],
        expr: '',
    },
};
class Role {
    constructor(r) {
        this.roleID = NoID;
        this.name = '';
        this.handle = '';
        this.members = [];
        this.labels = {};
        this.meta = defaultMeta;
        this.createdAt = undefined;
        this.updatedAt = undefined;
        this.deletedAt = undefined;
        this.archivedAt = undefined;
        this.isSystem = false;
        this.isClosed = false;
        this.isBypass = false;
        this.canGrant = false;
        this.canUpdateRole = false;
        this.canDeleteRole = false;
        this.canManageMembersOnRole = false;
        this.apply(r);
    }
    apply(r) {
        var _a;
        Apply(this, r, CortezaID, 'roleID');
        Apply(this, r, String, 'name', 'handle');
        if ((_a = r) === null || _a === void 0 ? void 0 : _a.members) {
            this.members = [];
            if (AreStrings(r.members)) {
                this.members = r.members;
            }
        }
        if (IsOf(r, 'meta')) {
            this.meta = Object.assign({}, r.meta);
        }
        if (!this.meta) {
            this.meta = Object.assign({}, defaultMeta);
        }
        if (!this.meta.context) {
            this.meta.context = Object.assign({}, defaultMeta.context);
        }
        if (IsOf(r, 'labels')) {
            this.labels = Object.assign({}, r.labels);
        }
        Apply(this, r, ISO8601Date, 'createdAt', 'updatedAt', 'deletedAt', 'archivedAt');
        Apply(this, r, Boolean, 'isSystem', 'isClosed', 'isBypass', 'canGrant', 'canUpdateRole', 'canDeleteRole', 'canManageMembersOnRole');
    }
    /**
     * Returns resource ID
     */
    get resourceID() {
        return `${this.resourceType}:${this.roleID}`;
    }
    /**
     * Resource type
     */
    get resourceType() {
        return 'system:role';
    }
    get isContext() {
        var _a, _b, _c;
        return ((_c = (_b = (_a = this.meta) === null || _a === void 0 ? void 0 : _a.context) === null || _b === void 0 ? void 0 : _b.expr) === null || _c === void 0 ? void 0 : _c.length) > 0;
    }
}

class Application {
}

class SinkRequest {
    constructor(r = {}) {
        this.method = '';
        this.path = '';
        this.host = '';
        this.header = {};
        this.query = {};
        this.postForm = {};
        this.username = '';
        this.password = '';
        this.remoteAddress = '';
        this.rawBody = '';
        merge$1(this, r);
    }
}
class SinkResponse {
    constructor(r = {}) {
        this.status = 200;
        this.header = {};
        merge$1(this, r);
    }
}

class Reminder {
    constructor(r) {
        this.reminderID = NoID;
        this.resource = NoID;
        this.payload = {};
        this.snoozeCount = 0;
        this.assignedTo = NoID;
        this.assignedBy = NoID;
        this.assignedAt = undefined;
        this.dismissedBy = NoID;
        this.dismissedAt = undefined;
        this.remindAt = undefined;
        this.createdAt = undefined;
        this.processed = false;
        this.actions = {};
        this.options = {};
        this.apply(r);
    }
    apply(r) {
        if (!r)
            return;
        Apply(this, r, CortezaID, 'reminderID');
        Apply(this, r, Number, 'snoozeCount');
        Apply(this, r, CortezaID, 'assignedTo', 'assignedBy', 'dismissedBy');
        // @todo actions, options, payload... all 3?
        this.payload = merge$1({}, this.payload, r.payload);
        this.actions = merge$1({}, this.actions, r.actions);
        this.options = merge$1({}, this.options, r.options);
        Apply(this, r, ISO8601Date, 'assignedAt', 'dismissedAt', 'remindAt', 'createdAt');
        Apply(this, r, Boolean, 'processed');
    }
}

class Template {
    constructor(r) {
        this.templateID = NoID;
        this.handle = '';
        this.language = '';
        this.type = 'text/html';
        this.partial = false;
        this.meta = {};
        this.template = '';
        this.labels = {};
        this.ownerID = NoID;
        this.createdAt = undefined;
        this.updatedAt = undefined;
        this.deletedAt = undefined;
        this.lastUsedAt = undefined;
        this.apply(r);
    }
    apply(r) {
        Apply(this, r, CortezaID, 'templateID', 'ownerID');
        Apply(this, r, String, 'handle', 'language', 'type', 'template');
        Apply(this, r, Boolean, 'partial');
        if (r && IsOf(r, 'meta')) {
            this.meta = r.meta;
        }
        Apply(this, r, ISO8601Date, 'createdAt', 'updatedAt', 'deletedAt', 'lastUsedAt');
        if (IsOf(r, 'labels')) {
            this.labels = Object.assign({}, r.labels);
        }
    }
    /**
     * Returns resource ID
     */
    get resourceID() {
        return `${this.resourceType}:${this.templateID}`;
    }
    /**
     * Resource type
     */
    get resourceType() {
        return 'system:template';
    }
}

const Registry = new Map();
class DisplayElement {
    constructor(de = {}) {
        this.name = '';
        this.description = '';
        this.options = {};
        this.meta = {
            size: undefined,
        };
        this.kind = '';
        this.apply(de);
    }
    apply(de) {
        if (!de)
            return;
        Apply(this, de, String, 'name', 'description');
        if (de.options) {
            this.options = merge$1({}, this.options, de.options);
        }
        if (de.meta) {
            this.meta = merge$1({}, this.meta, de.meta);
        }
    }
}

const kind = 'Chart';
function ChartOptionsMaker(options) {
    const { type } = options;
    if (type) {
        const ChartOptionsTemp = ChartOptionsRegistry.get(type);
        if (ChartOptionsTemp === undefined) {
            throw new Error(`unknown chart type '${type}'`);
        }
        if (options instanceof ChartOptions) {
            // Get rid of the references
            options = JSON.parse(JSON.stringify(options));
        }
        return new ChartOptionsTemp(options);
    }
    else {
        throw new Error(`no chart type`);
    }
}
const ChartOptionsRegistry = new Map();
class ChartOptions {
    constructor(o = {}) {
        this.title = '';
        this.type = 'bar';
        this.colorScheme = '';
        this.source = '';
        this.datasources = [];
        this.showTooltips = true;
        this.showLegend = true;
        this.xAxis = {
            type: '',
            skipMissing: true,
        };
        this.yAxis = {
            type: 'linear',
            position: 'left',
            beginAtZero: true,
        };
        if (!o)
            return;
        Apply(this, o, String, 'title', 'type', 'colorScheme', 'source');
        Apply(this, o, Boolean, 'showTooltips', 'showLegend');
        if (o.datasources) {
            this.datasources = o.datasources;
        }
        if (o.xAxis) {
            this.xAxis = Object.assign(Object.assign({}, this.xAxis), o.xAxis);
        }
        if (o.yAxis) {
            this.yAxis = Object.assign(Object.assign({}, this.yAxis), o.yAxis);
        }
    }
}
class DisplayElementChart extends DisplayElement {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind;
        this.options = ChartOptionsMaker({ type: 'bar' });
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        this.options = ChartOptionsMaker(o);
    }
    reportDefinitions(definition) {
        if (typeof this.options.source === 'object') {
            // @todo allow implicit sources
            throw new Error('chart source must be provided as a reference');
        }
        const dataframes = [];
        this.options.datasources.forEach(({ name = '', filter, sort }) => {
            var _a;
            const df = {
                name: this.name,
                source: this.options.source,
                ref: name,
                filter,
                sort,
            };
            const relatedDefinition = definition[name];
            if (relatedDefinition) {
                df.sort = (relatedDefinition.sort ? relatedDefinition.sort : sort) || undefined;
                if (relatedDefinition.filter && ((_a = relatedDefinition.filter) === null || _a === void 0 ? void 0 : _a.ref)) {
                    // If element and scenario have filter AND them together
                    if (filter && filter.ref) {
                        df.filter = {
                            ref: 'and',
                            args: [
                                filter,
                                relatedDefinition.filter,
                            ]
                        };
                    }
                    else {
                        df.filter = relatedDefinition.filter;
                    }
                }
            }
            dataframes.push(df);
        });
        return { dataframes };
    }
}
Registry.set(kind, DisplayElementChart);

class BasicChartOptions extends ChartOptions {
    constructor(o) {
        super(o);
        this.labelColumn = '';
        this.dataColumns = [];
        if (!o)
            return;
        Apply(this, o, String, 'labelColumn');
        if (o.dataColumns) {
            this.dataColumns = o.dataColumns || [];
        }
    }
    getChartConfiguration(dataframes) {
        const config = {
            type: this.type,
            data: this.getData(dataframes[0], dataframes),
            options: {
                title: {
                    display: !!this.title,
                    text: this.title,
                },
                legend: {
                    display: this.showLegend
                },
                responsive: true,
                maintainAspectRatio: false,
                scales: {},
                tooltips: {
                    enabled: this.showTooltips,
                    displayColors: false,
                    intersect: !['bar', 'line'].includes(this.type),
                    callbacks: {},
                },
                plugins: {
                    colorschemes: {
                        scheme: this.colorScheme,
                        reverse: true,
                    },
                },
            }
        };
        if (['bar', 'line'].includes(this.type)) {
            const { label: xLabel, type: xType, unit, } = this.xAxis;
            const { label: yLabel, type: yType = 'linear', position = 'left', beginAtZero = true, stepSize, min, max, } = this.yAxis;
            config.options.scales = {
                xAxes: [{
                        type: xType || undefined,
                        offset: true,
                        time: {
                            unit,
                            round: true,
                            minUnit: 'day',
                        },
                        scaleLabel: {
                            display: !!xLabel,
                            labelString: xLabel,
                        },
                        ticks: {
                            autoSkip: false,
                        }
                    }],
                yAxes: [{
                        display: true,
                        type: yType,
                        position,
                        scaleLabel: {
                            display: !!yLabel,
                            labelString: yLabel,
                        },
                        ticks: {
                            beginAtZero,
                            stepSize: stepSize ? parseFloat(stepSize) : undefined,
                            min: min ? parseFloat(min) : undefined,
                            max: max ? parseFloat(max) : undefined,
                        },
                    }],
            };
        }
        else {
            config.options.tooltips.callbacks = {
                label: this.makeLabel,
            };
        }
        return config;
    }
    getColIndex(dataframe, col) {
        if (!dataframe || !dataframe.columns)
            return -1;
        return dataframe.columns.findIndex(({ name }) => name === col);
    }
    makeLabel({ datasetIndex, index }, { datasets, labels }) {
        const dataset = datasets[datasetIndex];
        const total = dataset.data.reduce((acc, v) => acc + parseFloat(v), 0);
        let suffix = `(${total.toFixed(2)})%`;
        if (total) {
            suffix = `(${((dataset.data[index] * 100) / total).toFixed(2)}%)`;
        }
        return `${labels[index]}: ${dataset.data[index]} ${suffix}`;
    }
    getData(localDataframe, dataframes) {
        const datasets = [];
        let labels = [];
        // Get datasets
        if (this.dataColumns.length && localDataframe.rows) {
            for (const { name } of this.dataColumns) {
                // Assume localDataframe has the dataColumn
                let columnIndex = this.getColIndex(localDataframe, name);
                // If dataColumn is in localDataframe, then set that value
                let data = localDataframe.rows.map(r => {
                    return columnIndex < 0 ? undefined : r[columnIndex];
                });
                if (columnIndex < 0) {
                    dataframes.slice(1).forEach(df => {
                        const { relColumn = '', refValue = '' } = df;
                        // Get column that is referenced by relColumn
                        const relColumnIndex = this.getColIndex(localDataframe, relColumn);
                        if (relColumnIndex < 0) {
                            throw new Error(`Column ${relColumn} not found`);
                        }
                        if (!localDataframe.rows) {
                            throw new Error(`Local rows not found`);
                        }
                        // Get row index that matches refValue
                        const refRowIndex = localDataframe.rows.findIndex(row => row[relColumnIndex] === refValue);
                        if (refRowIndex < 0) {
                            throw new Error(`Row that matches refRowIndex ${refValue} not found`);
                        }
                        columnIndex = this.getColIndex(df, name);
                        if (columnIndex < 0) {
                            throw new Error(`Column ${name} not found`);
                        }
                        else if (df.rows) {
                            data[refRowIndex] = df.rows[0][columnIndex];
                        }
                    });
                }
                datasets.push({
                    label: name,
                    data,
                });
            }
        }
        // Get labels, if dimensions type is not time
        if (this.labelColumn && localDataframe) {
            const columnIndex = this.getColIndex(localDataframe, this.labelColumn);
            if (columnIndex < 0) {
                throw new Error(`Column ${this.labelColumn} not found`);
            }
            if (localDataframe.rows) {
                for (const row of localDataframe.rows) {
                    let label = row[columnIndex] || (!this.xAxis.skipMissing ? this.xAxis.defaultValue : undefined);
                    if (this.xAxis.type === 'time') {
                        label = label ? moment(label).toDate() : undefined;
                    }
                    labels.push(label);
                }
            }
        }
        if (this.xAxis.skipMissing) {
            labels.forEach((label, index) => {
                if (!label) {
                    datasets.forEach(ds => {
                        ds.data.splice(index, 1);
                    });
                }
            });
            labels = labels.filter(label => label);
        }
        return { datasets, labels };
    }
}
ChartOptionsRegistry.set('bar', BasicChartOptions);
ChartOptionsRegistry.set('line', BasicChartOptions);
ChartOptionsRegistry.set('pie', BasicChartOptions);
ChartOptionsRegistry.set('doughnut', BasicChartOptions);

function makeTipper(Tooltip, options = {}) {
    return {
        id: 'tipper',
        beforeRender: (chart) => {
            chart.options.tooltips.enabled = true;
            // build a tooltip for each data set data node
            const tipperTips = chart.config.data.datasets.map((_, i) => {
                // ignore if dataset is hidden
                if (!chart.isDatasetVisible(i)) {
                    return undefined;
                }
                // getDatasetMeta provides element selector; dataset does not
                return chart.getDatasetMeta(i).data.map((sc) => {
                    const opts = Object.assign(Object.assign(Object.assign({}, chart.options.tooltips), { 
                        // force these values
                        displayColors: false, backgroundColor: 'rgba(255,255,255,0.5)' }), options);
                    opts.callbacks = Object.assign(Object.assign({}, opts.callbacks), { labelTextColor: function () {
                            return '#000000';
                        }, 
                        // We should only show the value when doing this
                        beforeTitle: () => '', title: () => '', afterTitle: () => '', beforeBody: () => '', beforeLabel: () => '', afterLabel: () => '', afterBody: () => '', beforeFooter: () => '', footer: () => '', afterFooter: () => '' });
                    return new Tooltip({
                        _chart: chart.chart,
                        _chartInstance: chart,
                        _data: chart.data,
                        _options: opts,
                        _active: [sc],
                    }, chart);
                });
            });
            chart.config.tipperTips = tipperTips.filter((tt) => tt);
            chart.options.tooltips.enabled = false;
        },
        afterDraw: (chart, easing) => {
            // enable for drawing window
            chart.options.tooltips.enabled = true;
            chart.config.tipperTips.forEach((ds) => {
                ds.forEach(tt => {
                    tt.initialize();
                    tt.update();
                    tt.transition(easing).draw();
                });
            });
            chart.options.tooltips.enabled = false;
        },
    };
}

const ChartJS = require('chart.js');
class FunnelChartOptions extends ChartOptions {
    constructor(o) {
        super(o);
        this.labelColumn = '';
        this.dataColumns = [];
        if (!o)
            return;
        Apply(this, o, String, 'labelColumn');
        if (o.dataColumns) {
            this.dataColumns = o.dataColumns || [];
        }
    }
    getChartConfiguration(dataframes) {
        const config = {
            type: this.type,
            data: {
                labels: this.getLabels(dataframes[0]),
                datasets: this.getDatasets(dataframes[0], dataframes),
            },
            options: {
                title: {
                    display: !!this.title,
                    text: this.title,
                },
                legend: {
                    display: this.showLegend
                },
                responsive: true,
                maintainAspectRatio: false,
                sort: 'desc',
                tooltips: {
                    enabled: true,
                    displayColors: false,
                    callbacks: {
                        label: this.makeLabel,
                    },
                },
                plugins: {},
            }
        };
        config.options.plugins = {
            colorschemes: {
                scheme: this.colorScheme,
                custom: (e) => {
                    config.data.datasets[0].backgroundColor = config.data.labels.map((label, index) => {
                        return e[index];
                    });
                    return e;
                },
            },
            tipper: makeTipper(ChartJS.Tooltip, {})
        };
        return config;
    }
    getColIndex(dataframe, col) {
        if (!dataframe || !dataframe.columns)
            return -1;
        return dataframe.columns.findIndex(({ name }) => name === col);
    }
    getLabels(localDataframe) {
        const labels = [];
        if (this.labelColumn && localDataframe) {
            const columnIndex = this.getColIndex(localDataframe, this.labelColumn);
            if (columnIndex < 0) {
                throw new Error(`Column ${this.labelColumn} not found`);
            }
            if (localDataframe.rows) {
                for (const row of localDataframe.rows) {
                    labels.push(row[columnIndex]);
                }
            }
        }
        return labels;
    }
    makeLabel({ datasetIndex, index }, { datasets, labels }) {
        const dataset = datasets[datasetIndex];
        const total = dataset.data.reduce((acc, v) => acc + parseFloat(v), 0);
        let suffix = `(${total.toFixed(2)})%`;
        if (total) {
            suffix = `(${((dataset.data[index] * 100) / total).toFixed(2)}%)`;
        }
        return `${labels[index]}: ${dataset.data[index]} ${suffix}`;
    }
    getDatasets(localDataframe, dataframes) {
        const chartDataset = [];
        if (this.dataColumns.length && localDataframe.rows) {
            // Create dataset for each dataColumn
            for (const { name } of this.dataColumns) {
                // Assume localDataframe has the dataColumn
                let columnIndex = this.getColIndex(localDataframe, name);
                // If dataColumn is in localDataframe, then set that value
                const data = localDataframe.rows.map(r => {
                    return columnIndex < 0 ? undefined : r[columnIndex];
                });
                // Otherwise check other dataframes for that columnn
                if (columnIndex < 0) {
                    dataframes.slice(1).forEach(df => {
                        const { relColumn = '', refValue = '' } = df;
                        // Get column that is referenced by relColumn
                        const relColumnIndex = this.getColIndex(localDataframe, relColumn);
                        if (relColumnIndex < 0) {
                            throw new Error(`Column ${relColumn} not found`);
                        }
                        if (!localDataframe.rows) {
                            throw new Error(`Local rows not found`);
                        }
                        // Get row index that matches refValue
                        const refRowIndex = localDataframe.rows.findIndex(row => row[relColumnIndex] === refValue);
                        if (refRowIndex < 0) {
                            throw new Error(`Row that matches refRowIndex ${refValue} not found`);
                        }
                        columnIndex = this.getColIndex(df, name);
                        if (columnIndex < 0) {
                            throw new Error(`Column ${name} not found`);
                        }
                        else if (df.rows) {
                            data[refRowIndex] = df.rows[0][columnIndex];
                        }
                    });
                }
                const backgroundColor = [];
                chartDataset.push({
                    label: name,
                    data,
                    backgroundColor,
                });
            }
        }
        return chartDataset;
    }
}
ChartOptionsRegistry.set('funnel', FunnelChartOptions);

const kind$1 = 'Table';
const defaults = Object.freeze({
    source: '',
    datasources: [],
    columns: {},
    striped: false,
    bordered: false,
    borderless: false,
    small: false,
    hover: false,
    dark: false,
    fixed: false,
    responsive: true,
    noCollapse: false,
    headVariant: null,
    tableVariant: '',
});
class DisplayElementTable extends DisplayElement {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$1;
        this.options = Object.assign({}, defaults);
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        Apply(this.options, o, String, 'headVariant', 'tableVariant', 'source');
        Apply(this.options, o, Boolean, 'striped', 'bordered', 'borderless', 'small', 'hover', 'dark', 'fixed', 'responsive', 'noCollapse');
        if (o.datasources) {
            this.options.datasources = o.datasources || [];
        }
        if (o.columns) {
            this.options.columns = o.columns || [];
        }
    }
    reportDefinitions(definition) {
        if (typeof this.options.source === 'object') {
            // @todo allow implicit sources
            throw new Error('table source must be provided as a reference');
        }
        const dataframes = [];
        this.options.datasources.forEach(({ name = '', filter, sort, paging }) => {
            var _a, _b;
            const df = {
                name: this.name,
                source: this.options.source,
                ref: name,
                filter,
                sort,
                paging,
            };
            const relatedDefinition = definition[name];
            if (relatedDefinition) {
                df.sort = (relatedDefinition.sort ? relatedDefinition.sort : sort) || undefined;
                if (relatedDefinition.filter && ((_a = relatedDefinition.filter) === null || _a === void 0 ? void 0 : _a.ref)) {
                    // If element and scenario have filter AND them together
                    if (filter && filter.ref) {
                        df.filter = {
                            ref: 'and',
                            args: [
                                filter,
                                relatedDefinition.filter,
                            ]
                        };
                    }
                    else {
                        df.filter = relatedDefinition.filter;
                    }
                }
                if (relatedDefinition.paging || paging) {
                    df.paging = Object.assign(Object.assign({}, (paging || {})), (relatedDefinition.paging || {}));
                }
            }
            if ((_b = df.paging) === null || _b === void 0 ? void 0 : _b.limit) {
                df.paging.limit = parseInt(df.paging.limit);
            }
            dataframes.push(df);
        });
        return { dataframes };
    }
}
Registry.set(kind$1, DisplayElementTable);

const kind$2 = 'Text';
const defaults$1 = Object.freeze({
    value: 'Sample text...',
});
class DisplayElementText extends DisplayElement {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$2;
        this.options = Object.assign({}, defaults$1);
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        Apply(this.options, o, String, 'value');
    }
}
Registry.set(kind$2, DisplayElementText);

const kind$3 = 'Metric';
const defaults$2 = Object.freeze({
    source: '',
    datasources: [],
    valueColumn: '',
    format: '',
    prefix: '',
    suffix: '',
    color: '#000000',
    backgroundColor: '#ffffff',
});
class DisplayElementMetric extends DisplayElement {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$3;
        this.options = Object.assign({}, defaults$2);
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        Apply(this.options, o, String, 'source', 'valueColumn', 'format', 'prefix', 'suffix', 'color', 'backgroundColor');
        if (o.datasources) {
            this.options.datasources = o.datasources || [];
        }
    }
    reportDefinitions(definition) {
        if (typeof this.options.source === 'object') {
            // @todo allow implicit sources
            throw new Error('metric source must be provided as a reference');
        }
        const dataframes = [];
        this.options.datasources.forEach(({ name = '', filter, sort }) => {
            var _a;
            const df = {
                name: this.name,
                source: this.options.source,
                ref: name,
                filter,
                sort,
            };
            const relatedDefinition = definition[name];
            if (relatedDefinition) {
                df.sort = (relatedDefinition.sort ? relatedDefinition.sort : sort) || undefined;
                if (relatedDefinition.filter && ((_a = relatedDefinition.filter) === null || _a === void 0 ? void 0 : _a.ref)) {
                    // If element and scenario have filter AND them together
                    if (filter && filter.ref) {
                        df.filter = {
                            ref: 'and',
                            args: [
                                filter,
                                relatedDefinition.filter,
                            ]
                        };
                    }
                    else {
                        df.filter = relatedDefinition.filter;
                    }
                }
            }
            dataframes.push(df);
        });
        return { dataframes };
    }
}
Registry.set(kind$3, DisplayElementMetric);

function DisplayElementMaker(i) {
    const DisplayElementTemp = Registry.get(i.kind);
    if (DisplayElementTemp === undefined) {
        throw new Error(`unknown display element kind '${i.kind}'`);
    }
    if (i instanceof DisplayElement) {
        // Get rid of the references
        i = JSON.parse(JSON.stringify(i));
    }
    return new DisplayElementTemp(i);
}

const defaultXYWH = () => [0, 0, 20, 15];
class Block {
    constructor(p) {
        this.title = '';
        this.description = '';
        this.layout = 'horizontal';
        this.elements = [];
        this.xywh = defaultXYWH();
        if (!p)
            return;
        Apply(this, p, String, 'title', 'description', 'layout');
        if (p.xywh) {
            if (!Array.isArray(p.xywh)) {
                throw new Error('xywh must be an array');
            }
            if (p.xywh.length !== 4) {
                throw new Error('xywh must have 4 elements');
            }
            this.xywh = p.xywh;
        }
        if (p.elements) {
            this.elements = [];
            if (AreObjectsOf(p.elements, 'kind')) {
                this.elements = p.elements.map((e) => DisplayElementMaker(e));
            }
        }
    }
}

function StepFactory(step) {
    const k = Object.keys(step)[0];
    switch (k) {
        case 'load':
            return step;
        case 'join':
            return step;
        case 'group':
            return step;
        case 'transform':
            return step;
        default:
            throw new Error('unknown step: ' + k);
    }
}



var index$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Block: Block,
  StepFactory: StepFactory,
  DisplayElementMaker: DisplayElementMaker,
  DisplayElementRegistry: Registry,
  DisplayElement: DisplayElement,
  DisplayElementChart: DisplayElementChart,
  ChartOptionsMaker: ChartOptionsMaker,
  DisplayElementTable: DisplayElementTable,
  DisplayElementText: DisplayElementText,
  DisplayElementMetric: DisplayElementMetric
});

// @todo rework fresh reporter thing and the backend thing
class Report {
    constructor(r) {
        this.reportID = NoID;
        this.handle = '';
        this.meta = {};
        this.sources = [];
        this.blocks = [];
        this.scenarios = [];
        this.labels = {};
        this.createdAt = undefined;
        this.createdBy = undefined;
        this.updatedAt = undefined;
        this.updatedBy = undefined;
        this.deletedAt = undefined;
        this.deletedBy = undefined;
        this.canReadReport = false;
        this.canUpdateReport = false;
        this.canDeleteReport = false;
        this.canGrant = false;
        this.canRunReport = false;
        this.apply(r);
    }
    apply(r) {
        var _a, _b, _c;
        Apply(this, r, CortezaID, 'reportID');
        Apply(this, r, String, 'handle');
        if (r && IsOf(r, 'meta')) {
            this.meta = r.meta;
        }
        this.sources = [];
        for (const s of ((_a = r) === null || _a === void 0 ? void 0 : _a.sources) || []) {
            s.step = s.step;
            this.sources.push(s);
        }
        if ((_b = r) === null || _b === void 0 ? void 0 : _b.blocks) {
            this.blocks = [];
            for (const p of r.blocks) {
                this.blocks.push(new Block(p));
            }
        }
        if ((_c = r) === null || _c === void 0 ? void 0 : _c.scenarios) {
            this.scenarios = [];
            for (const s of r.scenarios) {
                this.scenarios.push(s);
            }
        }
        if (IsOf(r, 'labels')) {
            this.labels = Object.assign({}, r.labels);
        }
        Apply(this, r, ISO8601Date, 'createdAt', 'updatedAt', 'deletedAt');
        Apply(this, r, CortezaID, 'createdBy', 'updatedBy', 'deletedBy');
        Apply(this, r, Boolean, 'canReadReport', 'canUpdateReport', 'canDeleteReport', 'canGrant', 'canRunReport');
    }
}

// @todo refactor this into more compose-like event structure (see compose/events.ts
function SystemEvent(eventType = onManual) {
    return GenericEventMaker({ resourceType: 'system' }, eventType, () => true, {});
}
// @todo refactor this into more compose-like event structure (see compose/events.ts
function UserEvent(user, eventType = onManual) {
    return GenericEventMaker(user, eventType, function (c) {
        switch (c.Name()) {
            case 'user':
            case 'user.handle':
                return c.Match(user.handle);
            case 'user.email':
                return c.Match(user.email);
        }
        return false;
    }, { user });
}
// @todo refactor this into more compose-like event structure (see compose/events.ts
function RoleEvent(role, eventType = onManual) {
    return GenericEventMaker(role, eventType, function (c) {
        switch (c.Name()) {
            case 'role':
            case 'role.handle':
                return c.Match(role.handle);
            case 'role.name':
                return c.Match(role.name);
        }
        return false;
    }, { role });
}
/**
 * Returns handler that routes onManual events for server script to the system API
 *
 * See makeAutomationScriptsRegistrator
 *
 * @param api
 * @return function
 */
function TriggerSystemServerScriptOnManual(api) {
    return (ev, script) => {
        var _a, _b;
        const params = { script, args: ev.args };
        const { userID } = (_a = ev.args) === null || _a === void 0 ? void 0 : _a.user;
        const { roleID } = (_b = ev.args) === null || _b === void 0 ? void 0 : _b.role;
        switch (ev.resourceType) {
            case 'system':
                return api.automationTriggerScript(Object.assign({}, params));
            case 'system:user':
                return api.userTriggerScript(Object.assign({ userID }, params)).then(rval => new User(rval));
            case 'system:role':
                return api.roleTriggerScript(Object.assign({ roleID }, params)).then(rval => new Role(rval));
            default:
                throw Error(`cannot trigger server script: unknown resource type '${ev.resourceType}'`);
        }
    };
}



var index$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  User: User,
  Role: Role,
  Application: Application,
  SinkRequest: SinkRequest,
  SinkResponse: SinkResponse,
  Reminder: Reminder,
  Template: Template,
  Report: Report,
  SystemEvent: SystemEvent,
  RoleEvent: RoleEvent,
  UserEvent: UserEvent,
  TriggerSystemServerScriptOnManual: TriggerSystemServerScriptOnManual
});

/**
 * Helpers to determine if specific object looks like the type we are interested in.
 * It does not rely on instanceof, because of bundling issues.
 */
function isUser(o) {
    return o && !!o.userID;
}
function isRole(o) {
    return o && !!o.roleID;
}
/**
 * SystemHelper provides layer over System API and utilities that simplify automation script writing
 */
class SystemHelper {
    constructor(ctx) {
        this.SystemAPI = ctx.SystemAPI;
        this.$user = ctx.$user;
        this.$role = ctx.$role;
        this.$application = ctx.$application;
    }
    /**
     * Searches for users
     *
     * @example
     * System.findUsers('some-joe').then(({ set }) => {
     *   // do something with users (User[]) in set
     * })
     *
     * @param filter - filter object (or filtering conditions when string)
     * @property filter.query - Find %query% in email, handle, username, name...
     * @property filter.username - Filter by username
     * @property filter.handle - Filter by handle
     * @property filter.email - Filter by email
     * @property filter.kind - Filter by kind ('normal' - default, 'bot')
     * @property filter.incDeleted - Include deleted users
     * @property filter.incSuspended - Include suspended users
     * @property filter.sort - Sort results
     * @property filter.perPage - max returned records per page
     * @property filter.page - page to return (1-based)
     */
    findUsers(filter) {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof filter === 'string') {
                filter = { query: filter };
            }
            return this.SystemAPI
                .userList(filter || {})
                .then(res => {
                res.set = res.set.map(u => new User(u));
                return res;
            });
        });
    }
    /**
     * Finds user by ID
     *
     * @example
     * System.findUserByID()
     *
     * @param user
     */
    findUserByID(user) {
        return __awaiter(this, void 0, void 0, function* () {
            const userID = extractID(user, 'userID');
            return this.SystemAPI.userRead({ userID }).then(u => new User(u));
        });
    }
    /**
     * Finds user by email
     *
     * @example
     * System.findUserByEmail('name@example.tld').then(user => {
     *   // do something with user
     * })
     *
     * @param email
     */
    findUserByEmail(email) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.findUsers({ email }).then(res => {
                if (!Array.isArray(res.set) || res.set.length === 0) {
                    throw new Error('user not found');
                }
                return new User(res.set[0]);
            });
        });
    }
    /**
     * Finds user by handle
     *
     * @example
     * System.findUserByHandle('some-handle').then(user => {
     *   // do something with user
     * })
     *
     * @param handle
     */
    findUserByHandle(handle) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.findUsers({ handle }).then(res => {
                if (!Array.isArray(res.set) || res.set.length === 0 || !res.set) {
                    throw new Error('user not found');
                }
                return new User(res.set[0]);
            });
        });
    }
    /**
     * Updates or creates user
     *
     * @example
     * System.findUserByHandle('some-handle').then(user => {
     *   user.handle = 'better-handle'
     *   return System.saveUser(user)
     * })
     *
     * @param user
     */
    saveUser(user) {
        return __awaiter(this, void 0, void 0, function* () {
            return Promise.resolve(user).then(user => {
                if (isFresh(user.userID)) {
                    return this.SystemAPI.userCreate(kv(user)).then(user => new User(user));
                }
                else {
                    return this.SystemAPI.userUpdate(kv(user)).then(user => new User(user));
                }
            });
        });
    }
    /**
     * Sets/updates password for the user
     *
     * @example
     * System.findUserByHandle('some-handle').then(user => {
     *   user.handle = 'better-handle'
     *   return System.saveUser(user)
     * })
     *
     * @param password
     * @param user
     */
    setPassword(password, user = this.$user) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.resolveUser(user).then(user => {
                const { userID } = user;
                if (isFresh(userID)) {
                    throw new Error('Cannot set password for non existing user');
                }
                return this.SystemAPI.userSetPassword({ password, userID }).then(u => new User(u));
            });
        });
    }
    /**
     * Deletes user
     *
     * @example
     * System.findUserByHandle('soon-to-be-deleted').then(user => {
     *   return System.deleteUser(user)
     * })
     *
     * @param user
     */
    deleteUser(user) {
        return __awaiter(this, void 0, void 0, function* () {
            return Promise.resolve(user).then(user => {
                const userID = extractID(user, 'userID');
                if (!isFresh(userID)) {
                    return this.SystemAPI.userDelete({ userID });
                }
            });
        });
    }
    /**
     * Searches for roles
     *
     * @param filter
     */
    findRoles(filter) {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof filter === 'string') {
                filter = { query: filter };
            }
            return this.SystemAPI
                .roleList(filter || {})
                .then(res => {
                res.set = res.set.map(r => new Role(r));
                return res;
            });
        });
    }
    /**
     * Finds user by ID
     *
     * @param role
     */
    findRoleByID(role) {
        return __awaiter(this, void 0, void 0, function* () {
            const roleID = extractID(role, 'roleID');
            return this.SystemAPI.roleRead({ roleID }).then(r => new Role(r));
        });
    }
    /**
     * Finds role by handle
     *
     * @example
     * System.findRoleByHandle('some-handle').then(user => {
     *   // do something with role
     * })
     *
     * @param handle
     */
    findRoleByHandle(handle) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.findRoles(handle).then(res => {
                if (!Array.isArray(res.set) || res.set.length === 0 || !res.set) {
                    throw new Error('role not found');
                }
                return new Role(res.set[0]);
            });
        });
    }
    /**
     *
     * @param role
     */
    saveRole(role) {
        return __awaiter(this, void 0, void 0, function* () {
            return Promise.resolve(role).then(role => {
                if (isFresh(role.roleID)) {
                    return this.SystemAPI.roleCreate(kv(role)).then(role => new Role(role));
                }
                else {
                    return this.SystemAPI.roleUpdate(kv(role)).then(role => new Role(role));
                }
            });
        });
    }
    /**
     * Deletes a role
     *
     * @example
     * System.findUserByHandle('soon-to-be-deleted').then(user => {
     *   return System.deleteUser(user)
     * })
     *
     * @param role
     */
    deleteRole(role) {
        return __awaiter(this, void 0, void 0, function* () {
            return Promise.resolve(role).then(role => {
                const roleID = extractID(role, 'roleID');
                if (!isFresh(roleID)) {
                    return this.SystemAPI.roleDelete({ roleID });
                }
            });
        });
    }
    /**
     * Assign role to user
     *
     * @example
     * addUserToRole('user-we-can-trust', 'admins')
     *
     * @param user resolvable user input
     * @param role resolvable role input
     */
    addUserToRole(user, role) {
        return __awaiter(this, void 0, void 0, function* () {
            let userID;
            let roleID;
            return this.resolveUser(user, this.$user).then(user => {
                userID = extractID(user, 'userID');
                return this.resolveRole(role, this.$role);
            }).then(role => {
                roleID = extractID(role, 'roleID');
                return this.SystemAPI.roleMemberAdd({ roleID, userID });
            });
        });
    }
    /**
     * Remove role from user
     * @example
     * addUserToRole('user-we-can-trust', 'admins')
     *
     * @param user - resolvable user input
     * @param role - resolvable role input
     */
    removeUserFromRole(user, role) {
        return __awaiter(this, void 0, void 0, function* () {
            let userID;
            let roleID;
            return this.resolveUser(user, this.$user).then(user => {
                userID = extractID(user, 'userID');
                return this.resolveRole(role, this.$role);
            }).then(role => {
                roleID = extractID(role, 'roleID');
                return this.SystemAPI.roleMemberRemove({ roleID, userID });
            });
        });
    }
    /**
     * Resolves users from the arguments and returns first valid
     *
     * Knows how to resolve from:
     *  - string that looks like an ID - find by id (fallback to find-by-handle)
     *  - string that looks like an email - find by email (fallback to find-by-handle)
     *  - string - find by handle
     *  - User object
     *  - object with userID or ownerID properties
     */
    resolveUser(...args) {
        return __awaiter(this, void 0, void 0, function* () {
            for (let u of args) {
                // Resolve pending promises if any...
                u = yield u;
                if (!u) {
                    continue;
                }
                if (typeof u === 'string') {
                    try {
                        if (IsCortezaID(u)) {
                            // Looks like an ID, try to find it and fall back to handle
                            return yield this.findUserByID(u);
                        }
                        else if (u.indexOf('@') > 0) {
                            return yield this.findUserByEmail(u);
                        }
                    }
                    catch (e) { }
                    // Always fall back to handle
                    return this.findUserByHandle(u);
                }
                if (typeof u !== 'object') {
                    continue;
                }
                if (isUser(u)) {
                    // Already got what we need
                    return Promise.resolve(u);
                }
                // Other kind of object with properties that might hold user ID
                const { userID, ownerID, } = u;
                return this.resolveUser(userID, ownerID);
            }
            return Promise.reject(new Error('unexpected input type for user resolver'));
        });
    }
    /**
     * Resolves users from the arguments and returns first valid
     *
     * Knows how to resolve from:
     *  - string that looks like an ID - find by id (fallback to find-by-handle)
     *  - string - find by handle
     *  - Role object
     *  - object with roleID property
     */
    resolveRole(...args) {
        return __awaiter(this, void 0, void 0, function* () {
            for (let r of args) {
                // Resolve pending promises if any...
                r = yield r;
                if (!r) {
                    continue;
                }
                if (typeof r === 'string') {
                    if (IsCortezaID(r)) {
                        // Looks like an ID, try to find it and fall back to handle
                        return this.findRoleByID(r).catch(() => this.findRoleByHandle(r));
                    }
                    return this.findRoleByHandle(r);
                }
                if (typeof r !== 'object') {
                    continue;
                }
                if (isRole(r)) {
                    // Already got what we need
                    return r;
                }
                // Other kind of object with properties that might hold role ID
                const { roleID, } = r;
                return this.resolveRole(roleID);
            }
            return Promise.reject(Error('unexpected input type for role resolver'));
        });
    }
    /**
     * Allows access for the given role for the given System resource
     *
     * @example
     * // Allows users with `someRole` to access the newly created user
     * await Compose.allow({
     *    role: someRole,
     *    resource: newUser,
     *    operation: 'read',
     * })
     */
    allow(...pr) {
        return __awaiter(this, void 0, void 0, function* () {
            const rr = pr.map(p => ({
                role: p.role,
                resource: p.resource,
                operation: p.operation,
                access: 'allow',
            }));
            return genericPermissionUpdater(this.SystemAPI, rr);
        });
    }
    /**
     * Denies access for the given role for the given System resource
     *
     * @example
     * // Denies users with `someRole` from accessing the newly created user
     * await Compose.deny({
     *    role: someRole,
     *    resource: newUser,
     *    operation: 'read',
     * })
     */
    deny(...pr) {
        return __awaiter(this, void 0, void 0, function* () {
            const rr = pr.map(p => ({
                role: p.role,
                resource: p.resource,
                operation: p.operation,
                access: 'deny',
            }));
            return genericPermissionUpdater(this.SystemAPI, rr);
        });
    }
    /**
     * Inherits access for the given role for the given System resource
     *
     * @example
     * // Uses inherited permissions for the `sameRole` for the newly created user
     * await Compose.inherit({
     *    role: someRole,
     *    resource: newUser,
     *    operation: 'read',
     * })
     */
    inherit(...pr) {
        return __awaiter(this, void 0, void 0, function* () {
            const rr = pr.map(p => ({
                role: p.role,
                resource: p.resource,
                operation: p.operation,
                access: 'inherit',
            }));
            return genericPermissionUpdater(this.SystemAPI, rr);
        });
    }
}

class Attachment {
    constructor(i, baseURL) {
        this.attachmentID = NoID;
        this.ownerID = NoID;
        this.name = '';
        this.url = '';
        this.previewUrl = '';
        this.download = '';
        this.meta = {};
        this.createdAt = undefined;
        this.updatedAt = undefined;
        this.deletedAt = undefined;
        this.apply(i);
        this.setBaseURL(baseURL || '');
    }
    apply(i) {
        Apply(this, i, CortezaID, 'attachmentID', 'ownerID');
        Apply(this, i, String, 'name', 'url', 'previewUrl');
        if (IsOf(i, 'meta')) {
            this.meta = Object.assign({}, i.meta);
        }
        Apply(this, i, ISO8601Date, 'createdAt', 'updatedAt', 'deletedAt');
    }
    setBaseURL(baseURL) {
        this.url = baseURL + this.url;
        this.previewUrl = baseURL + this.previewUrl;
        this.download = this.url + '&download=1';
    }
}



var index$3 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Attachment: Attachment
});

const FieldNameValidator = /^\w{1,}$/;
const unsortableFieldKinds = ['User', 'Record', 'File'];
const unsortableSysFields = ['recordID', 'ownedBy', 'createdBy', 'updatedBy', 'deletedBy'];
const defaultOptions = () => Object.freeze({
    description: {
        view: '',
        edit: undefined,
    },
    hint: {
        view: '',
        edit: undefined,
    },
});
class ModuleField {
    constructor(f) {
        this.fieldID = NoID;
        this.name = '';
        this.kind = '';
        this.label = '';
        this.defaultValue = [];
        this.maxLength = 0;
        this.isRequired = false;
        this.isPrivate = false;
        this.isMulti = false;
        this.isSystem = false;
        this.isSortable = true;
        this.options = Object.assign({}, defaultOptions());
        this.expressions = {};
        this.canUpdateRecordValue = false;
        this.canReadRecordValue = false;
        this.apply(f);
    }
    applyOptions(o) {
        if (!o)
            return;
        Apply(this.options, o, Object, 'description', 'hint');
    }
    clone() {
        return new ModuleField(JSON.parse(JSON.stringify(this)));
    }
    apply(f) {
        if (!f)
            return;
        Apply(this, f, CortezaID, 'fieldID');
        Apply(this, f, String, 'name', 'label', 'kind');
        Apply(this, f, Number, 'maxLength');
        Apply(this, f, Boolean, 'isRequired', 'isPrivate', 'isMulti', 'isSystem');
        // Make sure field is align with it's capabilities
        if (!this.cap.multi)
            this.isMulti = false;
        if (!this.cap.required)
            this.isRequired = false;
        if (!this.cap.private)
            this.isPrivate = false;
        // Check if kind sortable
        if (unsortableFieldKinds.includes(this.kind)) {
            this.isSortable = false;
        }
        if (f.defaultValue && Array.isArray(f.defaultValue)) {
            /**
             * Converting default value into proper format
             * so we can use it without conversion
             * false boolean values are represented only by the name, in all other cases the value is also present
             */
            this.defaultValue = f.defaultValue
                .filter(({ name, value }) => name !== undefined || (value !== undefined && value !== null));
        }
        if (this.isSystem) {
            this.canUpdateRecordValue = true;
            this.canReadRecordValue = true;
            if (unsortableSysFields.includes(this.name)) {
                this.isSortable = false;
            }
        }
        else {
            Apply(this, f, Boolean, 'canUpdateRecordValue', 'canReadRecordValue');
        }
        if (IsOf(f, 'kind')) {
            this.kind = f.kind;
        }
        if (IsOf(f, 'expressions')) {
            this.expressions = f.expressions;
        }
    }
    /**
     * Test field validity
     *
     * Expecting valid name
     */
    get isValid() {
        return FieldNameValidator.test(this.name);
    }
    /**
     * Per module field type capabilities
     */
    get cap() {
        return {
            configurable: true,
            multi: true,
            writable: true,
            required: true,
            private: true,
        };
    }
    /**
     * Returns resource ID
     */
    get resourceID() {
        return `${this.resourceType}:${this.fieldID}`;
    }
    /**
     * Resource type
     */
    get resourceType() {
        return 'compose:module-field';
    }
}
const Registry$1 = new Map();

const kind$4 = 'Bool';
const defaults$3 = () => Object.freeze(Object.assign(Object.assign({}, defaultOptions()), { trueLabel: '', falseLabel: '' }));
class ModuleFieldBool extends ModuleField {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$4;
        this.options = Object.assign({}, defaults$3());
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        super.applyOptions(o);
        Apply(this.options, o, String, 'trueLabel', 'falseLabel');
    }
    /**
     * Per module field type capabilities
     */
    get cap() {
        return Object.assign(Object.assign({}, super.cap), { multi: false });
    }
}
Registry$1.set(kind$4, ModuleFieldBool);

/**
 * Returns current language
 *
 * This temporary solution returns an empty array;
 * this will cause Intl functions to format strings and numbers in the current (by-browser) language
 */
function currentLanguage() {
    return [];
}

/**
 * Parses input into Date using Moment library
 *
 * @param input
 */
function parse(input) {
    return moment(input).toDate();
}
function format(input, options) {
    return (new Intl.DateTimeFormat(currentLanguage(), options)).format(parse(input));
}
/**
 * Outputs locally formatted date and time, no seconds
 *
 * Examples:
 * "Wednesday, September 8, 2021 at 9:41 AM"
 * "sreda, 08. september 2021 09:41"
 * "srijeda, 8. rujna 2021. u 09:42"
 *
 * @param input
 * @param options
 */
function fullDateTime(input, options = { dateStyle: 'full', timeStyle: 'short' }) {
    return format(input, options);
}
/**
 * Outputs locally formatted date without time
 *
 * Example:
 * 09/04/1986
 *
 * @param input
 * @param options
 */
function date(input, options = { dateStyle: 'short' }) {
    return format(input, options);
}
/**
 * Outputs locally formatted time
 *
 * Example:
 * 8:30 PM
 *
 * @param input
 * @param options
 */
function time(input, options = { timeStyle: 'short' }) {
    return format(input, options);
}

function number(input, options = {}) {
    return new Intl.NumberFormat(currentLanguage(), options).format(input);
}



var index$4 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  fullDateTime: fullDateTime,
  date: date,
  time: time,
  number: number
});

// @todo option to allow only time entry
const kind$5 = 'DateTime';
const defaults$4 = () => Object.freeze(Object.assign(Object.assign({}, defaultOptions()), { format: '', multiDelimiter: '\n', onlyDate: false, onlyFutureValues: false, onlyPastValues: false, onlyTime: false, outputRelative: false }));
class ModuleFieldDateTime extends ModuleField {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$5;
        this.options = Object.assign({}, defaults$4());
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        super.applyOptions(o);
        Apply(this.options, o, String, 'format', 'multiDelimiter');
        Apply(this.options, o, Boolean, 'onlyDate', 'onlyTime', 'onlyPastValues', 'onlyFutureValues', 'outputRelative');
    }
    formatValue(value) {
        if (value === 'Invalid date') {
            return null;
        }
        const o = this.options;
        const m = moment(value);
        if (o.outputRelative) {
            return m.fromNow();
        }
        else if (o.format.length > 0) {
            return m.format(o.format);
        }
        else if (o.onlyTime) {
            return time(moment(value, ['YYYY-MM-DD HH:mm', 'YYYY-MM-DD', 'HH:mm']));
        }
        else if (o.onlyDate) {
            return date(m);
        }
        else {
            return fullDateTime(m);
        }
    }
    /**
     * Checks if given value is in the future
     * @param {String|Array<String>} v Value (in DateTime) to check
     * @param {Moment} now Time reference
     * @returns {undefined|String} undefined if valid, Error string if invalid
     */
    checkFuture(v, now = moment()) {
        if (!this.options.onlyFutureValues) {
            return undefined;
        }
        if (!Array.isArray(v)) {
            v = [v];
        }
        if (v.find(v => moment(v) < now)) {
            return 'notification.field-datetime.valueNotFuture';
        }
        return undefined;
    }
    /**
     * Checks if given value is in the past
     * @param {String|Array<String>} v Value (in DateTime) to check
     * @param {Moment} now Time reference
     * @returns {undefined|String} undefined if valid, Error string if invalid
     */
    checkPast(v, now = moment()) {
        if (!this.options.onlyPastValues) {
            return undefined;
        }
        if (!Array.isArray(v)) {
            v = [v];
        }
        if (v.find(v => moment(v) > now)) {
            return 'notification.field-datetime.valueNotPast';
        }
    }
    /**
     * Checks if given value is valid for this field
     * @param {String} v Value (in DateTime) to check
     * @param {Moment} now Reference time used to compare
     * @returns {Array<>} Array of issues; empty if none
     */
    validate(v, now = moment()) {
        let err = this.checkFuture(v, now);
        err = err || this.checkPast(v, now);
        if (err) {
            return [err];
        }
        return [];
    }
}
Registry$1.set(kind$5, ModuleFieldDateTime);

// @todo option to allow multiple entries
const kind$6 = 'Email';
const defaults$5 = () => Object.freeze(Object.assign(Object.assign({}, defaultOptions()), { outputPlain: true, multiDelimiter: '\n' }));
class ModuleFieldEmail extends ModuleField {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$6;
        this.options = Object.assign({}, defaults$5());
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        super.applyOptions(o);
        Apply(this.options, o, String, 'multiDelimiter');
        Apply(this.options, o, Boolean, 'outputPlain');
    }
}
Registry$1.set(kind$6, ModuleFieldEmail);

const kind$7 = 'File';
const modes = [
    // list of attachments, no preview
    'list',
    // grid of icons
    'grid',
    // single (first) image/file, show preview
    'single',
    // list of all images/files, show preview
    'gallery',
];
const defaults$6 = () => Object.freeze(Object.assign(Object.assign({}, defaultOptions()), { allowImages: true, allowDocuments: true, maxSize: 0, mode: '\n', inline: true, hideFileName: false, mimetypes: '' }));
class ModuleFieldFile extends ModuleField {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$7;
        this.options = Object.assign({}, defaults$6());
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        super.applyOptions(o);
        Apply(this.options, o, Number, 'maxSize');
        Apply(this.options, o, Boolean, 'allowImages', 'allowDocuments', 'inline', 'hideFileName');
        Apply(this.options, o, String, 'mimetypes');
        ApplyWhitelisted(this.options, o, modes, 'mode');
    }
}
Registry$1.set(kind$7, ModuleFieldFile);

// @todo option to allow multiple entries
const kind$8 = 'Select';
const defaults$7 = () => Object.freeze(Object.assign(Object.assign({}, defaultOptions()), { options: [], selectType: 'default', multiDelimiter: '\n' }));
class ModuleFieldSelect extends ModuleField {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$8;
        this.options = Object.assign({}, defaults$7());
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        super.applyOptions(o);
        Apply(this.options, o, String, 'selectType', 'multiDelimiter');
        if (o.options) {
            let opt = [];
            if (AreStrings(o.options)) {
                opt = o.options
                    .map(value => ({ value, text: value }));
            }
            else {
                opt = o.options
                    .map(({ value, text }) => ({ value, text: text || value }));
            }
            this.options.options = opt;
        }
    }
}
Registry$1.set(kind$8, ModuleFieldSelect);

const kind$9 = 'Number';
const defaults$8 = () => Object.freeze(Object.assign(Object.assign({}, defaultOptions()), { format: '', prefix: '', suffix: '', precision: 0, multiDelimiter: '\n' }));
class ModuleFieldNumber extends ModuleField {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$9;
        this.options = Object.assign({}, defaults$8());
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        super.applyOptions(o);
        Apply(this.options, o, String, 'format', 'prefix', 'suffix', 'multiDelimiter');
        Apply(this.options, o, Number, 'precision');
    }
    formatValue(value) {
        const o = this.options;
        let n;
        switch (typeof value) {
            case 'string':
                n = parseFloat(value);
                break;
            case 'number':
                n = value;
                break;
            default:
                n = 0;
        }
        let out = `${n}`;
        if (o.format && o.format.length > 0) {
            out = numeral(n).format(o.format);
        }
        else {
            out = number(n);
        }
        return '' + o.prefix + out + o.suffix;
    }
}
Registry$1.set(kind$9, ModuleFieldNumber);

// @todo option to allow multiple entries
const kind$a = 'Record';
const defaults$9 = () => Object.freeze(Object.assign(Object.assign({}, defaultOptions()), { moduleID: NoID, labelField: '', queryFields: [], selectType: '', multiDelimiter: '\n', prefilter: undefined }));
class ModuleFieldRecord extends ModuleField {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$a;
        this.options = Object.assign({}, defaults$9());
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        super.applyOptions(o);
        Apply(this.options, o, CortezaID, 'moduleID');
        Apply(this.options, o, String, 'labelField', 'selectType', 'multiDelimiter', 'prefilter');
        Apply(this.options, o, (o) => {
            if (!o) {
                return [];
            }
            if (!Array.isArray(o)) {
                return [o];
            }
            return o;
        }, 'queryFields');
    }
}
Registry$1.set(kind$a, ModuleFieldRecord);

const kind$b = 'String';
const defaults$a = () => Object.freeze(Object.assign(Object.assign({}, defaultOptions()), { multiLine: false, useRichTextEditor: false, multiDelimiter: '\n' }));
class ModuleFieldString extends ModuleField {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$b;
        this.options = Object.assign({}, defaults$a());
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        super.applyOptions(o);
        Apply(this.options, o, String, 'multiDelimiter');
        Apply(this.options, o, Boolean, 'multiLine', 'useRichTextEditor');
    }
}
Registry$1.set(kind$b, ModuleFieldString);

// @todo option to allow multiple entries
const kind$c = 'Url';
const defaults$b = () => Object.freeze(Object.assign(Object.assign({}, defaultOptions()), { trimFragment: false, trimQuery: false, trimPath: false, onlySecure: false, outputPlain: false, multiDelimiter: '\n' }));
class ModuleFieldUrl extends ModuleField {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$c;
        this.options = Object.assign({}, defaults$b());
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        super.applyOptions(o);
        Apply(this.options, o, String, 'multiDelimiter');
        Apply(this.options, o, Boolean, 'trimFragment', 'trimQuery', 'trimPath', 'onlySecure', 'outputPlain');
    }
}
Registry$1.set(kind$c, ModuleFieldUrl);

// @todo option to allow multiple entries
const kind$d = 'User';
const defaults$c = () => Object.freeze(Object.assign(Object.assign({}, defaultOptions()), { roles: [], presetWithAuthenticated: false, selectType: 'default', multiDelimiter: '\n' }));
class ModuleFieldUser extends ModuleField {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$d;
        this.options = Object.assign({}, defaults$c());
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        super.applyOptions(o);
        Apply(this.options, o, Boolean, 'presetWithAuthenticated');
        Apply(this.options, o, String, 'selectType', 'multiDelimiter');
        Apply(this.options, o, (o) => {
            if (!o) {
                return [];
            }
            if (!Array.isArray(o)) {
                return [o];
            }
            return o;
        }, 'roles');
    }
    formatter({ userID, name, username, email } = {}) {
        return name || username || email || userID || '';
    }
}
Registry$1.set(kind$d, ModuleFieldUser);

function ModuleFieldMaker(i) {
    if (!i.kind) {
        return new ModuleField(i);
    }
    if (!Registry$1.has(i.kind)) {
        throw new Error(`unknown module field kind '${i.kind}'`);
    }
    return new (Registry$1.get(i.kind))(i);
}

class Namespace {
    constructor(i) {
        this.namespaceID = NoID;
        this.name = '';
        this.slug = '';
        this.enabled = false;
        this.labels = {};
        this.meta = {};
        this.createdAt = undefined;
        this.updatedAt = undefined;
        this.deletedAt = undefined;
        this.canCreateChart = false;
        this.canCreateModule = false;
        this.canCreatePage = false;
        this.canDeleteNamespace = false;
        this.canUpdateNamespace = false;
        this.canManageNamespace = false;
        this.canCloneNamespace = false;
        this.canGrant = false;
        this.apply(i);
    }
    clone() {
        return new Namespace(JSON.parse(JSON.stringify(this)));
    }
    apply(n) {
        if (!n)
            return;
        Apply(this, n, CortezaID, 'namespaceID');
        Apply(this, n, String, 'name', 'slug');
        Apply(this, n, Boolean, 'enabled');
        if (IsOf(n, 'meta')) {
            this.meta = Object.assign({}, n.meta);
        }
        if (IsOf(n, 'labels')) {
            this.labels = Object.assign({}, n.labels);
        }
        Apply(this, n, ISO8601Date, 'createdAt', 'updatedAt', 'deletedAt');
        Apply(this, n, Boolean, 'canCreateChart', 'canCreateModule', 'canCreatePage', 'canDeleteNamespace', 'canUpdateNamespace', 'canManageNamespace', 'canCloneNamespace', 'canGrant');
    }
    /**
     * Returns resource ID
     */
    get resourceID() {
        return `${this.resourceType}:${this.namespaceID}`;
    }
    /**
     * Resource type
     */
    get resourceType() {
        return 'compose:namespace';
    }
}

const propNamespace = Symbol('namespace');
/**
 * System fields that are present in every object.
 */
const systemFields = Object.freeze([
    { isSystem: true, name: 'recordID', label: 'Record ID', kind: 'String' },
    { isSystem: true, name: 'ownedBy', label: 'Owned by', kind: 'User' },
    { isSystem: true, name: 'createdBy', label: 'Created by', kind: 'User' },
    { isSystem: true, name: 'createdAt', label: 'Created at', kind: 'DateTime' },
    { isSystem: true, name: 'updatedBy', label: 'Updated by', kind: 'User' },
    { isSystem: true, name: 'updatedAt', label: 'Updated at', kind: 'DateTime' },
    { isSystem: true, name: 'deletedBy', label: 'Deleted by', kind: 'User' },
    { isSystem: true, name: 'deletedAt', label: 'Deleted at', kind: 'DateTime' },
].map(f => ModuleFieldMaker(f)));
class Module {
    constructor(i, ns) {
        this.moduleID = NoID;
        this.namespaceID = NoID;
        this.name = '';
        this.handle = '';
        this.fields = [];
        this.meta = {};
        this.labels = {};
        this.createdAt = undefined;
        this.updatedAt = undefined;
        this.deletedAt = undefined;
        this.canUpdateModule = false;
        this.canDeleteModule = false;
        this.canCreateRecord = false;
        this.canGrant = false;
        if (ns) {
            this.namespace = ns;
        }
        this.apply(i);
    }
    clone() {
        return new Module(JSON.parse(JSON.stringify(this)), this.namespace);
    }
    apply(m) {
        if (!m)
            return;
        if (this.namespace && m.namespaceID && m.namespaceID !== this.namespace.namespaceID) {
            throw new Error('module can not change namespace');
        }
        Apply(this, m, CortezaID, 'moduleID', 'namespaceID');
        Apply(this, m, String, 'name', 'handle');
        if (IsOf(m, 'fields')) {
            this.fields = [];
            if (AreObjects(m.fields)) {
                // We're very permissive here -- array of (empty) objects is all we need
                // to create fields.
                this.fields = m.fields.map((b) => ModuleFieldMaker(b));
            }
        }
        if (IsOf(m, 'meta')) {
            this.meta = Object.assign({}, m.meta);
        }
        if (IsOf(m, 'labels')) {
            this.labels = Object.assign({}, m.labels);
        }
        Apply(this, m, ISO8601Date, 'createdAt', 'updatedAt', 'deletedAt');
        Apply(this, m, Boolean, 'canUpdateModule', 'canDeleteModule', 'canCreateRecord', 'canGrant');
    }
    /**
     * Returns resource ID
     */
    get resourceID() {
        return `${this.resourceType}:${this.moduleID}`;
    }
    /**
     * Resource type
     */
    get resourceType() {
        return 'compose:module';
    }
    get namespace() {
        return this[propNamespace];
    }
    set namespace(ns) {
        if (this[propNamespace]) {
            if (this[propNamespace].namespaceID !== ns.namespaceID) {
                throw new Error('namespace for this module already set');
            }
        }
        this.namespaceID = ns.namespaceID;
        if (Object.isFrozen(ns)) {
            this[propNamespace] = ns;
        }
        else {
            // Making a copy and freezing it
            this[propNamespace] = Object.freeze(new Namespace(ns));
        }
        this[propNamespace] = ns;
    }
    /**
     * Returns fields from module, filtered and order as requested
     */
    filterFields(requested) {
        if (!requested || requested.length === 0) {
            return [];
        }
        if (!AreStrings(requested)) {
            requested = requested.map((f) => f.name);
        }
        const out = [];
        for (const r of requested) {
            const sf = this.systemFields().find(f => r === f.name);
            if (sf) {
                out.push(sf);
                continue;
            }
            const mf = this.fields.find(f => r === f.name);
            if (mf) {
                out.push(mf);
            }
        }
        return out;
    }
    findField(name) {
        const r = this.filterFields([name]);
        return r && r.length > 0 ? r[0] : undefined;
    }
    fieldNames() {
        return this.fields.map(f => f.name);
    }
    systemFields() {
        return systemFields;
    }
    export() {
        return this;
    }
    import() {
        return this;
    }
}

var _a;
const fieldIndex = Symbol('fieldIndex');
const propModule = Symbol('module');
const cleanValues = Symbol('cleanValues');
const reservedFieldNames = [
    'toJSON',
];
/**
 * For something to be useful module (for a Record), it needs to contain fields
 */
function isModule(m) {
    return m && IsOf(m, 'fields') && Array.isArray(m.fields) && m.fields.length > 0;
}
/**
 * Record class will be used all over the place, user scripts, etc..
 *
 * Constructor (and apply fn) is as versatile as possible to handle
 * different use-cases.
 */
class Record {
    constructor(recModVal1, recModVal2) {
        this.recordID = NoID;
        this.moduleID = NoID;
        this.namespaceID = NoID;
        this.values = {};
        this.labels = {};
        this.createdAt = undefined;
        this.updatedAt = undefined;
        this.deletedAt = undefined;
        this.ownedBy = NoID;
        this.createdBy = NoID;
        this.updatedBy = NoID;
        this.deletedBy = NoID;
        this.canUpdateRecord = false;
        this.canReadRecord = false;
        this.canDeleteRecord = false;
        this.canGrant = false;
        this[_a] = {};
        if (recModVal1 instanceof Record) {
            this.module = recModVal1.module;
            this.apply(recModVal1);
            return;
        }
        if (isModule(recModVal1)) {
            this.module = recModVal1;
            this.apply(recModVal2);
            return;
        }
        if (isModule(recModVal2)) {
            this.module = recModVal2;
            this.apply(recModVal1);
            return;
        }
        throw new Error('invalid module used to initialize a record');
    }
    clone() {
        return new Record(JSON.parse(JSON.stringify(this)));
    }
    /**
     * apply (partially) updates record and it's values
     *
     * @param p
     */
    apply(p) {
        if (p === undefined) {
            // This is a brand new record; set default values
            this.defaultValues();
            return;
        }
        let r;
        // Determine what kind of value we got
        switch (true) {
            case IsOf(p, 'recordID') || IsOf(p, 'values'):
                // p1 is something that looks like a record object
                r = p;
                break;
            case AreObjectsOf(p, 'name'):
                // assuming p1 is array of raw values
                r = ({ values: p });
                break;
            default:
                r = ({ values: p });
        }
        r = r;
        if (this.module && r.moduleID && r.moduleID !== this.module.moduleID) {
            throw new Error('can not change module on a record');
        }
        if (this.namespace && r.namespaceID && r.namespaceID !== this.namespace.namespaceID) {
            throw new Error('can not change namespace on a record');
        }
        if (r.namespaceID && r.namespaceID !== this.module.namespaceID) {
            throw new Error('record and module namespace do not match');
        }
        Apply(this, r, CortezaID, 'recordID', 'moduleID', 'namespaceID');
        Apply(this, r, ISO8601Date, 'createdAt', 'updatedAt', 'deletedAt');
        Apply(this, r, CortezaID, 'ownedBy', 'createdBy', 'updatedBy', 'deletedBy');
        Apply(this, r, Boolean, 'canUpdateRecord', 'canReadRecord', 'canDeleteRecord', 'canGrant');
        // This is a brand new record; set default values
        if (!r.recordID || r.recordID === NoID) {
            this.defaultValues();
        }
        if (r.values !== undefined) {
            this.updateValues(r.values);
        }
        if (!this[cleanValues]) {
            // When there are no clean values,
            // make copy of values so that we know if change occurred
            this[cleanValues] = Object.freeze(Object.assign({}, this.values));
        }
        if (IsOf(r, 'labels')) {
            this.labels = Object.assign({}, r.labels);
        }
    }
    get cleanValues() {
        return this[cleanValues];
    }
    get module() {
        if (this[propModule] === undefined) {
            throw new Error('module not set');
        }
        return this[propModule];
    }
    set module(m) {
        if (this[propModule]) {
            if (this[propModule].moduleID !== m.moduleID) {
                throw new Error('module for this record already set');
            }
        }
        if (!m.fields || !Array.isArray(m.fields) || m.fields.length === 0) {
            throw new Error('module used to initialize a record does not contain any fields');
        }
        this.moduleID = m.moduleID;
        this.namespaceID = m.namespaceID;
        this[fieldIndex] = new Map();
        if (Object.isFrozen(m)) {
            this[propModule] = m;
        }
        else {
            // Making a copy and freezing it
            this[propModule] = Object.freeze(new Module(m));
        }
        this[propModule].fields.forEach(f => {
            const { name, isMulti, kind, defaultValue, } = f;
            if (reservedFieldNames.includes(name)) {
                throw new Error('can not use reserved field name ' + name);
            }
            this[fieldIndex].set(name, { isMulti, kind, defaultValue });
        });
        Object.freeze(this[fieldIndex]);
        this.initValues();
    }
    get namespace() {
        return this.module.namespace;
    }
    /**
     * Converts internal representation of values into array of RawValue objects
     */
    serializeValues() {
        const vv = [];
        this[fieldIndex].forEach(({ isMulti }, name) => {
            if (this.values[name] === undefined) {
                return;
            }
            const val = this.values[name];
            if (isMulti) {
                if (Array.isArray(this.values[name])) {
                    for (let i = 0; i < val.length; i++) {
                        if (val[i] !== undefined) {
                            vv.push({ name, value: val[i].toString() });
                        }
                    }
                }
            }
            else {
                vv.push({ name, value: val.toString() });
            }
        });
        return vv;
    }
    /**
     * Removes existing, resets default values and updates it with new ones
     */
    setValues(...i) {
        this.initValues();
        this.defaultValues();
        this.updateValues(...i);
    }
    /**
     * Removes existing and resets default values
     */
    initValues() {
        const dst = {};
        this[fieldIndex].forEach(({ isMulti }, name) => {
            if (isMulti) {
                dst[name] = [];
            }
            else {
                dst[name] = undefined;
            }
        });
        // TypeScript complains about incompatibility between
        // indexed object and toJSON function
        // @ts-ignore
        dst.toJSON = () => this.serializeValues();
        this.values = dst;
    }
    defaultValues() {
        this[fieldIndex].forEach(({ isMulti, defaultValue }, name) => {
            if (defaultValue && Array.isArray(defaultValue) && defaultValue.length > 0) {
                if (isMulti) {
                    this.values[name] = defaultValue.map(({ value }) => value);
                }
                else {
                    this.values[name] = defaultValue[0].value;
                }
            }
        });
    }
    /**
     * Updates record's values object with provided input
     *
     * Accepted values:
     * 1. Array of RawValue objects:
     *    updateValues([{ name: ..., value: ...}, ...])
     *
     * 2. One or more Value object:
     *    updateValues({ foo: ..., bar: ... }, ...)
     */
    updateValues(...combo) {
        // If all values are formatted as raw value
        if (combo.length === 1 && AreObjectsOf(combo[0], 'name')) {
            combo[0].forEach(({ name, value }) => this.setValue(name, value));
            return;
        }
        combo.forEach(v => {
            if (Array.isArray(v)) {
                this.updateValues(...v);
                return;
            }
            if (!v || typeof v !== 'object') {
                throw Error('expecting array of values or values object');
            }
            // Handle Values
            for (const name of Object.getOwnPropertyNames(v)) {
                this.setValue(name, v[name]);
            }
        });
    }
    /**
     * Sets single value
     *
     * @param name
     * @param value
     */
    setValue(name, value, index = -1) {
        // Skip reserved names
        if (reservedFieldNames.includes(name)) {
            return;
        }
        // Skip unknown fields
        if (!this[fieldIndex].has(name)) {
            return;
        }
        const { kind, isMulti } = this[fieldIndex].get(name);
        if (value === undefined || value.length === 0) {
            // nothing given, nothing set
            this.values[name] = isMulti ? [] : (kind === 'Bool' ? '0' : undefined);
            return;
        }
        if (isMulti) {
            if (Array.isArray(value)) {
                if (index < -1) {
                    // assigning [] to [i]
                    throw Error('can not set array of values to a single value');
                }
                this.values[name] = Array.isArray(value) ? value : [value];
                return;
            }
            if (index === -1) {
                this.values[name].push(value);
                return;
            }
            this.values[name][index] = value;
            return;
        }
        if (Array.isArray(value)) {
            value = value[0];
        }
        // Update with first item or set to undefined
        this.values[name] = value;
    }
    /**
     * Returns resource ID
     */
    get resourceID() {
        return `${this.resourceType}:${this.recordID}`;
    }
    /**
     * Resource type
     */
    get resourceType() {
        return 'compose:record';
    }
}
_a = cleanValues;

const defaultXYWH$1 = [0, 2000, 3, 3];
class PageBlock {
    constructor(i) {
        this.title = '';
        this.description = '';
        // blockID is auto generated by the server in order to support resource translations
        this.blockID = '0';
        this.xywh = defaultXYWH$1;
        this.kind = '';
        this.options = {};
        this.style = {
            variants: {
                headerText: 'dark',
            },
            wrap: {
                kind: 'card',
            },
        };
        this.apply(i);
    }
    apply(i) {
        if (!i)
            return;
        Apply(this, i, String, 'title', 'description', 'blockID');
        if (i.xywh) {
            if (!Array.isArray(i.xywh)) {
                throw new Error('xywh must be an array');
            }
            if (i.xywh.length !== 4) {
                throw new Error('xywh must have 4 elements');
            }
            // by default, park 3x3 block in upper left corner
            this.xywh = i.xywh || defaultXYWH$1;
        }
        if (i.options) {
            this.options = merge$1({}, this.options, i.options);
        }
        if (i.style) {
            this.style = merge$1({}, this.style, i.style);
        }
    }
    // Returns Page Block configuration errors
    validate() {
        return [];
    }
}
const Registry$2 = new Map();

class Button {
    constructor(b) {
        // Used when referring to Corredor automation script
        this.script = undefined;
        // Used when referring to workflow with onManual trigger
        this.workflowID = undefined;
        // Used when referring to a specific step (triggered by onManual trigger)
        this.stepID = undefined;
        // resource type (copied from ui hook or from trigger)
        this.resourceType = undefined;
        // Can override hook's label
        this.label = undefined;
        // can override hook's variant
        this.variant = undefined;
        this.enabled = true;
        Apply(this, b, Boolean, 'enabled');
        Apply(this, b, String, 'label', 'variant', 'script', 'resourceType');
        Apply(this, b, CortezaID, 'workflowID', 'stepID');
    }
}

const kind$e = 'Automation';
const defaults$d = Object.freeze({
    buttons: [],
    sealed: false,
});
class PageBlockAutomation extends PageBlock {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$e;
        this.options = Object.assign({}, defaults$d);
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        if (o.buttons) {
            this.options.buttons = o.buttons.map(b => new Button(b));
        }
    }
    // Validates Page Block configuration
    validate() {
        const ee = super.validate();
        this.options.buttons.forEach(b => {
            if (b.workflowID) {
                // workflow defined
                return;
            }
            if (b.script) {
                // script defined
                return;
            }
            ee.push('Automation button without configured script or workflow');
        });
        return ee;
    }
}
Registry$2.set(kind$e, PageBlockAutomation);

const kind$f = 'Chart';
const defaults$e = Object.freeze({
    chartID: NoID,
});
class PageBlockChart extends PageBlock {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$f;
        this.options = Object.assign({}, defaults$e);
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        Apply(this.options, o, CortezaID, 'chartID');
    }
}
Registry$2.set(kind$f, PageBlockChart);

const kind$g = 'Content';
const defaults$f = Object.freeze({
    body: '',
});
class PageBlockContent extends PageBlock {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$g;
        this.options = Object.assign({}, defaults$f);
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        Apply(this.options, o, String, 'body');
    }
}
Registry$2.set(kind$g, PageBlockContent);

const kind$h = 'File';
const PageBlockFileDefaultMode = 'list';
const PageBlockFileModes = [
    // list of attachments, no preview
    'list',
    // grid of icons
    'grid',
    // single (first) image/file, show preview
    'single',
    // list of all images/files, show preview
    'gallery',
];
const defaults$g = Object.freeze({
    mode: PageBlockFileDefaultMode,
    attachments: [],
    hideFileName: false,
});
class PageBlockFile extends PageBlock {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$h;
        this.options = Object.assign({}, defaults$g);
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        if (o.attachments) {
            this.options.attachments = o.attachments;
        }
        Apply(this.options, o, Boolean, 'hideFileName');
        if (o.mode) {
            if (PageBlockFileModes.includes(o.mode)) {
                this.options.mode = o.mode;
            }
            else {
                o.mode = PageBlockFileDefaultMode;
            }
        }
    }
}
Registry$2.set(kind$h, PageBlockFile);

const kind$i = 'IFrame';
const defaults$h = Object.freeze({
    srcField: '',
    src: '',
    wrap: 'Plain',
});
class PageBlockIFrame extends PageBlock {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$i;
        this.options = Object.assign({}, defaults$h);
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        Apply(this.options, o, String, 'srcField', 'src', 'wrap');
    }
}
Registry$2.set(kind$i, PageBlockIFrame);

const kind$j = 'Record';
const defaults$i = Object.freeze({
    fields: [],
});
class PageBlockRecord extends PageBlock {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$j;
        this.options = Object.assign({}, defaults$i);
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        if (o.fields) {
            this.options.fields = o.fields;
        }
    }
}
Registry$2.set(kind$j, PageBlockRecord);

const kind$k = 'RecordList';
const defaults$j = Object.freeze({
    moduleID: NoID,
    prefilter: '',
    presort: '',
    fields: [],
    hideHeader: false,
    hideAddButton: false,
    hideImportButton: false,
    hideSearch: false,
    hidePaging: false,
    hideSorting: false,
    hideRecordReminderButton: true,
    hideRecordCloneButton: true,
    hideRecordEditButton: false,
    hideRecordViewButton: true,
    hideRecordPermissionsButton: true,
    allowExport: false,
    perPage: 20,
    fullPageNavigation: true,
    showTotalCount: true,
    editable: false,
    draggable: false,
    positionField: undefined,
    refField: undefined,
    editFields: [],
    selectable: true,
    selectMode: 'multi',
    selectionButtons: [],
});
class PageBlockRecordList extends PageBlock {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$k;
        this.options = Object.assign({}, defaults$j);
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        Apply(this.options, o, CortezaID, 'moduleID');
        Apply(this.options, o, String, 'prefilter', 'presort', 'selectMode', 'positionField', 'refField');
        Apply(this.options, o, Number, 'perPage');
        if (o.fields) {
            this.options.fields = o.fields;
        }
        if (o.editFields) {
            this.options.editFields = o.editFields;
        }
        Apply(this.options, o, Boolean, 'hideHeader', 'hideAddButton', 'hideImportButton', 'hideSearch', 'hidePaging', 'fullPageNavigation', 'showTotalCount', 'hideSorting', 'allowExport', 'selectable', 'hideRecordReminderButton', 'hideRecordCloneButton', 'hideRecordEditButton', 'hideRecordViewButton', 'hideRecordPermissionsButton', 'editable', 'draggable');
        if (o.selectionButtons) {
            this.options.selectionButtons = o.selectionButtons.map(b => new Button(b));
        }
    }
    fetch(api, recordListModule, filter) {
        return __awaiter(this, void 0, void 0, function* () {
            if (recordListModule.moduleID !== this.options.moduleID) {
                throw Error('Module incompatible, module mismatch');
            }
            filter.moduleID = this.options.moduleID;
            filter.namespaceID = recordListModule.namespaceID;
            return api
                .recordList(filter)
                .then(r => {
                const { set: records, filter } = r;
                return { records, filter };
            });
        });
    }
}
Registry$2.set(kind$k, PageBlockRecordList);

const kind$l = 'RecordOrganizer';
const defaults$k = Object.freeze({
    moduleID: NoID,
    labelField: '',
    descriptionField: '',
    filter: '',
    positionField: '',
    groupField: '',
    group: '',
});
class PageBlockRecordOrganizer extends PageBlock {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$l;
        this.options = Object.assign({}, defaults$k);
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        Apply(this.options, o, CortezaID, 'moduleID');
        Apply(this.options, o, String, 'labelField', 'descriptionField', 'filter', 'positionField', 'groupField', 'group');
    }
}
Registry$2.set(kind$l, PageBlockRecordOrganizer);

const kind$m = 'SocialFeed';
const defaults$l = Object.freeze({
    moduleID: NoID,
    fields: [],
    profileSourceField: '',
    profileUrl: '',
});
class PageBlockSocialFeed extends PageBlock {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$m;
        this.options = Object.assign({}, defaults$l);
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        Apply(this.options, o, CortezaID, 'moduleID');
        Apply(this.options, o, String, 'profileSourceField', 'profileUrl');
        if (o.fields) {
            this.options.fields = o.fields;
        }
    }
}
Registry$2.set(kind$m, PageBlockSocialFeed);

const feedResources = {
    record: 'compose:record',
    reminder: 'system:reminder',
};

const defOptions = {
    moduleID: NoID,
    color: '#ffffff',
    prefilter: '',
};
/**
 * Feed class represents an event feed for the given calendar
 */
class Feed {
    constructor(i) {
        this.resource = '';
        this.startField = '';
        this.endField = '';
        this.titleField = '';
        this.options = defOptions;
        this.allDay = false;
        this.apply(i);
    }
    apply(i) {
        if (!i)
            return;
        if (!IsOf(i, 'resource') && IsOf(i, 'moduleID')) {
            i = Feed.fromLegacy(i);
        }
        if (IsOf(i, 'resource')) {
            Apply(this, i, String, 'resource', 'startField', 'endField', 'titleField');
            Apply(this, i, Boolean, 'allDay');
            if (i.options) {
                this.options = Object.assign(Object.assign({}, this.options), i.options);
            }
        }
    }
    static fromLegacy(legacy) {
        const p = Object.assign({ 
            // legacy does not have resource,
            // we've used it with records only
            resource: feedResources.record }, legacy);
        if (legacy.moduleID) {
            if (!p.options) {
                p.options = Object.assign({}, defOptions);
            }
            // module was moved under options
            p.options.moduleID = legacy.moduleID;
        }
        return p;
    }
}

const isLightThreshold = 100;
const bgAlpha = 0.7;
/**
 * Helper to determine event's colors
 * @param {String} hex Base color in HEX format
 * @returns {Object} { backgroundColor: String, borderColor: String, isLight: Boolean }
 */
function makeColors(hex) {
    const bg = hr(hex, { format: 'array' });
    const br = [...bg];
    bg[3] = bgAlpha;
    return {
        backgroundColor: `rgba(${bg.join(',')})`,
        borderColor: `rgba(${br.join(',')})`,
        isLight: (bg.slice(0, 3).reduce((acc, cur) => acc + cur, 0) / (bg.length - 1)) > isLightThreshold,
    };
}

// import variables from 'corteza-webapp-compose/src/themes/corteza-base/variables.scss'
// const defaultColor = variables.secondary
// @todo fix this!
const defaultColor = '#568ba2';
/**
 * Loads & converts reminder resource into FC events
 * @param {SystemAPI} $SystemAPI SystemAPI provider
 * @param {User} user Current user
 * @param {Feed} feed Current feed
 * @param {Object} range Current date range
 * @returns {Promise<Array>} Resolves to a set of FC events to display
 */
function ReminderFeed($SystemAPI, user, feed, range) {
    return __awaiter(this, void 0, void 0, function* () {
        feed.options.color = feed.options.color || defaultColor;
        return $SystemAPI.reminderList({
            scheduledFrom: range.start.toISOString(),
            scheduledUntil: range.end.toISOString(),
            scheduledOnly: true,
            excludeDismissed: true,
            assignedTo: user.userID,
        }).then(({ set }) => {
            const { backgroundColor, borderColor, isLight } = makeColors(feed.options.color);
            if (!AreObjectsOf(set, 'reminderID', 'assignedTo', 'remindAt', 'payload')) {
                return [];
            }
            return set.map(r => {
                var _a, _b, _c;
                r = new Reminder(r);
                const classNames = ['event', 'event-reminder'];
                if (r.assignedTo !== user.userID) {
                    classNames.push('event-not-owner');
                }
                if (isLight) {
                    classNames.push('text-dark');
                }
                else {
                    classNames.push('text-white');
                }
                const e = {
                    id: r.reminderID,
                    title: (_b = (_a = r.payload.title, (_a !== null && _a !== void 0 ? _a : r.reminderID)), (_b !== null && _b !== void 0 ? _b : '-')),
                    start: (_c = r.remindAt) === null || _c === void 0 ? void 0 : _c.toISOString(),
                    backgroundColor,
                    borderColor,
                    classNames,
                    allDay: false,
                    extendedProps: {
                        reminderID: r.reminderID,
                    },
                };
                return e;
            });
        });
    });
}

// import variables from 'corteza-webapp-compose/src/themes/corteza-base/variables.scss'
// const defaultColor = variables.primary
// @todo fix this!
const defaultColor$1 = '#568ba2';
function getRecordValue(record, field) {
    const ef = record.module.fields.find(({ name }) => name === field);
    if (ef) {
        return ef.isMulti ? record.values[field] : [record.values[field] || undefined];
    }
    else {
        switch (field) {
            case 'recordID':
            case 'moduleID':
            case 'namespaceID':
                return [record[field]];
            case 'createdAt':
            case 'updatedAt':
            case 'deletedAt':
                if (record[field] !== undefined) {
                    return [record[field].toISOString()];
                }
                break;
        }
    }
    return [undefined];
}
/**
 * Method expands the given record in a (set) of FC event objects.
 * Handles basic recurrence -- multiple date fields.
 * @param {Record} record Record to expand
 * @param {Feed} feed Feed, this record belongs to
 * @returns {Array} A set of expanded events
 */
function expandRecord(record, feed) {
    const events = [];
    const starts = getRecordValue(record, feed.startField);
    const ends = getRecordValue(record, feed.endField);
    const title = getRecordValue(record, feed.titleField).shift() || record.recordID;
    // Make sure ends is at least as long as starts, to avoid length checks
    ends.push(...(new Array(Math.max(starts.length - ends.length, 0)).fill(undefined)));
    const classNames = ['event', 'event-record'];
    const { backgroundColor, borderColor, isLight } = makeColors(feed.options.color || defaultColor$1);
    if (isLight) {
        classNames.push('text-dark');
    }
    else {
        classNames.push('text-white');
    }
    starts.forEach((start, i) => {
        events.push({
            // So FC knows how to group these expanded events
            groupId: record.recordID,
            id: record.recordID,
            title,
            start: start,
            end: ends[i],
            allDay: feed.allDay,
            backgroundColor,
            borderColor,
            classNames,
            extendedProps: {
                moduleID: record.module.moduleID,
                recordID: record.recordID,
            },
        });
    });
    return events;
}
/**
 * Checks if the given field can be used with the given record.
 * A field can be used if it's either defined as a record value OR it's a system field.
 *
 * @param r The record to check
 * @param field The field we wish to use
 */
function recordFeedFilter(r, field) {
    if (r.values[field]) {
        return true;
    }
    return ['createdAt', 'updatedAt', 'deletedAt'].includes(field);
}
/**
 * Loads & converts module resource into FC events
 * @param {ComposeAPI} $ComposeAPI ComposeAPI provider
 * @param {Module} module Current module
 * @param {Namespace} namespace Current namespace
 * @param {Feed} feed Current feed
 * @param {Object} range Current date range
 * @returns {Promise<Array>} Resolves to a set of FC events to display
 */
function RecordFeed($ComposeAPI, module, namespace, feed, range) {
    return __awaiter(this, void 0, void 0, function* () {
        // Params for record fetching
        const params = {
            namespaceID: namespace.namespaceID,
            moduleID: module.moduleID,
            query: `(date(${feed.startField}) <= '${range.end.toISOString()}' AND '${range.start.toISOString()}' <= date(${feed.endField || feed.startField}))`,
        };
        if (feed.options.prefilter) {
            params.query += ` AND (${feed.options.prefilter})`;
        }
        const events = [];
        return $ComposeAPI.recordList(params).then(({ set }) => {
            set
                // Removes all duplicates
                .filter(({ recordID }, index, set) => set.findIndex((r) => recordID === r.recordID) === index)
                // cast & freeze
                .map(r => Object.freeze(new Record(module, r)))
                // drop record w/o proper values
                .filter(r => recordFeedFilter(r, feed.startField))
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                .forEach(r => events.push(...expandRecord(r, feed)));
            return events;
        });
    });
}

const kind$n = 'Calendar';
// Map of < V4 view names to >= V4 view names
const legacyViewMapping = {
    month: 'dayGridMonth',
    agendaMonth: 'dayGridMonth',
    agendaWeek: 'timeGridWeek',
    agendaDay: 'timeGridDay',
    listMonth: 'listMonth',
};
class CalendarOptions {
    constructor() {
        this.defaultView = '';
        this.feeds = [];
        this.header = {};
        this.locale = 'en-gb';
    }
}
/**
 * Helper class to help define calendar's functionality
 */
class PageBlockCalendar extends PageBlock {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$n;
        this.options = new CalendarOptions();
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        var _a;
        if (!o)
            return;
        this.options.defaultView = PageBlockCalendar.handleLegacyView(o.defaultView) || 'dayGridMonth';
        this.options.feeds = (o.feeds || []).map(f => new Feed(f));
        this.options.header = merge$1({}, this.options.header, o.header, { views: PageBlockCalendar.handleLegacyViews(((_a = o.header) === null || _a === void 0 ? void 0 : _a.views) || []) });
        this.options.locale = o.locale || 'en-gb';
    }
    /**
     * Generates a header object of fullcalendar
     * @returns {Object}
     */
    getHeader() {
        const h = this.options.header;
        if (h.hide) {
            return;
        }
        // Show view buttons only when 2 or more are selected
        let right = '';
        if (h.views && h.views.length >= 2) {
            right = this.reorderViews(h.views).join(',');
        }
        return {
            left: `${h.hidePrevNext ? '' : 'prev,next'} ${h.hideToday ? '' : 'today'}`.trim(),
            center: `${h.hideTitle ? '' : 'title'}`,
            right,
        };
    }
    /**
     * Provides a list of available views.
     * @note When adding new ones, make sure included plugins support it.
     * @returns {Array}
     */
    static availableViews() {
        return [
            'dayGridMonth',
            'timeGridWeek',
            'timeGridDay',
            'listMonth',
        ];
    }
    /**
     * Reorder views according to available views array order.
     * @param {Array} views Array of views to filter & sort
     */
    reorderViews(views = []) {
        return PageBlockCalendar.availableViews()
            .filter(v => views.find(fv => fv === v))
            .map(v => v);
    }
    /**
     * Converts old < V4 view names to >= V4 view names.
     * @note It wil preserve fields that don't need to/can't be converted
     * @param {string} views converted view name
     */
    static handleLegacyView(views = 'dayGridMonth') {
        return legacyViewMapping[views] || views;
    }
    /**
     * Converts old < V4 view names to >= V4 view names.
     * @note It wil preserve fields that don't need to/can't be converted
     * @param {string[]} views converted view names
     */
    static handleLegacyViews(views) {
        return views.map(v => legacyViewMapping[v] || v);
    }
    static makeFeed(f) {
        return new Feed(f);
    }
}
PageBlockCalendar.feedResources = Object.freeze({
    record: 'compose:record',
    reminder: 'system:reminder',
});
PageBlockCalendar.ReminderFeed = ReminderFeed;
PageBlockCalendar.RecordFeed = RecordFeed;
Registry$2.set(kind$n, PageBlockCalendar);

var ChartType;
(function (ChartType) {
    ChartType["pie"] = "pie";
    ChartType["bar"] = "bar";
    ChartType["line"] = "line";
    ChartType["doughnut"] = "doughnut";
    ChartType["funnel"] = "funnel";
    ChartType["gauge"] = "gauge";
})(ChartType || (ChartType = {}));
var ChartRenderer;
(function (ChartRenderer) {
    ChartRenderer["chartJS"] = "chart.js";
})(ChartRenderer || (ChartRenderer = {}));
const aggregateFunctions = [
    {
        value: 'SUM',
        text: 'sum',
    },
    {
        value: 'MAX',
        text: 'max',
    },
    {
        value: 'MIN',
        text: 'min',
    },
    {
        value: 'AVG',
        text: 'avg',
    },
    {
        value: 'STD',
        text: 'std',
    },
];
class DimensionFunctions extends Array {
    constructor(items) {
        super(...(items || []));
    }
    static create() {
        return Object.create(DimensionFunctions.prototype);
    }
    lookup(d) {
        return this.find((f) => d.modifier === f.value);
    }
    convert(d) {
        return (this.lookup(d) || {}).convert(d.field);
    }
}
const dimensionFunctions = DimensionFunctions.create();
dimensionFunctions.push(...[
    {
        text: 'none',
        value: '(no grouping / buckets)',
        convert: (f) => f,
        time: false,
    },
    {
        text: 'date',
        value: 'DATE',
        convert: (f) => `DATE(${f})`,
        time: { unit: 'day', minUnit: 'day', round: true },
    },
    {
        text: 'week',
        value: 'WEEK',
        convert: (f) => `DATE(${f})`,
        time: { unit: 'week', minUnit: 'week', round: true, isoWeekday: true },
    },
    {
        text: 'month',
        value: 'MONTH',
        convert: (f) => `DATE_FORMAT(${f}, '%Y-%m-01')`,
        time: { unit: 'month', minUnit: 'month', round: true },
    },
    {
        text: 'quarter',
        value: 'QUARTER',
        convert: (f) => `DATE_FORMAT(${f}, '%Y-%m-01')`,
        time: { unit: 'quarter', minUnit: 'quarter', round: true },
    },
    {
        text: 'year',
        value: 'YEAR',
        convert: (f) => `DATE_FORMAT(${f}, '%Y-01-01')`,
        time: { unit: 'year', minUnit: 'year', round: true },
    },
]);
const predefinedFilters = [
    {
        value: 'YEAR(created_at) = YEAR(NOW())',
        text: 'recordsCreatedThisYear',
    },
    {
        value: 'YEAR(created_at) = YEAR(NOW()) - 1',
        text: 'recordsCreatedLastYear',
    },
    {
        value: 'YEAR(created_at) = YEAR(NOW()) AND QUARTER(created_at) = QUARTER(NOW())',
        text: 'recordsCreatedThisQuarter',
    },
    {
        value: 'YEAR(created_at) = YEAR(NOW()) - 1 AND QUARTER(created_at) = QUARTER(DATE_SUB(NOW(), INTERVAL 3 MONTH)',
        text: 'recordsCreatedLastQuarter',
    },
    {
        value: 'DATE_FORMAT(created_at, \'%Y-%m\') = DATE_FORMAT(NOW(), \'%Y-%m\')',
        text: 'recordsCreatedThisMonth',
    },
    {
        value: 'DATE_FORMAT(created_at, \'%Y-%m\') = DATE_FORMAT(DATE_SUB(NOW(), INTERVAL 1 YEAR), \'%Y-%m\')',
        text: 'recordsCreatedLastMonth',
    },
];
dimensionFunctions.lookup = (d) => dimensionFunctions.find(f => d.modifier === f.value);
dimensionFunctions.convert = (d) => (dimensionFunctions.lookup(d) || {}).convert(d.field);
const isRadialChart = ({ type }) => type === 'doughnut' || type === 'pie';
const hasRelativeDisplay = ({ type }) => isRadialChart({ type });
const makeDataLabel = ({ prefix = '', value = 0, relativeValue = false, suffix = '', }) => {
    if (typeof value === 'object') {
        value = value.y || 0;
    }
    if (relativeValue) {
        suffix = suffix || '%';
    }
    return `${prefix ? prefix + ': ' : ''}${(Number(value))}${suffix}`;
};
function calculatePercentages(values, relativePrecision, relativeValue = false) {
    let errorRounding = 0;
    const total = values.reduce((acc, cur) => acc + cur, 0);
    let portions = values.map((n) => n / total * 100);
    if (relativeValue) {
        let result = 0;
        return portions.map(v => (result = Number((v + errorRounding).toFixed(relativePrecision || 2)), errorRounding += v - Number(result), result));
    }
    return values;
}
// Makes a standarised alias from modifier or dimension report option
const makeAlias = ({ alias, aggregate, modifier, field }) => alias || `${aggregate || modifier || 'none'}_${field}`.toLocaleLowerCase();
const chartUtil = {
    dimensionFunctions,
    hasRelativeDisplay,
    aggregateFunctions,
    predefinedFilters,
    ChartType,
};

const kind$o = 'Metric';
var Operation;
(function (Operation) {
    Operation["SUM"] = "sum";
    Operation["MAX"] = "max";
    Operation["MIN"] = "min";
    Operation["AVG"] = "avg";
})(Operation || (Operation = {}));
const defaults$m = Object.freeze({
    metrics: [],
});
class PageBlockMetric extends PageBlock {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$o;
        this.options = Object.assign({}, defaults$m);
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        if (o.metrics) {
            this.options.metrics = o.metrics;
        }
    }
    /**
     * Helper function to fetch and parse reporter's reports.
     */
    fetch({ m }, reporter) {
        return __awaiter(this, void 0, void 0, function* () {
            const w = yield reporter(this.formatParams(m));
            const datasets = w.map((r) => r.rp || r.count);
            let rtr;
            if (m.operation === Operation.MAX) {
                rtr = datasets.sort((a, b) => b - a)[0];
            }
            else if (m.operation === Operation.MIN) {
                rtr = datasets.sort((a, b) => a - b)[0];
            }
            else if (m.operation === Operation.AVG) {
                rtr = datasets.reduce((acc, cur) => acc + cur, 0) / datasets.length;
            }
            else {
                rtr = datasets.reduce((acc, cur) => acc + cur, 0);
            }
            if (m.transformFx) {
                rtr = (new Function('v', `return ${m.transformFx}`))(rtr);
            }
            return [{ value: rtr }];
        });
    }
    /**
     * Helper to construct reporter's params
     */
    formatParams({ moduleID, filter, metricField, operation }) {
        const metrics = [];
        if (metricField !== 'count') {
            metrics.push(`${operation}(${metricField}) AS rp`);
        }
        return {
            moduleID,
            filter,
            metrics: metrics.join(','),
            dimensions: dimensionFunctions.convert({ modifier: 'YEAR', field: 'created_at' }),
        };
    }
}
Registry$2.set(kind$o, PageBlockMetric);

const kind$p = 'Comment';
const defaults$n = Object.freeze({
    moduleID: NoID,
    filter: '',
    titleField: '',
    contentField: '',
    sortDirection: '',
    referenceField: '',
});
class PageBlockComment extends PageBlock {
    constructor(i) {
        var _a;
        super(i);
        this.kind = kind$p;
        this.options = Object.assign({}, defaults$n);
        this.applyOptions((_a = i) === null || _a === void 0 ? void 0 : _a.options);
    }
    applyOptions(o) {
        if (!o)
            return;
        Apply(this.options, o, CortezaID, 'moduleID');
        Apply(this.options, o, String, 'titleField', 'contentField', 'referenceField', 'filter', 'sortDirection');
    }
}
Registry$2.set(kind$p, PageBlockComment);

function PageBlockMaker(i) {
    const PageBlockTemp = Registry$2.get(i.kind);
    if (PageBlockTemp === undefined) {
        throw new Error(`unknown block kind '${i.kind}'`);
    }
    if (i instanceof PageBlock) {
        // Get rid of the references
        i = JSON.parse(JSON.stringify(i));
    }
    return new PageBlockTemp(i);
}

class Page {
    constructor(i) {
        this.pageID = NoID;
        this.selfID = NoID;
        this.moduleID = NoID;
        this.namespaceID = NoID;
        this.title = '';
        this.handle = '';
        this.description = '';
        this.weight = 0;
        this.labels = {};
        this.visible = false;
        this.blocks = [];
        this.createdAt = undefined;
        this.updatedAt = undefined;
        this.deletedAt = undefined;
        this.canUpdatePage = false;
        this.canDeletePage = false;
        this.canGrant = false;
        this.apply(i);
    }
    clone() {
        return new Page(JSON.parse(JSON.stringify(this)));
    }
    apply(i) {
        if (!i)
            return;
        Apply(this, i, CortezaID, 'pageID', 'selfID', 'moduleID', 'namespaceID');
        Apply(this, i, String, 'title', 'handle', 'description');
        Apply(this, i, Number, 'weight');
        Apply(this, i, Boolean, 'visible');
        if (i.blocks) {
            this.blocks = [];
            if (AreObjectsOf(i.blocks, 'kind') && AreObjectsOf(i.blocks, 'xywh')) {
                this.blocks = i.blocks.map((b) => PageBlockMaker(b));
            }
        }
        if (i.children) {
            this.children = [];
            if (AreObjectsOf(i.children, 'pageID')) {
                this.children = i.children.map(c => new Page(c));
            }
        }
        if (IsOf(i, 'labels')) {
            this.labels = Object.assign({}, i.labels);
        }
        Apply(this, i, ISO8601Date, 'createdAt', 'updatedAt', 'deletedAt');
        Apply(this, i, Boolean, 'canUpdatePage', 'canDeletePage', 'canGrant');
    }
    /**
     * Returns resource ID
     */
    get resourceID() {
        return `${this.resourceType}:${this.pageID}`;
    }
    /**
     * Resource type
     */
    get resourceType() {
        return 'compose:page';
    }
    get isRecordPage() {
        return this.moduleID !== NoID;
    }
    get firstLevel() {
        return this.selfID === NoID;
    }
    /**
     * Validates page & it's blocks
     */
    validate() {
        const ee = [];
        if (this.blocks.length === 0) {
            ee.push('blocks missing');
        }
        else {
            this.blocks.forEach(b => {
                ee.push(...b.validate());
            });
        }
        return ee;
    }
    export() {
        return {
            title: this.title,
            handle: this.handle,
            description: this.description,
            visible: this.visible,
            blocks: this.blocks,
        };
    }
}

class ValidatorError {
    constructor(i) {
        /**
         * Any additional meta data that can be used to expand (translated) message,
         * or to group, categorize validator results
         */
        this.meta = {};
        if (typeof i === 'string') {
            this.kind = i;
            this.message = i;
        }
        else {
            this.kind = i.kind;
            this.message = i.message || i.kind;
            if (i.meta) {
                this.meta = merge$1({}, this.meta, i.meta);
            }
        }
    }
}
const ValidatorFalseDefaultError = Object.freeze(new ValidatorError({
    message: 'Internal error',
    kind: 'internal',
}));
function NormalizeValidatorResults(...r) {
    const out = [];
    r.forEach(r => {
        if (r === undefined || r === null || r === true) {
            // considering these validation results as valid
            return;
        }
        if (Array.isArray(r)) {
            // Expand & normalize each item...
            out.push(...(NormalizeValidatorResults(...r)));
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        if (r instanceof Validated) {
            out.push(...r.get());
            return;
        }
        if (r instanceof ValidatorError) {
            out.push(r);
            return;
        }
        if (IsOf(r, 'kind') && r.kind) {
            out.push(new ValidatorError(r));
            return;
        }
        // Catch-all for non object errors
        out.push(ValidatorFalseDefaultError);
    });
    return out;
}
/**
 * Holds an manipulates set of errors
 */
class Validated {
    constructor(...r) {
        this.set = [];
        this.push(...r);
    }
    get() {
        return this.set;
    }
    get length() {
        return this.set.length;
    }
    valid() {
        return this.length === 0;
    }
    push(...r) {
        this.set.push(...NormalizeValidatorResults(...r));
    }
    applyMeta(meta) {
        this.set = this.set.map(r => {
            const appliedMeta = Object.assign(Object.assign({}, r), { meta: Object.assign(Object.assign({}, r.meta), meta) });
            if (r instanceof ValidatorError) {
                return new ValidatorError(appliedMeta);
            }
            return new ValidatorError(appliedMeta);
        });
    }
    filter(fn) {
        return new Validated(this.set.filter(fn));
    }
    /**
     * Filters by meta keys
     *
     * If only key is given it returns entries that have meta with that key
     *
     * @param {string} key
     * @param {unknown} value
     */
    filterByMeta(key, value) {
        return this.filter((err) => (value === undefined ? err.meta[key] !== undefined : err.meta[key] === value));
    }
}
function IsEmpty(v) {
    if (!v || (IsOf(v, 'length') && v.length && v.length === 0)) {
        return true;
    }
    if (Array.isArray(v)) {
        return v.every(i => !i);
    }
    return !v;
}
/**
 * Checks if values are equal
 * @param {string|string[]} v1 Value in question
 * @param {string|string[]} v2 Value to compare to
 * @returns {boolean}
 */
function AreEqual(v1, v2) {
    if (Array.isArray(v1)) {
        if (!Array.isArray(v2) || v1.length !== v2.length) {
            return false;
        }
        return !!v1.find((v, i) => v !== v2[i]);
    }
    else {
        return v1 === v2;
    }
}
/**
 * Validator is record validation tool that registers and runs record & field validators
 *
 * Record and field validators are functions that
 */
class Validator {
    constructor(...vfn) {
        /**
         * Validators
         */
        this.registered = [];
        if (vfn) {
            this.registered.push(...vfn);
        }
    }
    push(...vfn) {
        this.registered.push(...vfn);
    }
    run(target, ...args) {
        return new Validated(...this.registered.map(vfn => vfn.call(target, ...args)));
    }
}

var validator = /*#__PURE__*/Object.freeze({
  __proto__: null,
  ValidatorError: ValidatorError,
  NormalizeValidatorResults: NormalizeValidatorResults,
  Validated: Validated,
  IsEmpty: IsEmpty,
  AreEqual: AreEqual,
  Validator: Validator
});

const emptyErr = new ValidatorError({ kind: 'empty', message: 'This field is required' });
const duplicateValueInSetErr = new ValidatorError('duplicateValueInSet');
function genericFieldValidator(field) {
    // newValue is of type unknown to satisfy ValidatorFn interface
    return function (arg0) {
        if (!IsOf(arg0, 'field', 'value', 'oldValue')) {
            throw Error('invalid field validator argument type');
        }
        const { value } = arg0;
        if (field.isRequired) {
            if (value === undefined || IsEmpty(value)) {
                return emptyErr;
            }
        }
    };
}
class RecordValidator extends Validator {
    /**
     * Construct record validator from module (or record)
     *
     * @param m
     */
    constructor(m) {
        super();
        this.rfv = {};
        if (m instanceof Record) {
            m = m.module;
        }
        m.fields.forEach(field => {
            this.rfv[field.name] = new Validator(genericFieldValidator(field));
        });
    }
    /**
     * Append more record validators
     *
     * @param name
     * @param vfn
     */
    push(...vfn) {
        this.registered.push(...vfn);
    }
    /**
     * Append more field validators
     *
     * @param name
     * @param vfn
     */
    pushToField(name, ...vfn) {
        if (!this.rfv[name]) {
            throw new Error('can not push validators to unknown field');
        }
        this.rfv[name].push(...vfn);
    }
    /**
     * Runs validators on record and all (or whitelisted) fields
     */
    run(r, ...fields) {
        const out = new Validated();
        if (fields.length === 0) {
            // Fields are not explicitly provided,
            // we can run record-wide validators:
            const result = super.run(r);
            out.push(result.get());
            // get list of fields from registered field validators
            fields = Object.getOwnPropertyNames(this.rfv);
        }
        for (const f of fields) {
            const field = r.module.findField(f);
            if (!field) {
                continue;
            }
            const payload = {
                value: r.values[f],
                oldValue: r.cleanValues[f],
                field,
            };
            const { recordID = NoID } = r || {};
            const results = this.rfv[f].run(r, payload);
            results.applyMeta({ field: f, id: recordID === NoID ? 'parent:0' : recordID });
            out.push(results.get());
        }
        return out;
    }
}

function isNothing(subject) {
  return (typeof subject === 'undefined') || (subject === null);
}


function isObject(subject) {
  return (typeof subject === 'object') && (subject !== null);
}


function toArray(sequence) {
  if (Array.isArray(sequence)) return sequence;
  else if (isNothing(sequence)) return [];

  return [ sequence ];
}


function extend(target, source) {
  var index, length, key, sourceKeys;

  if (source) {
    sourceKeys = Object.keys(source);

    for (index = 0, length = sourceKeys.length; index < length; index += 1) {
      key = sourceKeys[index];
      target[key] = source[key];
    }
  }

  return target;
}


function repeat(string, count) {
  var result = '', cycle;

  for (cycle = 0; cycle < count; cycle += 1) {
    result += string;
  }

  return result;
}


function isNegativeZero(number) {
  return (number === 0) && (Number.NEGATIVE_INFINITY === 1 / number);
}


var isNothing_1      = isNothing;
var isObject_1       = isObject;
var toArray_1        = toArray;
var repeat_1         = repeat;
var isNegativeZero_1 = isNegativeZero;
var extend_1         = extend;

var common = {
	isNothing: isNothing_1,
	isObject: isObject_1,
	toArray: toArray_1,
	repeat: repeat_1,
	isNegativeZero: isNegativeZero_1,
	extend: extend_1
};

// YAML error class. http://stackoverflow.com/questions/8458984

function YAMLException(reason, mark) {
  // Super constructor
  Error.call(this);

  this.name = 'YAMLException';
  this.reason = reason;
  this.mark = mark;
  this.message = (this.reason || '(unknown reason)') + (this.mark ? ' ' + this.mark.toString() : '');

  // Include stack trace in error object
  if (Error.captureStackTrace) {
    // Chrome and NodeJS
    Error.captureStackTrace(this, this.constructor);
  } else {
    // FF, IE 10+ and Safari 6+. Fallback for others
    this.stack = (new Error()).stack || '';
  }
}


// Inherit from Error
YAMLException.prototype = Object.create(Error.prototype);
YAMLException.prototype.constructor = YAMLException;


YAMLException.prototype.toString = function toString(compact) {
  var result = this.name + ': ';

  result += this.reason || '(unknown reason)';

  if (!compact && this.mark) {
    result += ' ' + this.mark.toString();
  }

  return result;
};


var exception = YAMLException;

function Mark(name, buffer, position, line, column) {
  this.name     = name;
  this.buffer   = buffer;
  this.position = position;
  this.line     = line;
  this.column   = column;
}


Mark.prototype.getSnippet = function getSnippet(indent, maxLength) {
  var head, start, tail, end, snippet;

  if (!this.buffer) return null;

  indent = indent || 4;
  maxLength = maxLength || 75;

  head = '';
  start = this.position;

  while (start > 0 && '\x00\r\n\x85\u2028\u2029'.indexOf(this.buffer.charAt(start - 1)) === -1) {
    start -= 1;
    if (this.position - start > (maxLength / 2 - 1)) {
      head = ' ... ';
      start += 5;
      break;
    }
  }

  tail = '';
  end = this.position;

  while (end < this.buffer.length && '\x00\r\n\x85\u2028\u2029'.indexOf(this.buffer.charAt(end)) === -1) {
    end += 1;
    if (end - this.position > (maxLength / 2 - 1)) {
      tail = ' ... ';
      end -= 5;
      break;
    }
  }

  snippet = this.buffer.slice(start, end);

  return common.repeat(' ', indent) + head + snippet + tail + '\n' +
         common.repeat(' ', indent + this.position - start + head.length) + '^';
};


Mark.prototype.toString = function toString(compact) {
  var snippet, where = '';

  if (this.name) {
    where += 'in "' + this.name + '" ';
  }

  where += 'at line ' + (this.line + 1) + ', column ' + (this.column + 1);

  if (!compact) {
    snippet = this.getSnippet();

    if (snippet) {
      where += ':\n' + snippet;
    }
  }

  return where;
};


var mark = Mark;

var TYPE_CONSTRUCTOR_OPTIONS = [
  'kind',
  'resolve',
  'construct',
  'instanceOf',
  'predicate',
  'represent',
  'defaultStyle',
  'styleAliases'
];

var YAML_NODE_KINDS = [
  'scalar',
  'sequence',
  'mapping'
];

function compileStyleAliases(map) {
  var result = {};

  if (map !== null) {
    Object.keys(map).forEach(function (style) {
      map[style].forEach(function (alias) {
        result[String(alias)] = style;
      });
    });
  }

  return result;
}

function Type(tag, options) {
  options = options || {};

  Object.keys(options).forEach(function (name) {
    if (TYPE_CONSTRUCTOR_OPTIONS.indexOf(name) === -1) {
      throw new exception('Unknown option "' + name + '" is met in definition of "' + tag + '" YAML type.');
    }
  });

  // TODO: Add tag format check.
  this.tag          = tag;
  this.kind         = options['kind']         || null;
  this.resolve      = options['resolve']      || function () { return true; };
  this.construct    = options['construct']    || function (data) { return data; };
  this.instanceOf   = options['instanceOf']   || null;
  this.predicate    = options['predicate']    || null;
  this.represent    = options['represent']    || null;
  this.defaultStyle = options['defaultStyle'] || null;
  this.styleAliases = compileStyleAliases(options['styleAliases'] || null);

  if (YAML_NODE_KINDS.indexOf(this.kind) === -1) {
    throw new exception('Unknown kind "' + this.kind + '" is specified for "' + tag + '" YAML type.');
  }
}

var type = Type;

/*eslint-disable max-len*/






function compileList(schema, name, result) {
  var exclude = [];

  schema.include.forEach(function (includedSchema) {
    result = compileList(includedSchema, name, result);
  });

  schema[name].forEach(function (currentType) {
    result.forEach(function (previousType, previousIndex) {
      if (previousType.tag === currentType.tag && previousType.kind === currentType.kind) {
        exclude.push(previousIndex);
      }
    });

    result.push(currentType);
  });

  return result.filter(function (type, index) {
    return exclude.indexOf(index) === -1;
  });
}


function compileMap(/* lists... */) {
  var result = {
        scalar: {},
        sequence: {},
        mapping: {},
        fallback: {}
      }, index, length;

  function collectType(type) {
    result[type.kind][type.tag] = result['fallback'][type.tag] = type;
  }

  for (index = 0, length = arguments.length; index < length; index += 1) {
    arguments[index].forEach(collectType);
  }
  return result;
}


function Schema(definition) {
  this.include  = definition.include  || [];
  this.implicit = definition.implicit || [];
  this.explicit = definition.explicit || [];

  this.implicit.forEach(function (type) {
    if (type.loadKind && type.loadKind !== 'scalar') {
      throw new exception('There is a non-scalar type in the implicit list of a schema. Implicit resolving of such types is not supported.');
    }
  });

  this.compiledImplicit = compileList(this, 'implicit', []);
  this.compiledExplicit = compileList(this, 'explicit', []);
  this.compiledTypeMap  = compileMap(this.compiledImplicit, this.compiledExplicit);
}


Schema.DEFAULT = null;


Schema.create = function createSchema() {
  var schemas, types;

  switch (arguments.length) {
    case 1:
      schemas = Schema.DEFAULT;
      types = arguments[0];
      break;

    case 2:
      schemas = arguments[0];
      types = arguments[1];
      break;

    default:
      throw new exception('Wrong number of arguments for Schema.create function');
  }

  schemas = common.toArray(schemas);
  types = common.toArray(types);

  if (!schemas.every(function (schema) { return schema instanceof Schema; })) {
    throw new exception('Specified list of super schemas (or a single Schema object) contains a non-Schema object.');
  }

  if (!types.every(function (type$1) { return type$1 instanceof type; })) {
    throw new exception('Specified list of YAML types (or a single Type object) contains a non-Type object.');
  }

  return new Schema({
    include: schemas,
    explicit: types
  });
};


var schema = Schema;

var str = new type('tag:yaml.org,2002:str', {
  kind: 'scalar',
  construct: function (data) { return data !== null ? data : ''; }
});

var seq = new type('tag:yaml.org,2002:seq', {
  kind: 'sequence',
  construct: function (data) { return data !== null ? data : []; }
});

var map = new type('tag:yaml.org,2002:map', {
  kind: 'mapping',
  construct: function (data) { return data !== null ? data : {}; }
});

var failsafe = new schema({
  explicit: [
    str,
    seq,
    map
  ]
});

function resolveYamlNull(data) {
  if (data === null) return true;

  var max = data.length;

  return (max === 1 && data === '~') ||
         (max === 4 && (data === 'null' || data === 'Null' || data === 'NULL'));
}

function constructYamlNull() {
  return null;
}

function isNull(object) {
  return object === null;
}

var _null = new type('tag:yaml.org,2002:null', {
  kind: 'scalar',
  resolve: resolveYamlNull,
  construct: constructYamlNull,
  predicate: isNull,
  represent: {
    canonical: function () { return '~';    },
    lowercase: function () { return 'null'; },
    uppercase: function () { return 'NULL'; },
    camelcase: function () { return 'Null'; }
  },
  defaultStyle: 'lowercase'
});

function resolveYamlBoolean(data) {
  if (data === null) return false;

  var max = data.length;

  return (max === 4 && (data === 'true' || data === 'True' || data === 'TRUE')) ||
         (max === 5 && (data === 'false' || data === 'False' || data === 'FALSE'));
}

function constructYamlBoolean(data) {
  return data === 'true' ||
         data === 'True' ||
         data === 'TRUE';
}

function isBoolean(object) {
  return Object.prototype.toString.call(object) === '[object Boolean]';
}

var bool = new type('tag:yaml.org,2002:bool', {
  kind: 'scalar',
  resolve: resolveYamlBoolean,
  construct: constructYamlBoolean,
  predicate: isBoolean,
  represent: {
    lowercase: function (object) { return object ? 'true' : 'false'; },
    uppercase: function (object) { return object ? 'TRUE' : 'FALSE'; },
    camelcase: function (object) { return object ? 'True' : 'False'; }
  },
  defaultStyle: 'lowercase'
});

function isHexCode(c) {
  return ((0x30/* 0 */ <= c) && (c <= 0x39/* 9 */)) ||
         ((0x41/* A */ <= c) && (c <= 0x46/* F */)) ||
         ((0x61/* a */ <= c) && (c <= 0x66/* f */));
}

function isOctCode(c) {
  return ((0x30/* 0 */ <= c) && (c <= 0x37/* 7 */));
}

function isDecCode(c) {
  return ((0x30/* 0 */ <= c) && (c <= 0x39/* 9 */));
}

function resolveYamlInteger(data) {
  if (data === null) return false;

  var max = data.length,
      index = 0,
      hasDigits = false,
      ch;

  if (!max) return false;

  ch = data[index];

  // sign
  if (ch === '-' || ch === '+') {
    ch = data[++index];
  }

  if (ch === '0') {
    // 0
    if (index + 1 === max) return true;
    ch = data[++index];

    // base 2, base 8, base 16

    if (ch === 'b') {
      // base 2
      index++;

      for (; index < max; index++) {
        ch = data[index];
        if (ch === '_') continue;
        if (ch !== '0' && ch !== '1') return false;
        hasDigits = true;
      }
      return hasDigits && ch !== '_';
    }


    if (ch === 'x') {
      // base 16
      index++;

      for (; index < max; index++) {
        ch = data[index];
        if (ch === '_') continue;
        if (!isHexCode(data.charCodeAt(index))) return false;
        hasDigits = true;
      }
      return hasDigits && ch !== '_';
    }

    // base 8
    for (; index < max; index++) {
      ch = data[index];
      if (ch === '_') continue;
      if (!isOctCode(data.charCodeAt(index))) return false;
      hasDigits = true;
    }
    return hasDigits && ch !== '_';
  }

  // base 10 (except 0) or base 60

  // value should not start with `_`;
  if (ch === '_') return false;

  for (; index < max; index++) {
    ch = data[index];
    if (ch === '_') continue;
    if (ch === ':') break;
    if (!isDecCode(data.charCodeAt(index))) {
      return false;
    }
    hasDigits = true;
  }

  // Should have digits and should not end with `_`
  if (!hasDigits || ch === '_') return false;

  // if !base60 - done;
  if (ch !== ':') return true;

  // base60 almost not used, no needs to optimize
  return /^(:[0-5]?[0-9])+$/.test(data.slice(index));
}

function constructYamlInteger(data) {
  var value = data, sign = 1, ch, base, digits = [];

  if (value.indexOf('_') !== -1) {
    value = value.replace(/_/g, '');
  }

  ch = value[0];

  if (ch === '-' || ch === '+') {
    if (ch === '-') sign = -1;
    value = value.slice(1);
    ch = value[0];
  }

  if (value === '0') return 0;

  if (ch === '0') {
    if (value[1] === 'b') return sign * parseInt(value.slice(2), 2);
    if (value[1] === 'x') return sign * parseInt(value, 16);
    return sign * parseInt(value, 8);
  }

  if (value.indexOf(':') !== -1) {
    value.split(':').forEach(function (v) {
      digits.unshift(parseInt(v, 10));
    });

    value = 0;
    base = 1;

    digits.forEach(function (d) {
      value += (d * base);
      base *= 60;
    });

    return sign * value;

  }

  return sign * parseInt(value, 10);
}

function isInteger(object) {
  return (Object.prototype.toString.call(object)) === '[object Number]' &&
         (object % 1 === 0 && !common.isNegativeZero(object));
}

var int_1 = new type('tag:yaml.org,2002:int', {
  kind: 'scalar',
  resolve: resolveYamlInteger,
  construct: constructYamlInteger,
  predicate: isInteger,
  represent: {
    binary:      function (obj) { return obj >= 0 ? '0b' + obj.toString(2) : '-0b' + obj.toString(2).slice(1); },
    octal:       function (obj) { return obj >= 0 ? '0'  + obj.toString(8) : '-0'  + obj.toString(8).slice(1); },
    decimal:     function (obj) { return obj.toString(10); },
    /* eslint-disable max-len */
    hexadecimal: function (obj) { return obj >= 0 ? '0x' + obj.toString(16).toUpperCase() :  '-0x' + obj.toString(16).toUpperCase().slice(1); }
  },
  defaultStyle: 'decimal',
  styleAliases: {
    binary:      [ 2,  'bin' ],
    octal:       [ 8,  'oct' ],
    decimal:     [ 10, 'dec' ],
    hexadecimal: [ 16, 'hex' ]
  }
});

var YAML_FLOAT_PATTERN = new RegExp(
  // 2.5e4, 2.5 and integers
  '^(?:[-+]?(?:0|[1-9][0-9_]*)(?:\\.[0-9_]*)?(?:[eE][-+]?[0-9]+)?' +
  // .2e4, .2
  // special case, seems not from spec
  '|\\.[0-9_]+(?:[eE][-+]?[0-9]+)?' +
  // 20:59
  '|[-+]?[0-9][0-9_]*(?::[0-5]?[0-9])+\\.[0-9_]*' +
  // .inf
  '|[-+]?\\.(?:inf|Inf|INF)' +
  // .nan
  '|\\.(?:nan|NaN|NAN))$');

function resolveYamlFloat(data) {
  if (data === null) return false;

  if (!YAML_FLOAT_PATTERN.test(data) ||
      // Quick hack to not allow integers end with `_`
      // Probably should update regexp & check speed
      data[data.length - 1] === '_') {
    return false;
  }

  return true;
}

function constructYamlFloat(data) {
  var value, sign, base, digits;

  value  = data.replace(/_/g, '').toLowerCase();
  sign   = value[0] === '-' ? -1 : 1;
  digits = [];

  if ('+-'.indexOf(value[0]) >= 0) {
    value = value.slice(1);
  }

  if (value === '.inf') {
    return (sign === 1) ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;

  } else if (value === '.nan') {
    return NaN;

  } else if (value.indexOf(':') >= 0) {
    value.split(':').forEach(function (v) {
      digits.unshift(parseFloat(v, 10));
    });

    value = 0.0;
    base = 1;

    digits.forEach(function (d) {
      value += d * base;
      base *= 60;
    });

    return sign * value;

  }
  return sign * parseFloat(value, 10);
}


var SCIENTIFIC_WITHOUT_DOT = /^[-+]?[0-9]+e/;

function representYamlFloat(object, style) {
  var res;

  if (isNaN(object)) {
    switch (style) {
      case 'lowercase': return '.nan';
      case 'uppercase': return '.NAN';
      case 'camelcase': return '.NaN';
    }
  } else if (Number.POSITIVE_INFINITY === object) {
    switch (style) {
      case 'lowercase': return '.inf';
      case 'uppercase': return '.INF';
      case 'camelcase': return '.Inf';
    }
  } else if (Number.NEGATIVE_INFINITY === object) {
    switch (style) {
      case 'lowercase': return '-.inf';
      case 'uppercase': return '-.INF';
      case 'camelcase': return '-.Inf';
    }
  } else if (common.isNegativeZero(object)) {
    return '-0.0';
  }

  res = object.toString(10);

  // JS stringifier can build scientific format without dots: 5e-100,
  // while YAML requres dot: 5.e-100. Fix it with simple hack

  return SCIENTIFIC_WITHOUT_DOT.test(res) ? res.replace('e', '.e') : res;
}

function isFloat(object) {
  return (Object.prototype.toString.call(object) === '[object Number]') &&
         (object % 1 !== 0 || common.isNegativeZero(object));
}

var float_1 = new type('tag:yaml.org,2002:float', {
  kind: 'scalar',
  resolve: resolveYamlFloat,
  construct: constructYamlFloat,
  predicate: isFloat,
  represent: representYamlFloat,
  defaultStyle: 'lowercase'
});

var json = new schema({
  include: [
    failsafe
  ],
  implicit: [
    _null,
    bool,
    int_1,
    float_1
  ]
});

var core = new schema({
  include: [
    json
  ]
});

var YAML_DATE_REGEXP = new RegExp(
  '^([0-9][0-9][0-9][0-9])'          + // [1] year
  '-([0-9][0-9])'                    + // [2] month
  '-([0-9][0-9])$');                   // [3] day

var YAML_TIMESTAMP_REGEXP = new RegExp(
  '^([0-9][0-9][0-9][0-9])'          + // [1] year
  '-([0-9][0-9]?)'                   + // [2] month
  '-([0-9][0-9]?)'                   + // [3] day
  '(?:[Tt]|[ \\t]+)'                 + // ...
  '([0-9][0-9]?)'                    + // [4] hour
  ':([0-9][0-9])'                    + // [5] minute
  ':([0-9][0-9])'                    + // [6] second
  '(?:\\.([0-9]*))?'                 + // [7] fraction
  '(?:[ \\t]*(Z|([-+])([0-9][0-9]?)' + // [8] tz [9] tz_sign [10] tz_hour
  '(?::([0-9][0-9]))?))?$');           // [11] tz_minute

function resolveYamlTimestamp(data) {
  if (data === null) return false;
  if (YAML_DATE_REGEXP.exec(data) !== null) return true;
  if (YAML_TIMESTAMP_REGEXP.exec(data) !== null) return true;
  return false;
}

function constructYamlTimestamp(data) {
  var match, year, month, day, hour, minute, second, fraction = 0,
      delta = null, tz_hour, tz_minute, date;

  match = YAML_DATE_REGEXP.exec(data);
  if (match === null) match = YAML_TIMESTAMP_REGEXP.exec(data);

  if (match === null) throw new Error('Date resolve error');

  // match: [1] year [2] month [3] day

  year = +(match[1]);
  month = +(match[2]) - 1; // JS month starts with 0
  day = +(match[3]);

  if (!match[4]) { // no hour
    return new Date(Date.UTC(year, month, day));
  }

  // match: [4] hour [5] minute [6] second [7] fraction

  hour = +(match[4]);
  minute = +(match[5]);
  second = +(match[6]);

  if (match[7]) {
    fraction = match[7].slice(0, 3);
    while (fraction.length < 3) { // milli-seconds
      fraction += '0';
    }
    fraction = +fraction;
  }

  // match: [8] tz [9] tz_sign [10] tz_hour [11] tz_minute

  if (match[9]) {
    tz_hour = +(match[10]);
    tz_minute = +(match[11] || 0);
    delta = (tz_hour * 60 + tz_minute) * 60000; // delta in mili-seconds
    if (match[9] === '-') delta = -delta;
  }

  date = new Date(Date.UTC(year, month, day, hour, minute, second, fraction));

  if (delta) date.setTime(date.getTime() - delta);

  return date;
}

function representYamlTimestamp(object /*, style*/) {
  return object.toISOString();
}

var timestamp = new type('tag:yaml.org,2002:timestamp', {
  kind: 'scalar',
  resolve: resolveYamlTimestamp,
  construct: constructYamlTimestamp,
  instanceOf: Date,
  represent: representYamlTimestamp
});

function resolveYamlMerge(data) {
  return data === '<<' || data === null;
}

var merge = new type('tag:yaml.org,2002:merge', {
  kind: 'scalar',
  resolve: resolveYamlMerge
});

function commonjsRequire () {
	throw new Error('Dynamic requires are not currently supported by rollup-plugin-commonjs');
}

/*eslint-disable no-bitwise*/

var NodeBuffer;

try {
  // A trick for browserified version, to not include `Buffer` shim
  var _require = commonjsRequire;
  NodeBuffer = _require('buffer').Buffer;
} catch (__) {}




// [ 64, 65, 66 ] -> [ padding, CR, LF ]
var BASE64_MAP = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=\n\r';


function resolveYamlBinary(data) {
  if (data === null) return false;

  var code, idx, bitlen = 0, max = data.length, map = BASE64_MAP;

  // Convert one by one.
  for (idx = 0; idx < max; idx++) {
    code = map.indexOf(data.charAt(idx));

    // Skip CR/LF
    if (code > 64) continue;

    // Fail on illegal characters
    if (code < 0) return false;

    bitlen += 6;
  }

  // If there are any bits left, source was corrupted
  return (bitlen % 8) === 0;
}

function constructYamlBinary(data) {
  var idx, tailbits,
      input = data.replace(/[\r\n=]/g, ''), // remove CR/LF & padding to simplify scan
      max = input.length,
      map = BASE64_MAP,
      bits = 0,
      result = [];

  // Collect by 6*4 bits (3 bytes)

  for (idx = 0; idx < max; idx++) {
    if ((idx % 4 === 0) && idx) {
      result.push((bits >> 16) & 0xFF);
      result.push((bits >> 8) & 0xFF);
      result.push(bits & 0xFF);
    }

    bits = (bits << 6) | map.indexOf(input.charAt(idx));
  }

  // Dump tail

  tailbits = (max % 4) * 6;

  if (tailbits === 0) {
    result.push((bits >> 16) & 0xFF);
    result.push((bits >> 8) & 0xFF);
    result.push(bits & 0xFF);
  } else if (tailbits === 18) {
    result.push((bits >> 10) & 0xFF);
    result.push((bits >> 2) & 0xFF);
  } else if (tailbits === 12) {
    result.push((bits >> 4) & 0xFF);
  }

  // Wrap into Buffer for NodeJS and leave Array for browser
  if (NodeBuffer) {
    // Support node 6.+ Buffer API when available
    return NodeBuffer.from ? NodeBuffer.from(result) : new NodeBuffer(result);
  }

  return result;
}

function representYamlBinary(object /*, style*/) {
  var result = '', bits = 0, idx, tail,
      max = object.length,
      map = BASE64_MAP;

  // Convert every three bytes to 4 ASCII characters.

  for (idx = 0; idx < max; idx++) {
    if ((idx % 3 === 0) && idx) {
      result += map[(bits >> 18) & 0x3F];
      result += map[(bits >> 12) & 0x3F];
      result += map[(bits >> 6) & 0x3F];
      result += map[bits & 0x3F];
    }

    bits = (bits << 8) + object[idx];
  }

  // Dump tail

  tail = max % 3;

  if (tail === 0) {
    result += map[(bits >> 18) & 0x3F];
    result += map[(bits >> 12) & 0x3F];
    result += map[(bits >> 6) & 0x3F];
    result += map[bits & 0x3F];
  } else if (tail === 2) {
    result += map[(bits >> 10) & 0x3F];
    result += map[(bits >> 4) & 0x3F];
    result += map[(bits << 2) & 0x3F];
    result += map[64];
  } else if (tail === 1) {
    result += map[(bits >> 2) & 0x3F];
    result += map[(bits << 4) & 0x3F];
    result += map[64];
    result += map[64];
  }

  return result;
}

function isBinary(object) {
  return NodeBuffer && NodeBuffer.isBuffer(object);
}

var binary = new type('tag:yaml.org,2002:binary', {
  kind: 'scalar',
  resolve: resolveYamlBinary,
  construct: constructYamlBinary,
  predicate: isBinary,
  represent: representYamlBinary
});

var _hasOwnProperty = Object.prototype.hasOwnProperty;
var _toString       = Object.prototype.toString;

function resolveYamlOmap(data) {
  if (data === null) return true;

  var objectKeys = [], index, length, pair, pairKey, pairHasKey,
      object = data;

  for (index = 0, length = object.length; index < length; index += 1) {
    pair = object[index];
    pairHasKey = false;

    if (_toString.call(pair) !== '[object Object]') return false;

    for (pairKey in pair) {
      if (_hasOwnProperty.call(pair, pairKey)) {
        if (!pairHasKey) pairHasKey = true;
        else return false;
      }
    }

    if (!pairHasKey) return false;

    if (objectKeys.indexOf(pairKey) === -1) objectKeys.push(pairKey);
    else return false;
  }

  return true;
}

function constructYamlOmap(data) {
  return data !== null ? data : [];
}

var omap = new type('tag:yaml.org,2002:omap', {
  kind: 'sequence',
  resolve: resolveYamlOmap,
  construct: constructYamlOmap
});

var _toString$1 = Object.prototype.toString;

function resolveYamlPairs(data) {
  if (data === null) return true;

  var index, length, pair, keys, result,
      object = data;

  result = new Array(object.length);

  for (index = 0, length = object.length; index < length; index += 1) {
    pair = object[index];

    if (_toString$1.call(pair) !== '[object Object]') return false;

    keys = Object.keys(pair);

    if (keys.length !== 1) return false;

    result[index] = [ keys[0], pair[keys[0]] ];
  }

  return true;
}

function constructYamlPairs(data) {
  if (data === null) return [];

  var index, length, pair, keys, result,
      object = data;

  result = new Array(object.length);

  for (index = 0, length = object.length; index < length; index += 1) {
    pair = object[index];

    keys = Object.keys(pair);

    result[index] = [ keys[0], pair[keys[0]] ];
  }

  return result;
}

var pairs = new type('tag:yaml.org,2002:pairs', {
  kind: 'sequence',
  resolve: resolveYamlPairs,
  construct: constructYamlPairs
});

var _hasOwnProperty$1 = Object.prototype.hasOwnProperty;

function resolveYamlSet(data) {
  if (data === null) return true;

  var key, object = data;

  for (key in object) {
    if (_hasOwnProperty$1.call(object, key)) {
      if (object[key] !== null) return false;
    }
  }

  return true;
}

function constructYamlSet(data) {
  return data !== null ? data : {};
}

var set = new type('tag:yaml.org,2002:set', {
  kind: 'mapping',
  resolve: resolveYamlSet,
  construct: constructYamlSet
});

var default_safe = new schema({
  include: [
    core
  ],
  implicit: [
    timestamp,
    merge
  ],
  explicit: [
    binary,
    omap,
    pairs,
    set
  ]
});

function resolveJavascriptUndefined() {
  return true;
}

function constructJavascriptUndefined() {
  /*eslint-disable no-undefined*/
  return undefined;
}

function representJavascriptUndefined() {
  return '';
}

function isUndefined(object) {
  return typeof object === 'undefined';
}

var _undefined = new type('tag:yaml.org,2002:js/undefined', {
  kind: 'scalar',
  resolve: resolveJavascriptUndefined,
  construct: constructJavascriptUndefined,
  predicate: isUndefined,
  represent: representJavascriptUndefined
});

function resolveJavascriptRegExp(data) {
  if (data === null) return false;
  if (data.length === 0) return false;

  var regexp = data,
      tail   = /\/([gim]*)$/.exec(data),
      modifiers = '';

  // if regexp starts with '/' it can have modifiers and must be properly closed
  // `/foo/gim` - modifiers tail can be maximum 3 chars
  if (regexp[0] === '/') {
    if (tail) modifiers = tail[1];

    if (modifiers.length > 3) return false;
    // if expression starts with /, is should be properly terminated
    if (regexp[regexp.length - modifiers.length - 1] !== '/') return false;
  }

  return true;
}

function constructJavascriptRegExp(data) {
  var regexp = data,
      tail   = /\/([gim]*)$/.exec(data),
      modifiers = '';

  // `/foo/gim` - tail can be maximum 4 chars
  if (regexp[0] === '/') {
    if (tail) modifiers = tail[1];
    regexp = regexp.slice(1, regexp.length - modifiers.length - 1);
  }

  return new RegExp(regexp, modifiers);
}

function representJavascriptRegExp(object /*, style*/) {
  var result = '/' + object.source + '/';

  if (object.global) result += 'g';
  if (object.multiline) result += 'm';
  if (object.ignoreCase) result += 'i';

  return result;
}

function isRegExp(object) {
  return Object.prototype.toString.call(object) === '[object RegExp]';
}

var regexp = new type('tag:yaml.org,2002:js/regexp', {
  kind: 'scalar',
  resolve: resolveJavascriptRegExp,
  construct: constructJavascriptRegExp,
  predicate: isRegExp,
  represent: representJavascriptRegExp
});

var esprima;

// Browserified version does not have esprima
//
// 1. For node.js just require module as deps
// 2. For browser try to require mudule via external AMD system.
//    If not found - try to fallback to window.esprima. If not
//    found too - then fail to parse.
//
try {
  // workaround to exclude package from browserify list.
  var _require$1 = commonjsRequire;
  esprima = _require$1('esprima');
} catch (_) {
  /* eslint-disable no-redeclare */
  /* global window */
  if (typeof window !== 'undefined') esprima = window.esprima;
}



function resolveJavascriptFunction(data) {
  if (data === null) return false;

  try {
    var source = '(' + data + ')',
        ast    = esprima.parse(source, { range: true });

    if (ast.type                    !== 'Program'             ||
        ast.body.length             !== 1                     ||
        ast.body[0].type            !== 'ExpressionStatement' ||
        (ast.body[0].expression.type !== 'ArrowFunctionExpression' &&
          ast.body[0].expression.type !== 'FunctionExpression')) {
      return false;
    }

    return true;
  } catch (err) {
    return false;
  }
}

function constructJavascriptFunction(data) {
  /*jslint evil:true*/

  var source = '(' + data + ')',
      ast    = esprima.parse(source, { range: true }),
      params = [],
      body;

  if (ast.type                    !== 'Program'             ||
      ast.body.length             !== 1                     ||
      ast.body[0].type            !== 'ExpressionStatement' ||
      (ast.body[0].expression.type !== 'ArrowFunctionExpression' &&
        ast.body[0].expression.type !== 'FunctionExpression')) {
    throw new Error('Failed to resolve function');
  }

  ast.body[0].expression.params.forEach(function (param) {
    params.push(param.name);
  });

  body = ast.body[0].expression.body.range;

  // Esprima's ranges include the first '{' and the last '}' characters on
  // function expressions. So cut them out.
  if (ast.body[0].expression.body.type === 'BlockStatement') {
    /*eslint-disable no-new-func*/
    return new Function(params, source.slice(body[0] + 1, body[1] - 1));
  }
  // ES6 arrow functions can omit the BlockStatement. In that case, just return
  // the body.
  /*eslint-disable no-new-func*/
  return new Function(params, 'return ' + source.slice(body[0], body[1]));
}

function representJavascriptFunction(object /*, style*/) {
  return object.toString();
}

function isFunction(object) {
  return Object.prototype.toString.call(object) === '[object Function]';
}

var _function = new type('tag:yaml.org,2002:js/function', {
  kind: 'scalar',
  resolve: resolveJavascriptFunction,
  construct: constructJavascriptFunction,
  predicate: isFunction,
  represent: representJavascriptFunction
});

var default_full = schema.DEFAULT = new schema({
  include: [
    default_safe
  ],
  explicit: [
    _undefined,
    regexp,
    _function
  ]
});

/*eslint-disable max-len,no-use-before-define*/








var _hasOwnProperty$2 = Object.prototype.hasOwnProperty;


var CONTEXT_FLOW_IN   = 1;
var CONTEXT_FLOW_OUT  = 2;
var CONTEXT_BLOCK_IN  = 3;
var CONTEXT_BLOCK_OUT = 4;


var CHOMPING_CLIP  = 1;
var CHOMPING_STRIP = 2;
var CHOMPING_KEEP  = 3;


var PATTERN_NON_PRINTABLE         = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x84\x86-\x9F\uFFFE\uFFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF]/;
var PATTERN_NON_ASCII_LINE_BREAKS = /[\x85\u2028\u2029]/;
var PATTERN_FLOW_INDICATORS       = /[,\[\]\{\}]/;
var PATTERN_TAG_HANDLE            = /^(?:!|!!|![a-z\-]+!)$/i;
var PATTERN_TAG_URI               = /^(?:!|[^,\[\]\{\}])(?:%[0-9a-f]{2}|[0-9a-z\-#;\/\?:@&=\+\$,_\.!~\*'\(\)\[\]])*$/i;


function _class(obj) { return Object.prototype.toString.call(obj); }

function is_EOL(c) {
  return (c === 0x0A/* LF */) || (c === 0x0D/* CR */);
}

function is_WHITE_SPACE(c) {
  return (c === 0x09/* Tab */) || (c === 0x20/* Space */);
}

function is_WS_OR_EOL(c) {
  return (c === 0x09/* Tab */) ||
         (c === 0x20/* Space */) ||
         (c === 0x0A/* LF */) ||
         (c === 0x0D/* CR */);
}

function is_FLOW_INDICATOR(c) {
  return c === 0x2C/* , */ ||
         c === 0x5B/* [ */ ||
         c === 0x5D/* ] */ ||
         c === 0x7B/* { */ ||
         c === 0x7D/* } */;
}

function fromHexCode(c) {
  var lc;

  if ((0x30/* 0 */ <= c) && (c <= 0x39/* 9 */)) {
    return c - 0x30;
  }

  /*eslint-disable no-bitwise*/
  lc = c | 0x20;

  if ((0x61/* a */ <= lc) && (lc <= 0x66/* f */)) {
    return lc - 0x61 + 10;
  }

  return -1;
}

function escapedHexLen(c) {
  if (c === 0x78/* x */) { return 2; }
  if (c === 0x75/* u */) { return 4; }
  if (c === 0x55/* U */) { return 8; }
  return 0;
}

function fromDecimalCode(c) {
  if ((0x30/* 0 */ <= c) && (c <= 0x39/* 9 */)) {
    return c - 0x30;
  }

  return -1;
}

function simpleEscapeSequence(c) {
  /* eslint-disable indent */
  return (c === 0x30/* 0 */) ? '\x00' :
        (c === 0x61/* a */) ? '\x07' :
        (c === 0x62/* b */) ? '\x08' :
        (c === 0x74/* t */) ? '\x09' :
        (c === 0x09/* Tab */) ? '\x09' :
        (c === 0x6E/* n */) ? '\x0A' :
        (c === 0x76/* v */) ? '\x0B' :
        (c === 0x66/* f */) ? '\x0C' :
        (c === 0x72/* r */) ? '\x0D' :
        (c === 0x65/* e */) ? '\x1B' :
        (c === 0x20/* Space */) ? ' ' :
        (c === 0x22/* " */) ? '\x22' :
        (c === 0x2F/* / */) ? '/' :
        (c === 0x5C/* \ */) ? '\x5C' :
        (c === 0x4E/* N */) ? '\x85' :
        (c === 0x5F/* _ */) ? '\xA0' :
        (c === 0x4C/* L */) ? '\u2028' :
        (c === 0x50/* P */) ? '\u2029' : '';
}

function charFromCodepoint(c) {
  if (c <= 0xFFFF) {
    return String.fromCharCode(c);
  }
  // Encode UTF-16 surrogate pair
  // https://en.wikipedia.org/wiki/UTF-16#Code_points_U.2B010000_to_U.2B10FFFF
  return String.fromCharCode(
    ((c - 0x010000) >> 10) + 0xD800,
    ((c - 0x010000) & 0x03FF) + 0xDC00
  );
}

var simpleEscapeCheck = new Array(256); // integer, for fast access
var simpleEscapeMap = new Array(256);
for (var i = 0; i < 256; i++) {
  simpleEscapeCheck[i] = simpleEscapeSequence(i) ? 1 : 0;
  simpleEscapeMap[i] = simpleEscapeSequence(i);
}


function State(input, options) {
  this.input = input;

  this.filename  = options['filename']  || null;
  this.schema    = options['schema']    || default_full;
  this.onWarning = options['onWarning'] || null;
  this.legacy    = options['legacy']    || false;
  this.json      = options['json']      || false;
  this.listener  = options['listener']  || null;

  this.implicitTypes = this.schema.compiledImplicit;
  this.typeMap       = this.schema.compiledTypeMap;

  this.length     = input.length;
  this.position   = 0;
  this.line       = 0;
  this.lineStart  = 0;
  this.lineIndent = 0;

  this.documents = [];

  /*
  this.version;
  this.checkLineBreaks;
  this.tagMap;
  this.anchorMap;
  this.tag;
  this.anchor;
  this.kind;
  this.result;*/

}


function generateError(state, message) {
  return new exception(
    message,
    new mark(state.filename, state.input, state.position, state.line, (state.position - state.lineStart)));
}

function throwError(state, message) {
  throw generateError(state, message);
}

function throwWarning(state, message) {
  if (state.onWarning) {
    state.onWarning.call(null, generateError(state, message));
  }
}


var directiveHandlers = {

  YAML: function handleYamlDirective(state, name, args) {

    var match, major, minor;

    if (state.version !== null) {
      throwError(state, 'duplication of %YAML directive');
    }

    if (args.length !== 1) {
      throwError(state, 'YAML directive accepts exactly one argument');
    }

    match = /^([0-9]+)\.([0-9]+)$/.exec(args[0]);

    if (match === null) {
      throwError(state, 'ill-formed argument of the YAML directive');
    }

    major = parseInt(match[1], 10);
    minor = parseInt(match[2], 10);

    if (major !== 1) {
      throwError(state, 'unacceptable YAML version of the document');
    }

    state.version = args[0];
    state.checkLineBreaks = (minor < 2);

    if (minor !== 1 && minor !== 2) {
      throwWarning(state, 'unsupported YAML version of the document');
    }
  },

  TAG: function handleTagDirective(state, name, args) {

    var handle, prefix;

    if (args.length !== 2) {
      throwError(state, 'TAG directive accepts exactly two arguments');
    }

    handle = args[0];
    prefix = args[1];

    if (!PATTERN_TAG_HANDLE.test(handle)) {
      throwError(state, 'ill-formed tag handle (first argument) of the TAG directive');
    }

    if (_hasOwnProperty$2.call(state.tagMap, handle)) {
      throwError(state, 'there is a previously declared suffix for "' + handle + '" tag handle');
    }

    if (!PATTERN_TAG_URI.test(prefix)) {
      throwError(state, 'ill-formed tag prefix (second argument) of the TAG directive');
    }

    state.tagMap[handle] = prefix;
  }
};


function captureSegment(state, start, end, checkJson) {
  var _position, _length, _character, _result;

  if (start < end) {
    _result = state.input.slice(start, end);

    if (checkJson) {
      for (_position = 0, _length = _result.length; _position < _length; _position += 1) {
        _character = _result.charCodeAt(_position);
        if (!(_character === 0x09 ||
              (0x20 <= _character && _character <= 0x10FFFF))) {
          throwError(state, 'expected valid JSON character');
        }
      }
    } else if (PATTERN_NON_PRINTABLE.test(_result)) {
      throwError(state, 'the stream contains non-printable characters');
    }

    state.result += _result;
  }
}

function mergeMappings(state, destination, source, overridableKeys) {
  var sourceKeys, key, index, quantity;

  if (!common.isObject(source)) {
    throwError(state, 'cannot merge mappings; the provided source object is unacceptable');
  }

  sourceKeys = Object.keys(source);

  for (index = 0, quantity = sourceKeys.length; index < quantity; index += 1) {
    key = sourceKeys[index];

    if (!_hasOwnProperty$2.call(destination, key)) {
      destination[key] = source[key];
      overridableKeys[key] = true;
    }
  }
}

function storeMappingPair(state, _result, overridableKeys, keyTag, keyNode, valueNode, startLine, startPos) {
  var index, quantity;

  // The output is a plain object here, so keys can only be strings.
  // We need to convert keyNode to a string, but doing so can hang the process
  // (deeply nested arrays that explode exponentially using aliases).
  if (Array.isArray(keyNode)) {
    keyNode = Array.prototype.slice.call(keyNode);

    for (index = 0, quantity = keyNode.length; index < quantity; index += 1) {
      if (Array.isArray(keyNode[index])) {
        throwError(state, 'nested arrays are not supported inside keys');
      }

      if (typeof keyNode === 'object' && _class(keyNode[index]) === '[object Object]') {
        keyNode[index] = '[object Object]';
      }
    }
  }

  // Avoid code execution in load() via toString property
  // (still use its own toString for arrays, timestamps,
  // and whatever user schema extensions happen to have @@toStringTag)
  if (typeof keyNode === 'object' && _class(keyNode) === '[object Object]') {
    keyNode = '[object Object]';
  }


  keyNode = String(keyNode);

  if (_result === null) {
    _result = {};
  }

  if (keyTag === 'tag:yaml.org,2002:merge') {
    if (Array.isArray(valueNode)) {
      for (index = 0, quantity = valueNode.length; index < quantity; index += 1) {
        mergeMappings(state, _result, valueNode[index], overridableKeys);
      }
    } else {
      mergeMappings(state, _result, valueNode, overridableKeys);
    }
  } else {
    if (!state.json &&
        !_hasOwnProperty$2.call(overridableKeys, keyNode) &&
        _hasOwnProperty$2.call(_result, keyNode)) {
      state.line = startLine || state.line;
      state.position = startPos || state.position;
      throwError(state, 'duplicated mapping key');
    }
    _result[keyNode] = valueNode;
    delete overridableKeys[keyNode];
  }

  return _result;
}

function readLineBreak(state) {
  var ch;

  ch = state.input.charCodeAt(state.position);

  if (ch === 0x0A/* LF */) {
    state.position++;
  } else if (ch === 0x0D/* CR */) {
    state.position++;
    if (state.input.charCodeAt(state.position) === 0x0A/* LF */) {
      state.position++;
    }
  } else {
    throwError(state, 'a line break is expected');
  }

  state.line += 1;
  state.lineStart = state.position;
}

function skipSeparationSpace(state, allowComments, checkIndent) {
  var lineBreaks = 0,
      ch = state.input.charCodeAt(state.position);

  while (ch !== 0) {
    while (is_WHITE_SPACE(ch)) {
      ch = state.input.charCodeAt(++state.position);
    }

    if (allowComments && ch === 0x23/* # */) {
      do {
        ch = state.input.charCodeAt(++state.position);
      } while (ch !== 0x0A/* LF */ && ch !== 0x0D/* CR */ && ch !== 0);
    }

    if (is_EOL(ch)) {
      readLineBreak(state);

      ch = state.input.charCodeAt(state.position);
      lineBreaks++;
      state.lineIndent = 0;

      while (ch === 0x20/* Space */) {
        state.lineIndent++;
        ch = state.input.charCodeAt(++state.position);
      }
    } else {
      break;
    }
  }

  if (checkIndent !== -1 && lineBreaks !== 0 && state.lineIndent < checkIndent) {
    throwWarning(state, 'deficient indentation');
  }

  return lineBreaks;
}

function testDocumentSeparator(state) {
  var _position = state.position,
      ch;

  ch = state.input.charCodeAt(_position);

  // Condition state.position === state.lineStart is tested
  // in parent on each call, for efficiency. No needs to test here again.
  if ((ch === 0x2D/* - */ || ch === 0x2E/* . */) &&
      ch === state.input.charCodeAt(_position + 1) &&
      ch === state.input.charCodeAt(_position + 2)) {

    _position += 3;

    ch = state.input.charCodeAt(_position);

    if (ch === 0 || is_WS_OR_EOL(ch)) {
      return true;
    }
  }

  return false;
}

function writeFoldedLines(state, count) {
  if (count === 1) {
    state.result += ' ';
  } else if (count > 1) {
    state.result += common.repeat('\n', count - 1);
  }
}


function readPlainScalar(state, nodeIndent, withinFlowCollection) {
  var preceding,
      following,
      captureStart,
      captureEnd,
      hasPendingContent,
      _line,
      _lineStart,
      _lineIndent,
      _kind = state.kind,
      _result = state.result,
      ch;

  ch = state.input.charCodeAt(state.position);

  if (is_WS_OR_EOL(ch)      ||
      is_FLOW_INDICATOR(ch) ||
      ch === 0x23/* # */    ||
      ch === 0x26/* & */    ||
      ch === 0x2A/* * */    ||
      ch === 0x21/* ! */    ||
      ch === 0x7C/* | */    ||
      ch === 0x3E/* > */    ||
      ch === 0x27/* ' */    ||
      ch === 0x22/* " */    ||
      ch === 0x25/* % */    ||
      ch === 0x40/* @ */    ||
      ch === 0x60/* ` */) {
    return false;
  }

  if (ch === 0x3F/* ? */ || ch === 0x2D/* - */) {
    following = state.input.charCodeAt(state.position + 1);

    if (is_WS_OR_EOL(following) ||
        withinFlowCollection && is_FLOW_INDICATOR(following)) {
      return false;
    }
  }

  state.kind = 'scalar';
  state.result = '';
  captureStart = captureEnd = state.position;
  hasPendingContent = false;

  while (ch !== 0) {
    if (ch === 0x3A/* : */) {
      following = state.input.charCodeAt(state.position + 1);

      if (is_WS_OR_EOL(following) ||
          withinFlowCollection && is_FLOW_INDICATOR(following)) {
        break;
      }

    } else if (ch === 0x23/* # */) {
      preceding = state.input.charCodeAt(state.position - 1);

      if (is_WS_OR_EOL(preceding)) {
        break;
      }

    } else if ((state.position === state.lineStart && testDocumentSeparator(state)) ||
               withinFlowCollection && is_FLOW_INDICATOR(ch)) {
      break;

    } else if (is_EOL(ch)) {
      _line = state.line;
      _lineStart = state.lineStart;
      _lineIndent = state.lineIndent;
      skipSeparationSpace(state, false, -1);

      if (state.lineIndent >= nodeIndent) {
        hasPendingContent = true;
        ch = state.input.charCodeAt(state.position);
        continue;
      } else {
        state.position = captureEnd;
        state.line = _line;
        state.lineStart = _lineStart;
        state.lineIndent = _lineIndent;
        break;
      }
    }

    if (hasPendingContent) {
      captureSegment(state, captureStart, captureEnd, false);
      writeFoldedLines(state, state.line - _line);
      captureStart = captureEnd = state.position;
      hasPendingContent = false;
    }

    if (!is_WHITE_SPACE(ch)) {
      captureEnd = state.position + 1;
    }

    ch = state.input.charCodeAt(++state.position);
  }

  captureSegment(state, captureStart, captureEnd, false);

  if (state.result) {
    return true;
  }

  state.kind = _kind;
  state.result = _result;
  return false;
}

function readSingleQuotedScalar(state, nodeIndent) {
  var ch,
      captureStart, captureEnd;

  ch = state.input.charCodeAt(state.position);

  if (ch !== 0x27/* ' */) {
    return false;
  }

  state.kind = 'scalar';
  state.result = '';
  state.position++;
  captureStart = captureEnd = state.position;

  while ((ch = state.input.charCodeAt(state.position)) !== 0) {
    if (ch === 0x27/* ' */) {
      captureSegment(state, captureStart, state.position, true);
      ch = state.input.charCodeAt(++state.position);

      if (ch === 0x27/* ' */) {
        captureStart = state.position;
        state.position++;
        captureEnd = state.position;
      } else {
        return true;
      }

    } else if (is_EOL(ch)) {
      captureSegment(state, captureStart, captureEnd, true);
      writeFoldedLines(state, skipSeparationSpace(state, false, nodeIndent));
      captureStart = captureEnd = state.position;

    } else if (state.position === state.lineStart && testDocumentSeparator(state)) {
      throwError(state, 'unexpected end of the document within a single quoted scalar');

    } else {
      state.position++;
      captureEnd = state.position;
    }
  }

  throwError(state, 'unexpected end of the stream within a single quoted scalar');
}

function readDoubleQuotedScalar(state, nodeIndent) {
  var captureStart,
      captureEnd,
      hexLength,
      hexResult,
      tmp,
      ch;

  ch = state.input.charCodeAt(state.position);

  if (ch !== 0x22/* " */) {
    return false;
  }

  state.kind = 'scalar';
  state.result = '';
  state.position++;
  captureStart = captureEnd = state.position;

  while ((ch = state.input.charCodeAt(state.position)) !== 0) {
    if (ch === 0x22/* " */) {
      captureSegment(state, captureStart, state.position, true);
      state.position++;
      return true;

    } else if (ch === 0x5C/* \ */) {
      captureSegment(state, captureStart, state.position, true);
      ch = state.input.charCodeAt(++state.position);

      if (is_EOL(ch)) {
        skipSeparationSpace(state, false, nodeIndent);

        // TODO: rework to inline fn with no type cast?
      } else if (ch < 256 && simpleEscapeCheck[ch]) {
        state.result += simpleEscapeMap[ch];
        state.position++;

      } else if ((tmp = escapedHexLen(ch)) > 0) {
        hexLength = tmp;
        hexResult = 0;

        for (; hexLength > 0; hexLength--) {
          ch = state.input.charCodeAt(++state.position);

          if ((tmp = fromHexCode(ch)) >= 0) {
            hexResult = (hexResult << 4) + tmp;

          } else {
            throwError(state, 'expected hexadecimal character');
          }
        }

        state.result += charFromCodepoint(hexResult);

        state.position++;

      } else {
        throwError(state, 'unknown escape sequence');
      }

      captureStart = captureEnd = state.position;

    } else if (is_EOL(ch)) {
      captureSegment(state, captureStart, captureEnd, true);
      writeFoldedLines(state, skipSeparationSpace(state, false, nodeIndent));
      captureStart = captureEnd = state.position;

    } else if (state.position === state.lineStart && testDocumentSeparator(state)) {
      throwError(state, 'unexpected end of the document within a double quoted scalar');

    } else {
      state.position++;
      captureEnd = state.position;
    }
  }

  throwError(state, 'unexpected end of the stream within a double quoted scalar');
}

function readFlowCollection(state, nodeIndent) {
  var readNext = true,
      _line,
      _tag     = state.tag,
      _result,
      _anchor  = state.anchor,
      following,
      terminator,
      isPair,
      isExplicitPair,
      isMapping,
      overridableKeys = {},
      keyNode,
      keyTag,
      valueNode,
      ch;

  ch = state.input.charCodeAt(state.position);

  if (ch === 0x5B/* [ */) {
    terminator = 0x5D;/* ] */
    isMapping = false;
    _result = [];
  } else if (ch === 0x7B/* { */) {
    terminator = 0x7D;/* } */
    isMapping = true;
    _result = {};
  } else {
    return false;
  }

  if (state.anchor !== null) {
    state.anchorMap[state.anchor] = _result;
  }

  ch = state.input.charCodeAt(++state.position);

  while (ch !== 0) {
    skipSeparationSpace(state, true, nodeIndent);

    ch = state.input.charCodeAt(state.position);

    if (ch === terminator) {
      state.position++;
      state.tag = _tag;
      state.anchor = _anchor;
      state.kind = isMapping ? 'mapping' : 'sequence';
      state.result = _result;
      return true;
    } else if (!readNext) {
      throwError(state, 'missed comma between flow collection entries');
    }

    keyTag = keyNode = valueNode = null;
    isPair = isExplicitPair = false;

    if (ch === 0x3F/* ? */) {
      following = state.input.charCodeAt(state.position + 1);

      if (is_WS_OR_EOL(following)) {
        isPair = isExplicitPair = true;
        state.position++;
        skipSeparationSpace(state, true, nodeIndent);
      }
    }

    _line = state.line;
    composeNode(state, nodeIndent, CONTEXT_FLOW_IN, false, true);
    keyTag = state.tag;
    keyNode = state.result;
    skipSeparationSpace(state, true, nodeIndent);

    ch = state.input.charCodeAt(state.position);

    if ((isExplicitPair || state.line === _line) && ch === 0x3A/* : */) {
      isPair = true;
      ch = state.input.charCodeAt(++state.position);
      skipSeparationSpace(state, true, nodeIndent);
      composeNode(state, nodeIndent, CONTEXT_FLOW_IN, false, true);
      valueNode = state.result;
    }

    if (isMapping) {
      storeMappingPair(state, _result, overridableKeys, keyTag, keyNode, valueNode);
    } else if (isPair) {
      _result.push(storeMappingPair(state, null, overridableKeys, keyTag, keyNode, valueNode));
    } else {
      _result.push(keyNode);
    }

    skipSeparationSpace(state, true, nodeIndent);

    ch = state.input.charCodeAt(state.position);

    if (ch === 0x2C/* , */) {
      readNext = true;
      ch = state.input.charCodeAt(++state.position);
    } else {
      readNext = false;
    }
  }

  throwError(state, 'unexpected end of the stream within a flow collection');
}

function readBlockScalar(state, nodeIndent) {
  var captureStart,
      folding,
      chomping       = CHOMPING_CLIP,
      didReadContent = false,
      detectedIndent = false,
      textIndent     = nodeIndent,
      emptyLines     = 0,
      atMoreIndented = false,
      tmp,
      ch;

  ch = state.input.charCodeAt(state.position);

  if (ch === 0x7C/* | */) {
    folding = false;
  } else if (ch === 0x3E/* > */) {
    folding = true;
  } else {
    return false;
  }

  state.kind = 'scalar';
  state.result = '';

  while (ch !== 0) {
    ch = state.input.charCodeAt(++state.position);

    if (ch === 0x2B/* + */ || ch === 0x2D/* - */) {
      if (CHOMPING_CLIP === chomping) {
        chomping = (ch === 0x2B/* + */) ? CHOMPING_KEEP : CHOMPING_STRIP;
      } else {
        throwError(state, 'repeat of a chomping mode identifier');
      }

    } else if ((tmp = fromDecimalCode(ch)) >= 0) {
      if (tmp === 0) {
        throwError(state, 'bad explicit indentation width of a block scalar; it cannot be less than one');
      } else if (!detectedIndent) {
        textIndent = nodeIndent + tmp - 1;
        detectedIndent = true;
      } else {
        throwError(state, 'repeat of an indentation width identifier');
      }

    } else {
      break;
    }
  }

  if (is_WHITE_SPACE(ch)) {
    do { ch = state.input.charCodeAt(++state.position); }
    while (is_WHITE_SPACE(ch));

    if (ch === 0x23/* # */) {
      do { ch = state.input.charCodeAt(++state.position); }
      while (!is_EOL(ch) && (ch !== 0));
    }
  }

  while (ch !== 0) {
    readLineBreak(state);
    state.lineIndent = 0;

    ch = state.input.charCodeAt(state.position);

    while ((!detectedIndent || state.lineIndent < textIndent) &&
           (ch === 0x20/* Space */)) {
      state.lineIndent++;
      ch = state.input.charCodeAt(++state.position);
    }

    if (!detectedIndent && state.lineIndent > textIndent) {
      textIndent = state.lineIndent;
    }

    if (is_EOL(ch)) {
      emptyLines++;
      continue;
    }

    // End of the scalar.
    if (state.lineIndent < textIndent) {

      // Perform the chomping.
      if (chomping === CHOMPING_KEEP) {
        state.result += common.repeat('\n', didReadContent ? 1 + emptyLines : emptyLines);
      } else if (chomping === CHOMPING_CLIP) {
        if (didReadContent) { // i.e. only if the scalar is not empty.
          state.result += '\n';
        }
      }

      // Break this `while` cycle and go to the funciton's epilogue.
      break;
    }

    // Folded style: use fancy rules to handle line breaks.
    if (folding) {

      // Lines starting with white space characters (more-indented lines) are not folded.
      if (is_WHITE_SPACE(ch)) {
        atMoreIndented = true;
        // except for the first content line (cf. Example 8.1)
        state.result += common.repeat('\n', didReadContent ? 1 + emptyLines : emptyLines);

      // End of more-indented block.
      } else if (atMoreIndented) {
        atMoreIndented = false;
        state.result += common.repeat('\n', emptyLines + 1);

      // Just one line break - perceive as the same line.
      } else if (emptyLines === 0) {
        if (didReadContent) { // i.e. only if we have already read some scalar content.
          state.result += ' ';
        }

      // Several line breaks - perceive as different lines.
      } else {
        state.result += common.repeat('\n', emptyLines);
      }

    // Literal style: just add exact number of line breaks between content lines.
    } else {
      // Keep all line breaks except the header line break.
      state.result += common.repeat('\n', didReadContent ? 1 + emptyLines : emptyLines);
    }

    didReadContent = true;
    detectedIndent = true;
    emptyLines = 0;
    captureStart = state.position;

    while (!is_EOL(ch) && (ch !== 0)) {
      ch = state.input.charCodeAt(++state.position);
    }

    captureSegment(state, captureStart, state.position, false);
  }

  return true;
}

function readBlockSequence(state, nodeIndent) {
  var _line,
      _tag      = state.tag,
      _anchor   = state.anchor,
      _result   = [],
      following,
      detected  = false,
      ch;

  if (state.anchor !== null) {
    state.anchorMap[state.anchor] = _result;
  }

  ch = state.input.charCodeAt(state.position);

  while (ch !== 0) {

    if (ch !== 0x2D/* - */) {
      break;
    }

    following = state.input.charCodeAt(state.position + 1);

    if (!is_WS_OR_EOL(following)) {
      break;
    }

    detected = true;
    state.position++;

    if (skipSeparationSpace(state, true, -1)) {
      if (state.lineIndent <= nodeIndent) {
        _result.push(null);
        ch = state.input.charCodeAt(state.position);
        continue;
      }
    }

    _line = state.line;
    composeNode(state, nodeIndent, CONTEXT_BLOCK_IN, false, true);
    _result.push(state.result);
    skipSeparationSpace(state, true, -1);

    ch = state.input.charCodeAt(state.position);

    if ((state.line === _line || state.lineIndent > nodeIndent) && (ch !== 0)) {
      throwError(state, 'bad indentation of a sequence entry');
    } else if (state.lineIndent < nodeIndent) {
      break;
    }
  }

  if (detected) {
    state.tag = _tag;
    state.anchor = _anchor;
    state.kind = 'sequence';
    state.result = _result;
    return true;
  }
  return false;
}

function readBlockMapping(state, nodeIndent, flowIndent) {
  var following,
      allowCompact,
      _line,
      _pos,
      _tag          = state.tag,
      _anchor       = state.anchor,
      _result       = {},
      overridableKeys = {},
      keyTag        = null,
      keyNode       = null,
      valueNode     = null,
      atExplicitKey = false,
      detected      = false,
      ch;

  if (state.anchor !== null) {
    state.anchorMap[state.anchor] = _result;
  }

  ch = state.input.charCodeAt(state.position);

  while (ch !== 0) {
    following = state.input.charCodeAt(state.position + 1);
    _line = state.line; // Save the current line.
    _pos = state.position;

    //
    // Explicit notation case. There are two separate blocks:
    // first for the key (denoted by "?") and second for the value (denoted by ":")
    //
    if ((ch === 0x3F/* ? */ || ch === 0x3A/* : */) && is_WS_OR_EOL(following)) {

      if (ch === 0x3F/* ? */) {
        if (atExplicitKey) {
          storeMappingPair(state, _result, overridableKeys, keyTag, keyNode, null);
          keyTag = keyNode = valueNode = null;
        }

        detected = true;
        atExplicitKey = true;
        allowCompact = true;

      } else if (atExplicitKey) {
        // i.e. 0x3A/* : */ === character after the explicit key.
        atExplicitKey = false;
        allowCompact = true;

      } else {
        throwError(state, 'incomplete explicit mapping pair; a key node is missed; or followed by a non-tabulated empty line');
      }

      state.position += 1;
      ch = following;

    //
    // Implicit notation case. Flow-style node as the key first, then ":", and the value.
    //
    } else if (composeNode(state, flowIndent, CONTEXT_FLOW_OUT, false, true)) {

      if (state.line === _line) {
        ch = state.input.charCodeAt(state.position);

        while (is_WHITE_SPACE(ch)) {
          ch = state.input.charCodeAt(++state.position);
        }

        if (ch === 0x3A/* : */) {
          ch = state.input.charCodeAt(++state.position);

          if (!is_WS_OR_EOL(ch)) {
            throwError(state, 'a whitespace character is expected after the key-value separator within a block mapping');
          }

          if (atExplicitKey) {
            storeMappingPair(state, _result, overridableKeys, keyTag, keyNode, null);
            keyTag = keyNode = valueNode = null;
          }

          detected = true;
          atExplicitKey = false;
          allowCompact = false;
          keyTag = state.tag;
          keyNode = state.result;

        } else if (detected) {
          throwError(state, 'can not read an implicit mapping pair; a colon is missed');

        } else {
          state.tag = _tag;
          state.anchor = _anchor;
          return true; // Keep the result of `composeNode`.
        }

      } else if (detected) {
        throwError(state, 'can not read a block mapping entry; a multiline key may not be an implicit key');

      } else {
        state.tag = _tag;
        state.anchor = _anchor;
        return true; // Keep the result of `composeNode`.
      }

    } else {
      break; // Reading is done. Go to the epilogue.
    }

    //
    // Common reading code for both explicit and implicit notations.
    //
    if (state.line === _line || state.lineIndent > nodeIndent) {
      if (composeNode(state, nodeIndent, CONTEXT_BLOCK_OUT, true, allowCompact)) {
        if (atExplicitKey) {
          keyNode = state.result;
        } else {
          valueNode = state.result;
        }
      }

      if (!atExplicitKey) {
        storeMappingPair(state, _result, overridableKeys, keyTag, keyNode, valueNode, _line, _pos);
        keyTag = keyNode = valueNode = null;
      }

      skipSeparationSpace(state, true, -1);
      ch = state.input.charCodeAt(state.position);
    }

    if (state.lineIndent > nodeIndent && (ch !== 0)) {
      throwError(state, 'bad indentation of a mapping entry');
    } else if (state.lineIndent < nodeIndent) {
      break;
    }
  }

  //
  // Epilogue.
  //

  // Special case: last mapping's node contains only the key in explicit notation.
  if (atExplicitKey) {
    storeMappingPair(state, _result, overridableKeys, keyTag, keyNode, null);
  }

  // Expose the resulting mapping.
  if (detected) {
    state.tag = _tag;
    state.anchor = _anchor;
    state.kind = 'mapping';
    state.result = _result;
  }

  return detected;
}

function readTagProperty(state) {
  var _position,
      isVerbatim = false,
      isNamed    = false,
      tagHandle,
      tagName,
      ch;

  ch = state.input.charCodeAt(state.position);

  if (ch !== 0x21/* ! */) return false;

  if (state.tag !== null) {
    throwError(state, 'duplication of a tag property');
  }

  ch = state.input.charCodeAt(++state.position);

  if (ch === 0x3C/* < */) {
    isVerbatim = true;
    ch = state.input.charCodeAt(++state.position);

  } else if (ch === 0x21/* ! */) {
    isNamed = true;
    tagHandle = '!!';
    ch = state.input.charCodeAt(++state.position);

  } else {
    tagHandle = '!';
  }

  _position = state.position;

  if (isVerbatim) {
    do { ch = state.input.charCodeAt(++state.position); }
    while (ch !== 0 && ch !== 0x3E/* > */);

    if (state.position < state.length) {
      tagName = state.input.slice(_position, state.position);
      ch = state.input.charCodeAt(++state.position);
    } else {
      throwError(state, 'unexpected end of the stream within a verbatim tag');
    }
  } else {
    while (ch !== 0 && !is_WS_OR_EOL(ch)) {

      if (ch === 0x21/* ! */) {
        if (!isNamed) {
          tagHandle = state.input.slice(_position - 1, state.position + 1);

          if (!PATTERN_TAG_HANDLE.test(tagHandle)) {
            throwError(state, 'named tag handle cannot contain such characters');
          }

          isNamed = true;
          _position = state.position + 1;
        } else {
          throwError(state, 'tag suffix cannot contain exclamation marks');
        }
      }

      ch = state.input.charCodeAt(++state.position);
    }

    tagName = state.input.slice(_position, state.position);

    if (PATTERN_FLOW_INDICATORS.test(tagName)) {
      throwError(state, 'tag suffix cannot contain flow indicator characters');
    }
  }

  if (tagName && !PATTERN_TAG_URI.test(tagName)) {
    throwError(state, 'tag name cannot contain such characters: ' + tagName);
  }

  if (isVerbatim) {
    state.tag = tagName;

  } else if (_hasOwnProperty$2.call(state.tagMap, tagHandle)) {
    state.tag = state.tagMap[tagHandle] + tagName;

  } else if (tagHandle === '!') {
    state.tag = '!' + tagName;

  } else if (tagHandle === '!!') {
    state.tag = 'tag:yaml.org,2002:' + tagName;

  } else {
    throwError(state, 'undeclared tag handle "' + tagHandle + '"');
  }

  return true;
}

function readAnchorProperty(state) {
  var _position,
      ch;

  ch = state.input.charCodeAt(state.position);

  if (ch !== 0x26/* & */) return false;

  if (state.anchor !== null) {
    throwError(state, 'duplication of an anchor property');
  }

  ch = state.input.charCodeAt(++state.position);
  _position = state.position;

  while (ch !== 0 && !is_WS_OR_EOL(ch) && !is_FLOW_INDICATOR(ch)) {
    ch = state.input.charCodeAt(++state.position);
  }

  if (state.position === _position) {
    throwError(state, 'name of an anchor node must contain at least one character');
  }

  state.anchor = state.input.slice(_position, state.position);
  return true;
}

function readAlias(state) {
  var _position, alias,
      ch;

  ch = state.input.charCodeAt(state.position);

  if (ch !== 0x2A/* * */) return false;

  ch = state.input.charCodeAt(++state.position);
  _position = state.position;

  while (ch !== 0 && !is_WS_OR_EOL(ch) && !is_FLOW_INDICATOR(ch)) {
    ch = state.input.charCodeAt(++state.position);
  }

  if (state.position === _position) {
    throwError(state, 'name of an alias node must contain at least one character');
  }

  alias = state.input.slice(_position, state.position);

  if (!_hasOwnProperty$2.call(state.anchorMap, alias)) {
    throwError(state, 'unidentified alias "' + alias + '"');
  }

  state.result = state.anchorMap[alias];
  skipSeparationSpace(state, true, -1);
  return true;
}

function composeNode(state, parentIndent, nodeContext, allowToSeek, allowCompact) {
  var allowBlockStyles,
      allowBlockScalars,
      allowBlockCollections,
      indentStatus = 1, // 1: this>parent, 0: this=parent, -1: this<parent
      atNewLine  = false,
      hasContent = false,
      typeIndex,
      typeQuantity,
      type,
      flowIndent,
      blockIndent;

  if (state.listener !== null) {
    state.listener('open', state);
  }

  state.tag    = null;
  state.anchor = null;
  state.kind   = null;
  state.result = null;

  allowBlockStyles = allowBlockScalars = allowBlockCollections =
    CONTEXT_BLOCK_OUT === nodeContext ||
    CONTEXT_BLOCK_IN  === nodeContext;

  if (allowToSeek) {
    if (skipSeparationSpace(state, true, -1)) {
      atNewLine = true;

      if (state.lineIndent > parentIndent) {
        indentStatus = 1;
      } else if (state.lineIndent === parentIndent) {
        indentStatus = 0;
      } else if (state.lineIndent < parentIndent) {
        indentStatus = -1;
      }
    }
  }

  if (indentStatus === 1) {
    while (readTagProperty(state) || readAnchorProperty(state)) {
      if (skipSeparationSpace(state, true, -1)) {
        atNewLine = true;
        allowBlockCollections = allowBlockStyles;

        if (state.lineIndent > parentIndent) {
          indentStatus = 1;
        } else if (state.lineIndent === parentIndent) {
          indentStatus = 0;
        } else if (state.lineIndent < parentIndent) {
          indentStatus = -1;
        }
      } else {
        allowBlockCollections = false;
      }
    }
  }

  if (allowBlockCollections) {
    allowBlockCollections = atNewLine || allowCompact;
  }

  if (indentStatus === 1 || CONTEXT_BLOCK_OUT === nodeContext) {
    if (CONTEXT_FLOW_IN === nodeContext || CONTEXT_FLOW_OUT === nodeContext) {
      flowIndent = parentIndent;
    } else {
      flowIndent = parentIndent + 1;
    }

    blockIndent = state.position - state.lineStart;

    if (indentStatus === 1) {
      if (allowBlockCollections &&
          (readBlockSequence(state, blockIndent) ||
           readBlockMapping(state, blockIndent, flowIndent)) ||
          readFlowCollection(state, flowIndent)) {
        hasContent = true;
      } else {
        if ((allowBlockScalars && readBlockScalar(state, flowIndent)) ||
            readSingleQuotedScalar(state, flowIndent) ||
            readDoubleQuotedScalar(state, flowIndent)) {
          hasContent = true;

        } else if (readAlias(state)) {
          hasContent = true;

          if (state.tag !== null || state.anchor !== null) {
            throwError(state, 'alias node should not have any properties');
          }

        } else if (readPlainScalar(state, flowIndent, CONTEXT_FLOW_IN === nodeContext)) {
          hasContent = true;

          if (state.tag === null) {
            state.tag = '?';
          }
        }

        if (state.anchor !== null) {
          state.anchorMap[state.anchor] = state.result;
        }
      }
    } else if (indentStatus === 0) {
      // Special case: block sequences are allowed to have same indentation level as the parent.
      // http://www.yaml.org/spec/1.2/spec.html#id2799784
      hasContent = allowBlockCollections && readBlockSequence(state, blockIndent);
    }
  }

  if (state.tag !== null && state.tag !== '!') {
    if (state.tag === '?') {
      // Implicit resolving is not allowed for non-scalar types, and '?'
      // non-specific tag is only automatically assigned to plain scalars.
      //
      // We only need to check kind conformity in case user explicitly assigns '?'
      // tag, for example like this: "!<?> [0]"
      //
      if (state.result !== null && state.kind !== 'scalar') {
        throwError(state, 'unacceptable node kind for !<?> tag; it should be "scalar", not "' + state.kind + '"');
      }

      for (typeIndex = 0, typeQuantity = state.implicitTypes.length; typeIndex < typeQuantity; typeIndex += 1) {
        type = state.implicitTypes[typeIndex];

        if (type.resolve(state.result)) { // `state.result` updated in resolver if matched
          state.result = type.construct(state.result);
          state.tag = type.tag;
          if (state.anchor !== null) {
            state.anchorMap[state.anchor] = state.result;
          }
          break;
        }
      }
    } else if (_hasOwnProperty$2.call(state.typeMap[state.kind || 'fallback'], state.tag)) {
      type = state.typeMap[state.kind || 'fallback'][state.tag];

      if (state.result !== null && type.kind !== state.kind) {
        throwError(state, 'unacceptable node kind for !<' + state.tag + '> tag; it should be "' + type.kind + '", not "' + state.kind + '"');
      }

      if (!type.resolve(state.result)) { // `state.result` updated in resolver if matched
        throwError(state, 'cannot resolve a node with !<' + state.tag + '> explicit tag');
      } else {
        state.result = type.construct(state.result);
        if (state.anchor !== null) {
          state.anchorMap[state.anchor] = state.result;
        }
      }
    } else {
      throwError(state, 'unknown tag !<' + state.tag + '>');
    }
  }

  if (state.listener !== null) {
    state.listener('close', state);
  }
  return state.tag !== null ||  state.anchor !== null || hasContent;
}

function readDocument(state) {
  var documentStart = state.position,
      _position,
      directiveName,
      directiveArgs,
      hasDirectives = false,
      ch;

  state.version = null;
  state.checkLineBreaks = state.legacy;
  state.tagMap = {};
  state.anchorMap = {};

  while ((ch = state.input.charCodeAt(state.position)) !== 0) {
    skipSeparationSpace(state, true, -1);

    ch = state.input.charCodeAt(state.position);

    if (state.lineIndent > 0 || ch !== 0x25/* % */) {
      break;
    }

    hasDirectives = true;
    ch = state.input.charCodeAt(++state.position);
    _position = state.position;

    while (ch !== 0 && !is_WS_OR_EOL(ch)) {
      ch = state.input.charCodeAt(++state.position);
    }

    directiveName = state.input.slice(_position, state.position);
    directiveArgs = [];

    if (directiveName.length < 1) {
      throwError(state, 'directive name must not be less than one character in length');
    }

    while (ch !== 0) {
      while (is_WHITE_SPACE(ch)) {
        ch = state.input.charCodeAt(++state.position);
      }

      if (ch === 0x23/* # */) {
        do { ch = state.input.charCodeAt(++state.position); }
        while (ch !== 0 && !is_EOL(ch));
        break;
      }

      if (is_EOL(ch)) break;

      _position = state.position;

      while (ch !== 0 && !is_WS_OR_EOL(ch)) {
        ch = state.input.charCodeAt(++state.position);
      }

      directiveArgs.push(state.input.slice(_position, state.position));
    }

    if (ch !== 0) readLineBreak(state);

    if (_hasOwnProperty$2.call(directiveHandlers, directiveName)) {
      directiveHandlers[directiveName](state, directiveName, directiveArgs);
    } else {
      throwWarning(state, 'unknown document directive "' + directiveName + '"');
    }
  }

  skipSeparationSpace(state, true, -1);

  if (state.lineIndent === 0 &&
      state.input.charCodeAt(state.position)     === 0x2D/* - */ &&
      state.input.charCodeAt(state.position + 1) === 0x2D/* - */ &&
      state.input.charCodeAt(state.position + 2) === 0x2D/* - */) {
    state.position += 3;
    skipSeparationSpace(state, true, -1);

  } else if (hasDirectives) {
    throwError(state, 'directives end mark is expected');
  }

  composeNode(state, state.lineIndent - 1, CONTEXT_BLOCK_OUT, false, true);
  skipSeparationSpace(state, true, -1);

  if (state.checkLineBreaks &&
      PATTERN_NON_ASCII_LINE_BREAKS.test(state.input.slice(documentStart, state.position))) {
    throwWarning(state, 'non-ASCII line breaks are interpreted as content');
  }

  state.documents.push(state.result);

  if (state.position === state.lineStart && testDocumentSeparator(state)) {

    if (state.input.charCodeAt(state.position) === 0x2E/* . */) {
      state.position += 3;
      skipSeparationSpace(state, true, -1);
    }
    return;
  }

  if (state.position < (state.length - 1)) {
    throwError(state, 'end of the stream or a document separator is expected');
  } else {
    return;
  }
}


function loadDocuments(input, options) {
  input = String(input);
  options = options || {};

  if (input.length !== 0) {

    // Add tailing `\n` if not exists
    if (input.charCodeAt(input.length - 1) !== 0x0A/* LF */ &&
        input.charCodeAt(input.length - 1) !== 0x0D/* CR */) {
      input += '\n';
    }

    // Strip BOM
    if (input.charCodeAt(0) === 0xFEFF) {
      input = input.slice(1);
    }
  }

  var state = new State(input, options);

  var nullpos = input.indexOf('\0');

  if (nullpos !== -1) {
    state.position = nullpos;
    throwError(state, 'null byte is not allowed in input');
  }

  // Use 0 as string terminator. That significantly simplifies bounds check.
  state.input += '\0';

  while (state.input.charCodeAt(state.position) === 0x20/* Space */) {
    state.lineIndent += 1;
    state.position += 1;
  }

  while (state.position < (state.length - 1)) {
    readDocument(state);
  }

  return state.documents;
}


function loadAll(input, iterator, options) {
  if (iterator !== null && typeof iterator === 'object' && typeof options === 'undefined') {
    options = iterator;
    iterator = null;
  }

  var documents = loadDocuments(input, options);

  if (typeof iterator !== 'function') {
    return documents;
  }

  for (var index = 0, length = documents.length; index < length; index += 1) {
    iterator(documents[index]);
  }
}


function load(input, options) {
  var documents = loadDocuments(input, options);

  if (documents.length === 0) {
    /*eslint-disable no-undefined*/
    return undefined;
  } else if (documents.length === 1) {
    return documents[0];
  }
  throw new exception('expected a single document in the stream, but found more');
}


function safeLoadAll(input, iterator, options) {
  if (typeof iterator === 'object' && iterator !== null && typeof options === 'undefined') {
    options = iterator;
    iterator = null;
  }

  return loadAll(input, iterator, common.extend({ schema: default_safe }, options));
}


function safeLoad(input, options) {
  return load(input, common.extend({ schema: default_safe }, options));
}


var loadAll_1     = loadAll;
var load_1        = load;
var safeLoadAll_1 = safeLoadAll;
var safeLoad_1    = safeLoad;

var loader = {
	loadAll: loadAll_1,
	load: load_1,
	safeLoadAll: safeLoadAll_1,
	safeLoad: safeLoad_1
};

/*eslint-disable no-use-before-define*/






var _toString$2       = Object.prototype.toString;
var _hasOwnProperty$3 = Object.prototype.hasOwnProperty;

var CHAR_TAB                  = 0x09; /* Tab */
var CHAR_LINE_FEED            = 0x0A; /* LF */
var CHAR_CARRIAGE_RETURN      = 0x0D; /* CR */
var CHAR_SPACE                = 0x20; /* Space */
var CHAR_EXCLAMATION          = 0x21; /* ! */
var CHAR_DOUBLE_QUOTE         = 0x22; /* " */
var CHAR_SHARP                = 0x23; /* # */
var CHAR_PERCENT              = 0x25; /* % */
var CHAR_AMPERSAND            = 0x26; /* & */
var CHAR_SINGLE_QUOTE         = 0x27; /* ' */
var CHAR_ASTERISK             = 0x2A; /* * */
var CHAR_COMMA                = 0x2C; /* , */
var CHAR_MINUS                = 0x2D; /* - */
var CHAR_COLON                = 0x3A; /* : */
var CHAR_EQUALS               = 0x3D; /* = */
var CHAR_GREATER_THAN         = 0x3E; /* > */
var CHAR_QUESTION             = 0x3F; /* ? */
var CHAR_COMMERCIAL_AT        = 0x40; /* @ */
var CHAR_LEFT_SQUARE_BRACKET  = 0x5B; /* [ */
var CHAR_RIGHT_SQUARE_BRACKET = 0x5D; /* ] */
var CHAR_GRAVE_ACCENT         = 0x60; /* ` */
var CHAR_LEFT_CURLY_BRACKET   = 0x7B; /* { */
var CHAR_VERTICAL_LINE        = 0x7C; /* | */
var CHAR_RIGHT_CURLY_BRACKET  = 0x7D; /* } */

var ESCAPE_SEQUENCES = {};

ESCAPE_SEQUENCES[0x00]   = '\\0';
ESCAPE_SEQUENCES[0x07]   = '\\a';
ESCAPE_SEQUENCES[0x08]   = '\\b';
ESCAPE_SEQUENCES[0x09]   = '\\t';
ESCAPE_SEQUENCES[0x0A]   = '\\n';
ESCAPE_SEQUENCES[0x0B]   = '\\v';
ESCAPE_SEQUENCES[0x0C]   = '\\f';
ESCAPE_SEQUENCES[0x0D]   = '\\r';
ESCAPE_SEQUENCES[0x1B]   = '\\e';
ESCAPE_SEQUENCES[0x22]   = '\\"';
ESCAPE_SEQUENCES[0x5C]   = '\\\\';
ESCAPE_SEQUENCES[0x85]   = '\\N';
ESCAPE_SEQUENCES[0xA0]   = '\\_';
ESCAPE_SEQUENCES[0x2028] = '\\L';
ESCAPE_SEQUENCES[0x2029] = '\\P';

var DEPRECATED_BOOLEANS_SYNTAX = [
  'y', 'Y', 'yes', 'Yes', 'YES', 'on', 'On', 'ON',
  'n', 'N', 'no', 'No', 'NO', 'off', 'Off', 'OFF'
];

function compileStyleMap(schema, map) {
  var result, keys, index, length, tag, style, type;

  if (map === null) return {};

  result = {};
  keys = Object.keys(map);

  for (index = 0, length = keys.length; index < length; index += 1) {
    tag = keys[index];
    style = String(map[tag]);

    if (tag.slice(0, 2) === '!!') {
      tag = 'tag:yaml.org,2002:' + tag.slice(2);
    }
    type = schema.compiledTypeMap['fallback'][tag];

    if (type && _hasOwnProperty$3.call(type.styleAliases, style)) {
      style = type.styleAliases[style];
    }

    result[tag] = style;
  }

  return result;
}

function encodeHex(character) {
  var string, handle, length;

  string = character.toString(16).toUpperCase();

  if (character <= 0xFF) {
    handle = 'x';
    length = 2;
  } else if (character <= 0xFFFF) {
    handle = 'u';
    length = 4;
  } else if (character <= 0xFFFFFFFF) {
    handle = 'U';
    length = 8;
  } else {
    throw new exception('code point within a string may not be greater than 0xFFFFFFFF');
  }

  return '\\' + handle + common.repeat('0', length - string.length) + string;
}

function State$1(options) {
  this.schema        = options['schema'] || default_full;
  this.indent        = Math.max(1, (options['indent'] || 2));
  this.noArrayIndent = options['noArrayIndent'] || false;
  this.skipInvalid   = options['skipInvalid'] || false;
  this.flowLevel     = (common.isNothing(options['flowLevel']) ? -1 : options['flowLevel']);
  this.styleMap      = compileStyleMap(this.schema, options['styles'] || null);
  this.sortKeys      = options['sortKeys'] || false;
  this.lineWidth     = options['lineWidth'] || 80;
  this.noRefs        = options['noRefs'] || false;
  this.noCompatMode  = options['noCompatMode'] || false;
  this.condenseFlow  = options['condenseFlow'] || false;

  this.implicitTypes = this.schema.compiledImplicit;
  this.explicitTypes = this.schema.compiledExplicit;

  this.tag = null;
  this.result = '';

  this.duplicates = [];
  this.usedDuplicates = null;
}

// Indents every line in a string. Empty lines (\n only) are not indented.
function indentString(string, spaces) {
  var ind = common.repeat(' ', spaces),
      position = 0,
      next = -1,
      result = '',
      line,
      length = string.length;

  while (position < length) {
    next = string.indexOf('\n', position);
    if (next === -1) {
      line = string.slice(position);
      position = length;
    } else {
      line = string.slice(position, next + 1);
      position = next + 1;
    }

    if (line.length && line !== '\n') result += ind;

    result += line;
  }

  return result;
}

function generateNextLine(state, level) {
  return '\n' + common.repeat(' ', state.indent * level);
}

function testImplicitResolving(state, str) {
  var index, length, type;

  for (index = 0, length = state.implicitTypes.length; index < length; index += 1) {
    type = state.implicitTypes[index];

    if (type.resolve(str)) {
      return true;
    }
  }

  return false;
}

// [33] s-white ::= s-space | s-tab
function isWhitespace(c) {
  return c === CHAR_SPACE || c === CHAR_TAB;
}

// Returns true if the character can be printed without escaping.
// From YAML 1.2: "any allowed characters known to be non-printable
// should also be escaped. [However,] This isn’t mandatory"
// Derived from nb-char - \t - #x85 - #xA0 - #x2028 - #x2029.
function isPrintable(c) {
  return  (0x00020 <= c && c <= 0x00007E)
      || ((0x000A1 <= c && c <= 0x00D7FF) && c !== 0x2028 && c !== 0x2029)
      || ((0x0E000 <= c && c <= 0x00FFFD) && c !== 0xFEFF /* BOM */)
      ||  (0x10000 <= c && c <= 0x10FFFF);
}

// [34] ns-char ::= nb-char - s-white
// [27] nb-char ::= c-printable - b-char - c-byte-order-mark
// [26] b-char  ::= b-line-feed | b-carriage-return
// [24] b-line-feed       ::=     #xA    /* LF */
// [25] b-carriage-return ::=     #xD    /* CR */
// [3]  c-byte-order-mark ::=     #xFEFF
function isNsChar(c) {
  return isPrintable(c) && !isWhitespace(c)
    // byte-order-mark
    && c !== 0xFEFF
    // b-char
    && c !== CHAR_CARRIAGE_RETURN
    && c !== CHAR_LINE_FEED;
}

// Simplified test for values allowed after the first character in plain style.
function isPlainSafe(c, prev) {
  // Uses a subset of nb-char - c-flow-indicator - ":" - "#"
  // where nb-char ::= c-printable - b-char - c-byte-order-mark.
  return isPrintable(c) && c !== 0xFEFF
    // - c-flow-indicator
    && c !== CHAR_COMMA
    && c !== CHAR_LEFT_SQUARE_BRACKET
    && c !== CHAR_RIGHT_SQUARE_BRACKET
    && c !== CHAR_LEFT_CURLY_BRACKET
    && c !== CHAR_RIGHT_CURLY_BRACKET
    // - ":" - "#"
    // /* An ns-char preceding */ "#"
    && c !== CHAR_COLON
    && ((c !== CHAR_SHARP) || (prev && isNsChar(prev)));
}

// Simplified test for values allowed as the first character in plain style.
function isPlainSafeFirst(c) {
  // Uses a subset of ns-char - c-indicator
  // where ns-char = nb-char - s-white.
  return isPrintable(c) && c !== 0xFEFF
    && !isWhitespace(c) // - s-white
    // - (c-indicator ::=
    // “-” | “?” | “:” | “,” | “[” | “]” | “{” | “}”
    && c !== CHAR_MINUS
    && c !== CHAR_QUESTION
    && c !== CHAR_COLON
    && c !== CHAR_COMMA
    && c !== CHAR_LEFT_SQUARE_BRACKET
    && c !== CHAR_RIGHT_SQUARE_BRACKET
    && c !== CHAR_LEFT_CURLY_BRACKET
    && c !== CHAR_RIGHT_CURLY_BRACKET
    // | “#” | “&” | “*” | “!” | “|” | “=” | “>” | “'” | “"”
    && c !== CHAR_SHARP
    && c !== CHAR_AMPERSAND
    && c !== CHAR_ASTERISK
    && c !== CHAR_EXCLAMATION
    && c !== CHAR_VERTICAL_LINE
    && c !== CHAR_EQUALS
    && c !== CHAR_GREATER_THAN
    && c !== CHAR_SINGLE_QUOTE
    && c !== CHAR_DOUBLE_QUOTE
    // | “%” | “@” | “`”)
    && c !== CHAR_PERCENT
    && c !== CHAR_COMMERCIAL_AT
    && c !== CHAR_GRAVE_ACCENT;
}

// Determines whether block indentation indicator is required.
function needIndentIndicator(string) {
  var leadingSpaceRe = /^\n* /;
  return leadingSpaceRe.test(string);
}

var STYLE_PLAIN   = 1,
    STYLE_SINGLE  = 2,
    STYLE_LITERAL = 3,
    STYLE_FOLDED  = 4,
    STYLE_DOUBLE  = 5;

// Determines which scalar styles are possible and returns the preferred style.
// lineWidth = -1 => no limit.
// Pre-conditions: str.length > 0.
// Post-conditions:
//    STYLE_PLAIN or STYLE_SINGLE => no \n are in the string.
//    STYLE_LITERAL => no lines are suitable for folding (or lineWidth is -1).
//    STYLE_FOLDED => a line > lineWidth and can be folded (and lineWidth != -1).
function chooseScalarStyle(string, singleLineOnly, indentPerLevel, lineWidth, testAmbiguousType) {
  var i;
  var char, prev_char;
  var hasLineBreak = false;
  var hasFoldableLine = false; // only checked if shouldTrackWidth
  var shouldTrackWidth = lineWidth !== -1;
  var previousLineBreak = -1; // count the first line correctly
  var plain = isPlainSafeFirst(string.charCodeAt(0))
          && !isWhitespace(string.charCodeAt(string.length - 1));

  if (singleLineOnly) {
    // Case: no block styles.
    // Check for disallowed characters to rule out plain and single.
    for (i = 0; i < string.length; i++) {
      char = string.charCodeAt(i);
      if (!isPrintable(char)) {
        return STYLE_DOUBLE;
      }
      prev_char = i > 0 ? string.charCodeAt(i - 1) : null;
      plain = plain && isPlainSafe(char, prev_char);
    }
  } else {
    // Case: block styles permitted.
    for (i = 0; i < string.length; i++) {
      char = string.charCodeAt(i);
      if (char === CHAR_LINE_FEED) {
        hasLineBreak = true;
        // Check if any line can be folded.
        if (shouldTrackWidth) {
          hasFoldableLine = hasFoldableLine ||
            // Foldable line = too long, and not more-indented.
            (i - previousLineBreak - 1 > lineWidth &&
             string[previousLineBreak + 1] !== ' ');
          previousLineBreak = i;
        }
      } else if (!isPrintable(char)) {
        return STYLE_DOUBLE;
      }
      prev_char = i > 0 ? string.charCodeAt(i - 1) : null;
      plain = plain && isPlainSafe(char, prev_char);
    }
    // in case the end is missing a \n
    hasFoldableLine = hasFoldableLine || (shouldTrackWidth &&
      (i - previousLineBreak - 1 > lineWidth &&
       string[previousLineBreak + 1] !== ' '));
  }
  // Although every style can represent \n without escaping, prefer block styles
  // for multiline, since they're more readable and they don't add empty lines.
  // Also prefer folding a super-long line.
  if (!hasLineBreak && !hasFoldableLine) {
    // Strings interpretable as another type have to be quoted;
    // e.g. the string 'true' vs. the boolean true.
    return plain && !testAmbiguousType(string)
      ? STYLE_PLAIN : STYLE_SINGLE;
  }
  // Edge case: block indentation indicator can only have one digit.
  if (indentPerLevel > 9 && needIndentIndicator(string)) {
    return STYLE_DOUBLE;
  }
  // At this point we know block styles are valid.
  // Prefer literal style unless we want to fold.
  return hasFoldableLine ? STYLE_FOLDED : STYLE_LITERAL;
}

// Note: line breaking/folding is implemented for only the folded style.
// NB. We drop the last trailing newline (if any) of a returned block scalar
//  since the dumper adds its own newline. This always works:
//    • No ending newline => unaffected; already using strip "-" chomping.
//    • Ending newline    => removed then restored.
//  Importantly, this keeps the "+" chomp indicator from gaining an extra line.
function writeScalar(state, string, level, iskey) {
  state.dump = (function () {
    if (string.length === 0) {
      return "''";
    }
    if (!state.noCompatMode &&
        DEPRECATED_BOOLEANS_SYNTAX.indexOf(string) !== -1) {
      return "'" + string + "'";
    }

    var indent = state.indent * Math.max(1, level); // no 0-indent scalars
    // As indentation gets deeper, let the width decrease monotonically
    // to the lower bound min(state.lineWidth, 40).
    // Note that this implies
    //  state.lineWidth ≤ 40 + state.indent: width is fixed at the lower bound.
    //  state.lineWidth > 40 + state.indent: width decreases until the lower bound.
    // This behaves better than a constant minimum width which disallows narrower options,
    // or an indent threshold which causes the width to suddenly increase.
    var lineWidth = state.lineWidth === -1
      ? -1 : Math.max(Math.min(state.lineWidth, 40), state.lineWidth - indent);

    // Without knowing if keys are implicit/explicit, assume implicit for safety.
    var singleLineOnly = iskey
      // No block styles in flow mode.
      || (state.flowLevel > -1 && level >= state.flowLevel);
    function testAmbiguity(string) {
      return testImplicitResolving(state, string);
    }

    switch (chooseScalarStyle(string, singleLineOnly, state.indent, lineWidth, testAmbiguity)) {
      case STYLE_PLAIN:
        return string;
      case STYLE_SINGLE:
        return "'" + string.replace(/'/g, "''") + "'";
      case STYLE_LITERAL:
        return '|' + blockHeader(string, state.indent)
          + dropEndingNewline(indentString(string, indent));
      case STYLE_FOLDED:
        return '>' + blockHeader(string, state.indent)
          + dropEndingNewline(indentString(foldString(string, lineWidth), indent));
      case STYLE_DOUBLE:
        return '"' + escapeString(string) + '"';
      default:
        throw new exception('impossible error: invalid scalar style');
    }
  }());
}

// Pre-conditions: string is valid for a block scalar, 1 <= indentPerLevel <= 9.
function blockHeader(string, indentPerLevel) {
  var indentIndicator = needIndentIndicator(string) ? String(indentPerLevel) : '';

  // note the special case: the string '\n' counts as a "trailing" empty line.
  var clip =          string[string.length - 1] === '\n';
  var keep = clip && (string[string.length - 2] === '\n' || string === '\n');
  var chomp = keep ? '+' : (clip ? '' : '-');

  return indentIndicator + chomp + '\n';
}

// (See the note for writeScalar.)
function dropEndingNewline(string) {
  return string[string.length - 1] === '\n' ? string.slice(0, -1) : string;
}

// Note: a long line without a suitable break point will exceed the width limit.
// Pre-conditions: every char in str isPrintable, str.length > 0, width > 0.
function foldString(string, width) {
  // In folded style, $k$ consecutive newlines output as $k+1$ newlines—
  // unless they're before or after a more-indented line, or at the very
  // beginning or end, in which case $k$ maps to $k$.
  // Therefore, parse each chunk as newline(s) followed by a content line.
  var lineRe = /(\n+)([^\n]*)/g;

  // first line (possibly an empty line)
  var result = (function () {
    var nextLF = string.indexOf('\n');
    nextLF = nextLF !== -1 ? nextLF : string.length;
    lineRe.lastIndex = nextLF;
    return foldLine(string.slice(0, nextLF), width);
  }());
  // If we haven't reached the first content line yet, don't add an extra \n.
  var prevMoreIndented = string[0] === '\n' || string[0] === ' ';
  var moreIndented;

  // rest of the lines
  var match;
  while ((match = lineRe.exec(string))) {
    var prefix = match[1], line = match[2];
    moreIndented = (line[0] === ' ');
    result += prefix
      + (!prevMoreIndented && !moreIndented && line !== ''
        ? '\n' : '')
      + foldLine(line, width);
    prevMoreIndented = moreIndented;
  }

  return result;
}

// Greedy line breaking.
// Picks the longest line under the limit each time,
// otherwise settles for the shortest line over the limit.
// NB. More-indented lines *cannot* be folded, as that would add an extra \n.
function foldLine(line, width) {
  if (line === '' || line[0] === ' ') return line;

  // Since a more-indented line adds a \n, breaks can't be followed by a space.
  var breakRe = / [^ ]/g; // note: the match index will always be <= length-2.
  var match;
  // start is an inclusive index. end, curr, and next are exclusive.
  var start = 0, end, curr = 0, next = 0;
  var result = '';

  // Invariants: 0 <= start <= length-1.
  //   0 <= curr <= next <= max(0, length-2). curr - start <= width.
  // Inside the loop:
  //   A match implies length >= 2, so curr and next are <= length-2.
  while ((match = breakRe.exec(line))) {
    next = match.index;
    // maintain invariant: curr - start <= width
    if (next - start > width) {
      end = (curr > start) ? curr : next; // derive end <= length-2
      result += '\n' + line.slice(start, end);
      // skip the space that was output as \n
      start = end + 1;                    // derive start <= length-1
    }
    curr = next;
  }

  // By the invariants, start <= length-1, so there is something left over.
  // It is either the whole string or a part starting from non-whitespace.
  result += '\n';
  // Insert a break if the remainder is too long and there is a break available.
  if (line.length - start > width && curr > start) {
    result += line.slice(start, curr) + '\n' + line.slice(curr + 1);
  } else {
    result += line.slice(start);
  }

  return result.slice(1); // drop extra \n joiner
}

// Escapes a double-quoted string.
function escapeString(string) {
  var result = '';
  var char, nextChar;
  var escapeSeq;

  for (var i = 0; i < string.length; i++) {
    char = string.charCodeAt(i);
    // Check for surrogate pairs (reference Unicode 3.0 section "3.7 Surrogates").
    if (char >= 0xD800 && char <= 0xDBFF/* high surrogate */) {
      nextChar = string.charCodeAt(i + 1);
      if (nextChar >= 0xDC00 && nextChar <= 0xDFFF/* low surrogate */) {
        // Combine the surrogate pair and store it escaped.
        result += encodeHex((char - 0xD800) * 0x400 + nextChar - 0xDC00 + 0x10000);
        // Advance index one extra since we already used that char here.
        i++; continue;
      }
    }
    escapeSeq = ESCAPE_SEQUENCES[char];
    result += !escapeSeq && isPrintable(char)
      ? string[i]
      : escapeSeq || encodeHex(char);
  }

  return result;
}

function writeFlowSequence(state, level, object) {
  var _result = '',
      _tag    = state.tag,
      index,
      length;

  for (index = 0, length = object.length; index < length; index += 1) {
    // Write only valid elements.
    if (writeNode(state, level, object[index], false, false)) {
      if (index !== 0) _result += ',' + (!state.condenseFlow ? ' ' : '');
      _result += state.dump;
    }
  }

  state.tag = _tag;
  state.dump = '[' + _result + ']';
}

function writeBlockSequence(state, level, object, compact) {
  var _result = '',
      _tag    = state.tag,
      index,
      length;

  for (index = 0, length = object.length; index < length; index += 1) {
    // Write only valid elements.
    if (writeNode(state, level + 1, object[index], true, true)) {
      if (!compact || index !== 0) {
        _result += generateNextLine(state, level);
      }

      if (state.dump && CHAR_LINE_FEED === state.dump.charCodeAt(0)) {
        _result += '-';
      } else {
        _result += '- ';
      }

      _result += state.dump;
    }
  }

  state.tag = _tag;
  state.dump = _result || '[]'; // Empty sequence if no valid values.
}

function writeFlowMapping(state, level, object) {
  var _result       = '',
      _tag          = state.tag,
      objectKeyList = Object.keys(object),
      index,
      length,
      objectKey,
      objectValue,
      pairBuffer;

  for (index = 0, length = objectKeyList.length; index < length; index += 1) {

    pairBuffer = '';
    if (index !== 0) pairBuffer += ', ';

    if (state.condenseFlow) pairBuffer += '"';

    objectKey = objectKeyList[index];
    objectValue = object[objectKey];

    if (!writeNode(state, level, objectKey, false, false)) {
      continue; // Skip this pair because of invalid key;
    }

    if (state.dump.length > 1024) pairBuffer += '? ';

    pairBuffer += state.dump + (state.condenseFlow ? '"' : '') + ':' + (state.condenseFlow ? '' : ' ');

    if (!writeNode(state, level, objectValue, false, false)) {
      continue; // Skip this pair because of invalid value.
    }

    pairBuffer += state.dump;

    // Both key and value are valid.
    _result += pairBuffer;
  }

  state.tag = _tag;
  state.dump = '{' + _result + '}';
}

function writeBlockMapping(state, level, object, compact) {
  var _result       = '',
      _tag          = state.tag,
      objectKeyList = Object.keys(object),
      index,
      length,
      objectKey,
      objectValue,
      explicitPair,
      pairBuffer;

  // Allow sorting keys so that the output file is deterministic
  if (state.sortKeys === true) {
    // Default sorting
    objectKeyList.sort();
  } else if (typeof state.sortKeys === 'function') {
    // Custom sort function
    objectKeyList.sort(state.sortKeys);
  } else if (state.sortKeys) {
    // Something is wrong
    throw new exception('sortKeys must be a boolean or a function');
  }

  for (index = 0, length = objectKeyList.length; index < length; index += 1) {
    pairBuffer = '';

    if (!compact || index !== 0) {
      pairBuffer += generateNextLine(state, level);
    }

    objectKey = objectKeyList[index];
    objectValue = object[objectKey];

    if (!writeNode(state, level + 1, objectKey, true, true, true)) {
      continue; // Skip this pair because of invalid key.
    }

    explicitPair = (state.tag !== null && state.tag !== '?') ||
                   (state.dump && state.dump.length > 1024);

    if (explicitPair) {
      if (state.dump && CHAR_LINE_FEED === state.dump.charCodeAt(0)) {
        pairBuffer += '?';
      } else {
        pairBuffer += '? ';
      }
    }

    pairBuffer += state.dump;

    if (explicitPair) {
      pairBuffer += generateNextLine(state, level);
    }

    if (!writeNode(state, level + 1, objectValue, true, explicitPair)) {
      continue; // Skip this pair because of invalid value.
    }

    if (state.dump && CHAR_LINE_FEED === state.dump.charCodeAt(0)) {
      pairBuffer += ':';
    } else {
      pairBuffer += ': ';
    }

    pairBuffer += state.dump;

    // Both key and value are valid.
    _result += pairBuffer;
  }

  state.tag = _tag;
  state.dump = _result || '{}'; // Empty mapping if no valid pairs.
}

function detectType(state, object, explicit) {
  var _result, typeList, index, length, type, style;

  typeList = explicit ? state.explicitTypes : state.implicitTypes;

  for (index = 0, length = typeList.length; index < length; index += 1) {
    type = typeList[index];

    if ((type.instanceOf  || type.predicate) &&
        (!type.instanceOf || ((typeof object === 'object') && (object instanceof type.instanceOf))) &&
        (!type.predicate  || type.predicate(object))) {

      state.tag = explicit ? type.tag : '?';

      if (type.represent) {
        style = state.styleMap[type.tag] || type.defaultStyle;

        if (_toString$2.call(type.represent) === '[object Function]') {
          _result = type.represent(object, style);
        } else if (_hasOwnProperty$3.call(type.represent, style)) {
          _result = type.represent[style](object, style);
        } else {
          throw new exception('!<' + type.tag + '> tag resolver accepts not "' + style + '" style');
        }

        state.dump = _result;
      }

      return true;
    }
  }

  return false;
}

// Serializes `object` and writes it to global `result`.
// Returns true on success, or false on invalid object.
//
function writeNode(state, level, object, block, compact, iskey) {
  state.tag = null;
  state.dump = object;

  if (!detectType(state, object, false)) {
    detectType(state, object, true);
  }

  var type = _toString$2.call(state.dump);

  if (block) {
    block = (state.flowLevel < 0 || state.flowLevel > level);
  }

  var objectOrArray = type === '[object Object]' || type === '[object Array]',
      duplicateIndex,
      duplicate;

  if (objectOrArray) {
    duplicateIndex = state.duplicates.indexOf(object);
    duplicate = duplicateIndex !== -1;
  }

  if ((state.tag !== null && state.tag !== '?') || duplicate || (state.indent !== 2 && level > 0)) {
    compact = false;
  }

  if (duplicate && state.usedDuplicates[duplicateIndex]) {
    state.dump = '*ref_' + duplicateIndex;
  } else {
    if (objectOrArray && duplicate && !state.usedDuplicates[duplicateIndex]) {
      state.usedDuplicates[duplicateIndex] = true;
    }
    if (type === '[object Object]') {
      if (block && (Object.keys(state.dump).length !== 0)) {
        writeBlockMapping(state, level, state.dump, compact);
        if (duplicate) {
          state.dump = '&ref_' + duplicateIndex + state.dump;
        }
      } else {
        writeFlowMapping(state, level, state.dump);
        if (duplicate) {
          state.dump = '&ref_' + duplicateIndex + ' ' + state.dump;
        }
      }
    } else if (type === '[object Array]') {
      var arrayLevel = (state.noArrayIndent && (level > 0)) ? level - 1 : level;
      if (block && (state.dump.length !== 0)) {
        writeBlockSequence(state, arrayLevel, state.dump, compact);
        if (duplicate) {
          state.dump = '&ref_' + duplicateIndex + state.dump;
        }
      } else {
        writeFlowSequence(state, arrayLevel, state.dump);
        if (duplicate) {
          state.dump = '&ref_' + duplicateIndex + ' ' + state.dump;
        }
      }
    } else if (type === '[object String]') {
      if (state.tag !== '?') {
        writeScalar(state, state.dump, level, iskey);
      }
    } else {
      if (state.skipInvalid) return false;
      throw new exception('unacceptable kind of an object to dump ' + type);
    }

    if (state.tag !== null && state.tag !== '?') {
      state.dump = '!<' + state.tag + '> ' + state.dump;
    }
  }

  return true;
}

function getDuplicateReferences(object, state) {
  var objects = [],
      duplicatesIndexes = [],
      index,
      length;

  inspectNode(object, objects, duplicatesIndexes);

  for (index = 0, length = duplicatesIndexes.length; index < length; index += 1) {
    state.duplicates.push(objects[duplicatesIndexes[index]]);
  }
  state.usedDuplicates = new Array(length);
}

function inspectNode(object, objects, duplicatesIndexes) {
  var objectKeyList,
      index,
      length;

  if (object !== null && typeof object === 'object') {
    index = objects.indexOf(object);
    if (index !== -1) {
      if (duplicatesIndexes.indexOf(index) === -1) {
        duplicatesIndexes.push(index);
      }
    } else {
      objects.push(object);

      if (Array.isArray(object)) {
        for (index = 0, length = object.length; index < length; index += 1) {
          inspectNode(object[index], objects, duplicatesIndexes);
        }
      } else {
        objectKeyList = Object.keys(object);

        for (index = 0, length = objectKeyList.length; index < length; index += 1) {
          inspectNode(object[objectKeyList[index]], objects, duplicatesIndexes);
        }
      }
    }
  }
}

function dump(input, options) {
  options = options || {};

  var state = new State$1(options);

  if (!state.noRefs) getDuplicateReferences(input, state);

  if (writeNode(state, 0, input, true, true)) return state.dump + '\n';

  return '';
}

function safeDump(input, options) {
  return dump(input, common.extend({ schema: default_safe }, options));
}

var dump_1     = dump;
var safeDump_1 = safeDump;

var dumper = {
	dump: dump_1,
	safeDump: safeDump_1
};

function deprecated(name) {
  return function () {
    throw new Error('Function ' + name + ' is deprecated and cannot be used.');
  };
}


var Type$1                = type;
var Schema$1              = schema;
var FAILSAFE_SCHEMA     = failsafe;
var JSON_SCHEMA         = json;
var CORE_SCHEMA         = core;
var DEFAULT_SAFE_SCHEMA = default_safe;
var DEFAULT_FULL_SCHEMA = default_full;
var load$1                = loader.load;
var loadAll$1             = loader.loadAll;
var safeLoad$1            = loader.safeLoad;
var safeLoadAll$1         = loader.safeLoadAll;
var dump$1                = dumper.dump;
var safeDump$1            = dumper.safeDump;
var YAMLException$1       = exception;

// Deprecated schema names from JS-YAML 2.0.x
var MINIMAL_SCHEMA = failsafe;
var SAFE_SCHEMA    = default_safe;
var DEFAULT_SCHEMA = default_full;

// Deprecated functions from JS-YAML 1.x.x
var scan           = deprecated('scan');
var parse$1          = deprecated('parse');
var compose        = deprecated('compose');
var addConstructor = deprecated('addConstructor');

var jsYaml = {
	Type: Type$1,
	Schema: Schema$1,
	FAILSAFE_SCHEMA: FAILSAFE_SCHEMA,
	JSON_SCHEMA: JSON_SCHEMA,
	CORE_SCHEMA: CORE_SCHEMA,
	DEFAULT_SAFE_SCHEMA: DEFAULT_SAFE_SCHEMA,
	DEFAULT_FULL_SCHEMA: DEFAULT_FULL_SCHEMA,
	load: load$1,
	loadAll: loadAll$1,
	safeLoad: safeLoad$1,
	safeLoadAll: safeLoadAll$1,
	dump: dump$1,
	safeDump: safeDump$1,
	YAMLException: YAMLException$1,
	MINIMAL_SCHEMA: MINIMAL_SCHEMA,
	SAFE_SCHEMA: SAFE_SCHEMA,
	DEFAULT_SCHEMA: DEFAULT_SCHEMA,
	scan: scan,
	parse: parse$1,
	compose: compose,
	addConstructor: addConstructor
};

var jsYaml$1 = jsYaml;

function getModuleFromYaml(moduleName, yamlPath) {
    const data = jsYaml$1.safeLoadAll(fs.readFileSync(yamlPath, 'utf8'));
    const mod = data[0].modules[moduleName];
    if (mod) {
        // Convert fields from object to array
        mod.fields = Object.keys(mod.fields).map((k) => mod.fields[k]);
        return new Module(mod);
    }
    else {
        return undefined;
    }
}

/**
 * BaseChart represents a structure that stores any configuration data.
 * Any display and data rendering operations should be handled by any sub classes.
 */
class BaseChart {
    constructor(def = {}) {
        this.chartID = NoID;
        this.namespaceID = NoID;
        this.name = '';
        this.handle = '';
        this.createdAt = undefined;
        this.updatedAt = undefined;
        this.deletedAt = undefined;
        this.canUpdateChart = false;
        this.canDeleteChart = false;
        this.canGrant = false;
        this.config = {};
        this.merge(def);
    }
    merge(c) {
        let conf = Object.assign({}, (c.config || {}));
        Apply(this, c, CortezaID, 'chartID', 'namespaceID');
        Apply(this, c, String, 'name', 'handle');
        Apply(this, c, ISO8601Date, 'createdAt', 'updatedAt', 'deletedAt');
        Apply(this, c, Boolean, 'canUpdateChart', 'canDeleteChart', 'canGrant');
        Apply(this, c, Object, 'config');
        if (typeof c.config === 'object') {
            // Verify & normalize
            let _a = c.config, { renderer, reports } = _a, rest = __rest(_a, ["renderer", "reports"]);
            if (renderer) {
                const { version } = renderer || {};
                if (version !== 'chart.js') {
                    throw Error('notification.chart.unsupportedRenderer');
                }
            }
            else {
                renderer = { version: ChartRenderer.chartJS };
            }
            conf = Object.assign({ renderer, reports: reports || [] }, rest);
        }
        this.config = (conf ? _.merge(this.defConfig(), conf) : false) || this.config || this.defConfig();
    }
    /**
     * Checks reports validity.
     * Validates dimensions and metrics.
     * If invalid it throws an error.
     */
    isValid() {
        if (!this.config.reports || !this.config.reports.length) {
            throw new Error('notification.chart.invalidConfig.missingReports');
        }
        this.config.reports.map(({ moduleID, dimensions, metrics }) => {
            var _a, _b;
            if (!moduleID) {
                throw new Error('notification.chart.invalidConfig.missingModuleID');
            }
            // Expecting all dimensions to have defined fields
            (_a = dimensions) === null || _a === void 0 ? void 0 : _a.forEach(this.dimCheck);
            // Expecting all metrics to have defined fields
            (_b = metrics) === null || _b === void 0 ? void 0 : _b.forEach(this.mtrCheck);
        });
        return true;
    }
    /**
     * Checks validity of dimensions.
     * If invalid it throws an error
     */
    dimCheck({ field, modifier }) {
        if (!field) {
            throw new Error('notification.chart.invalidConfig.missingDimensionsField');
        }
        if (!modifier) {
            throw new Error('notification.chart.invalidConfig.missingDimensionsModifier');
        }
    }
    /**
     * Checks validity of metrics.
     * If invalid it throws an error
     */
    mtrCheck({ field, aggregate, type }) {
        if (!field) {
            throw new Error('notification.chart.invalidConfig.missingMetricsField');
        }
        if (field !== 'count' && !aggregate) {
            throw new Error('notification.chart.invalidConfig.missingMetricsAggregate');
        }
        if (!type) {
            throw new Error('notification.chart.invalidConfig.missingMetricsType');
        }
    }
    /**
     * Prepares params that the reporter can use for querying.
     */
    formatReporterParams({ moduleID, metrics, dimensions, filter }) {
        var _a, _b;
        return {
            moduleID,
            filter,
            // Remove count (we'll get it anyway) and construct FUNC(ARG) params
            metrics: (_a = metrics) === null || _a === void 0 ? void 0 : _a.filter((m) => m.field !== 'count').map((m) => `${m.aggregate}(${m.field}) AS ${makeAlias(m)}`).join(','),
            // Construct dimensions \w modifiers...
            dimensions: (_b = dimensions) === null || _b === void 0 ? void 0 : _b.map((d) => dimensionFunctions.convert(d))[0],
        };
    }
    /**
     * Fetcher reports defined in the given configuration with the help of the provided
     * reporter.
     */
    fetchReports({ reporter }) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const out = [];
            // Prepare params & filter out invalid combos (formatReporterParams will return null on invalid params)
            const reports = (_a = this.config.reports) === null || _a === void 0 ? void 0 : _a.map(this.formatReporterParams).map(params => reporter(params)).map((p, index) => p.then((results) => {
                out[index] = this.processReporterResults(results, (this.config.reports || [])[index]);
            }));
            // Wait for all requests to finish and return new promise, with results
            return Promise.all(reports).then(() => new Promise(resolve => {
                resolve(out);
            }));
        });
    }
    /**
     * Processes provided report with it's results:
     * * skip missing values, if so requested,
     * * generate labels,
     * * creates dataset for the chart.
     */
    processReporterResults(results, report) {
        var _a, _b;
        const dLabel = 'dimension_0';
        const { dimensions: [dimension] = [] } = report;
        const isTimeDimension = !!(dimensionFunctions.lookup(dimension) || {}).time;
        const hasRadialChart = (_a = report.metrics) === null || _a === void 0 ? void 0 : _a.find(isRadialChart);
        let labels = [];
        // helper to choose between eight the provided value, default value or a generic 'undefined'
        const pickValue = (val, { default: dDft }) => {
            if (val !== undefined && val !== null)
                return val;
            if (dDft !== undefined && dDft !== null)
                return dDft;
            return 'undefined';
        };
        // Skip missing values; if so requested
        if (dimension.skipMissing) {
            results = results.filter((r) => r[dLabel] !== null);
        }
        // Not a time dimensions, build set of labels
        if (!isTimeDimension || hasRadialChart) {
            labels = results.map((r) => pickValue(r[dLabel], dimension));
        }
        // Build data sets
        const datasets = (_b = report.metrics) === null || _b === void 0 ? void 0 : _b.map(m => {
            const alias = makeAlias({ field: m.field, aggregate: m.aggregate });
            const data = results.map((r) => {
                const y = r[m.field === 'count' ? m.field : alias];
                if (!isTimeDimension || hasRadialChart) {
                    return pickValue(y, dimension);
                }
                return { y, t: moment(pickValue(r[dLabel], dimension)).toDate() };
            });
            // Any sub class has the ability to define how the dataset looks like.
            // this comes in handy when we want to support charts with different definitions.
            return this.makeDataset(m, dimension, data, alias);
        });
        return {
            labels: this.processLabels(labels, dimension),
            datasets,
        };
    }
    processLabels(ll, d) {
        return ll;
    }
    makeDataset(m, d, data, alias) {
        throw new Error('method.makeDataset.notImplemented');
    }
    makeOptions(data) {
        throw new Error('method.makeOptions.notImplemented');
    }
    plugins(mm) {
        throw new Error('method.plugins.notImplemented');
    }
    baseChartType(datasets) {
        throw new Error('method.baseChartType.notImplemented');
    }
    /**
     * Performs chart export; used by exporter feature.
     */
    export(findModuleByID) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID } = this;
            const copy = new BaseChart(this);
            if ((_a = copy.config) === null || _a === void 0 ? void 0 : _a.reports) {
                yield Promise.all(copy.config.reports.map((r) => __awaiter(this, void 0, void 0, function* () {
                    const { moduleID } = r;
                    if (moduleID) {
                        const module = yield findModuleByID({ namespaceID, moduleID });
                        r.moduleID = module.name;
                        return r;
                    }
                    else {
                        return null;
                    }
                }))).then((a) => {
                    return a;
                });
            }
            return copy;
        });
    }
    /**
     * Performs import; used by importer feature
     */
    import(getModuleID) {
        var _a, _b;
        const copy = new BaseChart(this);
        (_b = (_a = copy.config) === null || _a === void 0 ? void 0 : _a.reports) === null || _b === void 0 ? void 0 : _b.map(r => {
            const { moduleID } = r;
            if (moduleID) {
                r.moduleID = getModuleID(moduleID);
            }
            return r;
        });
        return copy;
    }
    defDimension() {
        return Object.assign({}, { conditions: {}, meta: {} });
    }
    defMetrics() {
        return Object.assign({}, {});
    }
    defReport() {
        return Object.assign({}, {
            moduleID: null,
            filter: null,
            dimensions: [this.defDimension()],
            metrics: [this.defMetrics()],
            yAxis: {},
        });
    }
    defConfig() {
        return Object.assign({}, {
            colorScheme: undefined,
            reports: [this.defReport()],
            renderer: {
                version: ChartRenderer.chartJS,
            },
        });
    }
    /**
     * Resource type
     */
    get resourceType() {
        return 'compose:chart';
    }
}

const ChartJS$1 = require('chart.js');
// The default dataset post processing function to use.
// This one simply returns the current value.
const defaultFx = 'n';
/**
 * Chart represents a generic chart, such as a bar chart, line chart, ...
 */
class Chart extends BaseChart {
    // Generic charts (at the moment) support only 1 report per chart
    fetchReports(a) {
        const _super = Object.create(null, {
            fetchReports: { get: () => super.fetchReports }
        });
        return __awaiter(this, void 0, void 0, function* () {
            return _super.fetchReports.call(this, a).then((rr) => {
                return rr[0];
            });
        });
    }
    /**
     * The method performs post processing for each value in the given dataset.
     * It works with a simple equation written in javascript (example: n + m).
     * Available variables to use:
     * * n - current value
     * * m - previous value (undefined in case of the first element)
     * * r - entire data array.
     *
     * @param data Array of values in the given data set
     * @param m Metric for the given dataset
     */
    datasetPostProc(data, m) {
        var _a;
        // Define a valid function to evaluate
        let fxRaw = (m.fx || defaultFx).trim();
        if (!fxRaw.startsWith('return')) {
            fxRaw = 'return ' + fxRaw;
        }
        const fx = new Function('n', 'm', 'r', fxRaw);
        // Define a new array, so we don't alter the original one.
        const r = [...data];
        // Run postprocessing for all data in the given data set
        // There is a slight difference between temporal data points and categorical data points.
        if (data[0] instanceof Object) {
            // Temporal
            for (let i = 0; i < data.length; i++) {
                const a = data[i];
                const b = data[i - 1];
                const n = a.y;
                let m;
                if (i > 0) {
                    m = (_a = b) === null || _a === void 0 ? void 0 : _a.y;
                }
                a.y = fx(n, m, r);
            }
        }
        else {
            // Categorical
            for (let i = 0; i < data.length; i++) {
                const n = data[i];
                let m;
                if (i > 0) {
                    m = data[i - 1];
                }
                data[i] = fx(n, m, r);
            }
        }
        return data;
    }
    makeDataset(m, d, data, alias) {
        data = this.datasetPostProc(data, m);
        const ds = { data };
        // colors
        if (typeof m.backgroundColor === 'string' && !isRadialChart(m)) {
            ds.backgroundColor = 'rgba(' + parseInt(m.backgroundColor.slice(-6, -4), 16) + ',' + parseInt(m.backgroundColor.slice(-4, -2), 16) + ',' + parseInt(m.backgroundColor.slice(-2), 16) + ',0.7)';
            ds.hoverBackgroundColor = m.backgroundColor;
        }
        return Object.assign(ds, {
            label: m.label || m.field,
            type: m.type,
            fill: !!m.fill,
            lineTension: m.lineTension || 0,
            tooltips: {
                enabled: true,
                relativeValue: !!m.relativeValue,
                relativePrecision: m.relativePrecision,
                labelCallback: m.fixTooltips ? this.makeLabel : this.makeTooltip,
            },
        });
    }
    makeOptions() {
        const options = {
            // Allow chart to consume entire container
            responsive: true,
            maintainAspectRatio: false,
            animation: {
                duration: 500,
            },
            legend: {
                position: 'top',
            },
        };
        if (this.config.colorScheme) {
            options.plugins = {
                colorschemes: {
                    scheme: this.config.colorScheme,
                },
            };
        }
        (this.config.reports || []).forEach(r => {
            var _a, _b;
            if (!options.scales) {
                options.scales = { xAxes: [], yAxes: [] };
            }
            // can't disable tooltips on dataset level, so this is required
            options.tooltips = {
                filter: ({ datasetIndex }, { datasets }) => {
                    // enabled can be undefined, so it must be checked against false
                    return ((datasets[datasetIndex] || {}).tooltips || {}).enabled !== false;
                },
                callbacks: {
                    label: ({ datasetIndex, index }, { datasets, labels }) => {
                        const dataset = datasets[datasetIndex];
                        return dataset.tooltips.labelCallback({ datasetIndex, index }, { datasets, labels });
                    },
                },
            };
            if ((_a = r.metrics) === null || _a === void 0 ? void 0 : _a.find((m) => !isRadialChart(m))) {
                options.scales.xAxes = (_b = r.dimensions) === null || _b === void 0 ? void 0 : _b.map((d, i) => {
                    const ticks = {
                        autoSkip: !!d.autoSkip,
                    };
                    const timeDimensionUnit = (dimensionFunctions.lookup(d) || {}).time;
                    if (timeDimensionUnit) {
                        return {
                            type: 'time',
                            time: timeDimensionUnit,
                            ticks,
                        };
                    }
                    else {
                        return {
                            ticks,
                        };
                    }
                });
            }
            for (const m of r.metrics || []) {
                if (m.legendPosition) {
                    options.legend.position = m.legendPosition;
                    break;
                }
            }
            options.scales.yAxes = this.makeYAxis(r);
        });
        return options;
    }
    makeTooltip({ datasetIndex, index }, { datasets, labels }) {
        const dataset = datasets[datasetIndex];
        const percentages = calculatePercentages([...dataset.data], dataset.tooltips.relativePrecision, dataset.tooltips.relativeValue);
        return makeDataLabel({
            prefix: labels[index],
            value: percentages[index],
            relativeValue: dataset.tooltips.relativeValue,
        });
    }
    makeLabel({ datasetIndex, index }, { datasets, labels }) {
        const dataset = datasets[datasetIndex];
        const percentages = calculatePercentages([...dataset.data], dataset.tooltips.relativePrecision, dataset.tooltips.relativeValue);
        return makeDataLabel({
            value: percentages[index],
            relativeValue: dataset.tooltips.relativeValue,
        });
    }
    makeYAxis(r) {
        var _a, _b;
        if (r.yAxis) {
            return [{
                    display: !((_a = r.metrics) === null || _a === void 0 ? void 0 : _a.find((m) => isRadialChart(m))),
                    type: r.yAxis.axisType || 'linear',
                    position: r.yAxis.axisPosition || 'left',
                    scaleLabel: {
                        display: true,
                        labelString: r.yAxis.label || undefined,
                    },
                    ticks: {
                        beginAtZero: !!r.yAxis.beginAtZero,
                        min: r.yAxis.min ? parseFloat(r.yAxis.min) : undefined,
                        max: r.yAxis.max ? parseFloat(r.yAxis.max) : undefined,
                    },
                }];
        }
        else {
            const m = ((_b = r.metrics) === null || _b === void 0 ? void 0 : _b[0]) || {};
            return [{
                    display: !isRadialChart(m),
                    type: m.axisType || 'linear',
                    position: m.axisPosition || 'left',
                    scaleLabel: {
                        display: true,
                        labelString: m.label || m.field,
                    },
                    ticks: {
                        beginAtZero: !!m.beginAtZero,
                    },
                }];
        }
    }
    plugins() {
        const mm = [];
        for (const r of (this.config.reports || [])) {
            mm.push(...(r.metrics || []));
        }
        const rr = [];
        if (mm.find(({ fixTooltips }) => fixTooltips)) {
            rr.push(makeTipper(ChartJS$1.Tooltip, {}));
        }
        return rr;
    }
    baseChartType(datasets) {
        return datasets[0].type;
    }
}

const defaultBGColor = 'rgba(165, 165, 165, 1)';

const ChartJS$2 = require('chart.js');
/**
 * Funnel chart provides the definitions for the chartjs-plugin-funnel plugin.
 */
class FunnelChart extends BaseChart {
    constructor(def = {}) {
        super(def);
        // Assure required fields; this helps with backwards compatibility
        for (const v of (this.config.reports || [])) {
            for (const d of (v.dimensions || [])) {
                if (!d.meta) {
                    d.meta = {};
                }
                if (!d.meta.fields) {
                    d.meta.fields = [];
                }
            }
        }
    }
    /**
     * Since funnel charts always define one type, this check can be simplified
     */
    mtrCheck({ field, aggregate }) {
        if (!field) {
            throw new Error('notification.chart.invalidConfig.missingMetricsField');
        }
        if (field !== 'count' && !aggregate) {
            throw new Error('notification.chart.invalidConfig.missingMetricsAggregate');
        }
    }
    /**
     * Extend this method to include filtering for just specific values.
     * For example:
     * We wish to show only new and converted leads.
     */
    formatReporterParams(r) {
        const base = super.formatReporterParams(r);
        const ff = base.filter;
        let df = '';
        if (r.dimensions && r.dimensions[0]) {
            const rd = r.dimensions[0];
            if (r.dimensions[0].meta) {
                const fields = r.dimensions[0].meta.fields || [];
                df = fields.map(({ value }) => `${rd.field || ''}='${value}'`)
                    .join(' OR ');
            }
        }
        if (ff && df) {
            base.filter = `(${base.filter}) AND (${df})`;
        }
        else if (!ff && df) {
            base.filter = df;
        }
        return base;
    }
    // Funnel chart creates a metric including all reports, so this step is deferred to there
    makeDataset(m, d, data, alias) {
        const ds = { data };
        return ds;
    }
    // No much configurations available for funnel charts
    makeOptions(data) {
        const options = {
            sort: 'desc',
            maintainAspectRatio: false
        };
        if (this.config.colorScheme) {
            options.plugins = {
                colorschemes: {
                    scheme: this.config.colorScheme,
                    // this is a bit of a hack to make the plugin work on each dataset value
                    // we should improve this at a later point in time, but is ok for now.
                    custom: (e) => {
                        const cls = [...e];
                        while (cls.length < data.datasets[0].data.length) {
                            cls.push(...e);
                        }
                        data.datasets[0].backgroundColor = cls.slice(0, data.datasets[0].data.length);
                        return e;
                    },
                },
            };
        }
        options.tooltips = {
            enabled: true,
            displayColors: false,
            callbacks: {
                label: this.makeLabel,
            },
        };
        return options;
    }
    makeLabel({ datasetIndex, index }, { datasets, labels }) {
        const dataset = datasets[datasetIndex];
        const percentages = calculatePercentages([...dataset.data], 0, false);
        return makeDataLabel({
            prefix: labels[index],
            value: percentages[index],
            relativeValue: dataset.tooltips.relativeValue,
        });
    }
    /**
     * @note Funel chart requires the use of chartjs-plugin-funnel.
     * I was unable to make this work if the plugin was provided from this object,
     * so the plugin is registered on the webapp.
     * We should fix this at a later point in time...
     */
    plugins(mm) {
        return [makeTipper(ChartJS$2.Tooltip, {})];
    }
    baseChartType(datasets) {
        return 'funnel';
    }
    /**
     * Includes a few additional post processing steps:
     * * generate a set of labels based on all reports, all data sets,
     * * generates a set of data based on all reports, all data sets,
     * * creates a sort of a commutative chart
     * ** if a record is in the second stage, it's safe to assume that it has
     *    already passed the first stage.
     */
    fetchReports(a) {
        const _super = Object.create(null, {
            fetchReports: { get: () => super.fetchReports }
        });
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            let labels = [];
            let data = [];
            const rr = yield _super.fetchReports.call(this, a);
            // Above provided data sets might not have their labels/values ordered
            // correctly
            const valMap = {};
            // Map values to their labels
            for (let ri = 0; ri < rr.length; ri++) {
                const r = rr[ri];
                r.labels.forEach((l, i) => {
                    valMap[l] = r.datasets[0].data[i];
                });
                // Construct labels & data based on provided reports
                const report = (_a = this.config.reports) === null || _a === void 0 ? void 0 : _a[ri];
                const d = (_c = (_b = report) === null || _b === void 0 ? void 0 : _b.dimensions) === null || _c === void 0 ? void 0 : _c[0];
                for (const { value } of ((_d = d.meta) === null || _d === void 0 ? void 0 : _d.fields) || []) {
                    labels.push(value);
                    data.push(valMap[value] || 0);
                }
            }
            // We are rendering the chart upside down
            // (by default it renders in ASC, but we want DESC)
            labels = labels.reverse();
            data = data.reverse();
            // Determine color to render for specific value
            const colorMap = {};
            (_e = this.config.reports) === null || _e === void 0 ? void 0 : _e.forEach(r => {
                var _a, _b;
                for (const { value, color } of (_b = (_a = r.dimensions) === null || _a === void 0 ? void 0 : _a[0].meta) === null || _b === void 0 ? void 0 : _b.fields) {
                    colorMap[value] = color;
                }
            });
            // Create a commutative (see method comment)
            for (let i = 1; i < data.length; i++) {
                data[i] += data[i - 1];
            }
            return {
                labels,
                datasets: [{
                        data,
                        backgroundColor: labels.map(l => colorMap[l] || defaultBGColor),
                    }],
            };
        });
    }
    defMetrics() {
        return Object.assign({}, { type: ChartType.funnel });
    }
    defDimension() {
        return Object.assign({}, { conditions: {}, meta: { fields: [] } });
    }
}

const ChartJS$3 = require('chart.js');
/**
 * Gauge chart provides the definitions for the chartjs-plugin-funnel plugin.
 */
class GaugeChart extends BaseChart {
    constructor(def = {}) {
        super(def);
        // Assure required fields
        for (const v of (this.config.reports || [])) {
            for (const d of (v.dimensions || [])) {
                if (!d.meta) {
                    d.meta = {};
                }
                if (!d.meta.steps) {
                    d.meta.steps = [];
                }
            }
        }
    }
    /**
     * Since gauge charts always define one type, this check can be simplified
     */
    mtrCheck({ field, aggregate }) {
        if (!field) {
            throw new Error('notification.chart.invalidConfig.missingMetricsField');
        }
        if (field !== 'count' && !aggregate) {
            throw new Error('notification.chart.invalidConfig.missingMetricsAggregate');
        }
    }
    // Gauge charts (at the moment) support only 1 report per chart
    fetchReports(a) {
        const _super = Object.create(null, {
            fetchReports: { get: () => super.fetchReports }
        });
        return __awaiter(this, void 0, void 0, function* () {
            return _super.fetchReports.call(this, a).then((rr) => {
                return rr[0];
            });
        });
    }
    processLabels(ll, d) {
        var _a;
        return (((_a = d.meta) === null || _a === void 0 ? void 0 : _a.steps) || []).map(({ label }) => label);
    }
    makeDataset(m, d, data, alias) {
        var _a;
        const steps = (((_a = d.meta) === null || _a === void 0 ? void 0 : _a.steps) || []);
        return {
            value: data.reduce((acc, cur) => acc + cur, 0),
            data: steps.map(({ value }) => parseFloat(value)),
            backgroundColor: steps.map(({ color }) => color),
            tooltips: {
                enabled: true,
                labelCallback: m.fixTooltips ? this.makeLabel : this.makeTooltip,
            },
        };
    }
    makeOptions(data) {
        var _a;
        const rep = (_a = this.config.reports) === null || _a === void 0 ? void 0 : _a[0];
        const { metrics: [metric] = [] } = rep || {};
        const options = {
            needle: {
                radiusPercentage: 2,
                widthPercentage: 3.5,
                lengthPercentage: 70,
                color: metric.backgroundColor || defaultBGColor,
            },
            tooltips: {
                enabled: true,
                displayColors: false,
                callbacks: {
                    title: () => '',
                    label: ({ datasetIndex, index }, { datasets, labels }) => {
                        const dataset = datasets[datasetIndex];
                        return dataset.tooltips.labelCallback({ datasetIndex, index }, { datasets, labels });
                    },
                },
            },
            valueLabel: {
                display: true,
                color: 'rgba(0, 0, 0, 1)',
                backgroundColor: 'rgba(255,255,255,0.5)',
                borderRadius: 5,
                bottomMarginPercentage: 5,
                padding: {
                    top: 10,
                    bottom: 10,
                },
            },
        };
        if (this.config.colorScheme) {
            options.plugins = {
                colorschemes: {
                    scheme: this.config.colorScheme,
                    // this is a bit of a hack to make the plugin work on each dataset value
                    // we should improve this at a later point in time, but is ok for now.
                    custom: (e) => {
                        const cls = [...e];
                        while (cls.length < data.datasets[0].data.length) {
                            cls.push(...e);
                        }
                        data.datasets[0].backgroundColor = cls.slice(0, data.datasets[0].data.length);
                        return e;
                    },
                },
            };
        }
        return options;
    }
    makeTooltip({ datasetIndex, index }, { datasets, labels }) {
        const dataset = datasets[datasetIndex];
        const percentages = calculatePercentages([...dataset.data], dataset.tooltips.relativePrecision, dataset.tooltips.relativeValue);
        return makeDataLabel({
            prefix: labels[index],
            value: percentages[index],
            relativeValue: dataset.tooltips.relativeValue,
        });
    }
    makeLabel({ datasetIndex, index }, { datasets, labels }) {
        const dataset = datasets[datasetIndex];
        const percentages = calculatePercentages([...dataset.data], dataset.tooltips.relativePrecision, dataset.tooltips.relativeValue);
        return makeDataLabel({
            value: percentages[index],
            relativeValue: dataset.tooltips.relativeValue,
        });
    }
    /**
     * @note Gauge chart requires the use of chartjs-gauge.
     * I was unable to make this work if the plugin was provided from this object,
     * so the plugin is registered on the webapp.
     * We should fix this at a later point in time...
     */
    plugins() {
        const mm = [];
        for (const r of (this.config.reports || [])) {
            mm.push(...(r.metrics || []));
        }
        const rr = [];
        if (mm.find(({ fixTooltips }) => fixTooltips)) {
            rr.push(makeTipper(ChartJS$3.Tooltip, {}));
        }
        return rr;
    }
    baseChartType(datasets) {
        return 'gauge';
    }
    defMetrics() {
        return Object.assign({}, { type: ChartType.gauge });
    }
}

function namespaceMatcher(r, c, def) {
    if (!r) {
        throw new Error('can not run namespace matcher on undefined/null namespace');
    }
    // keep in sync with server/compose/service/event/namespace.go
    switch (c.Name()) {
        case 'namespace':
        case 'namespace.slug':
            return c.Match(r.slug);
        case 'namespace.name':
            return c.Match(r.name);
    }
    return def;
}
function moduleMatcher(r, c, def) {
    if (!r) {
        throw new Error('can not run module matcher on undefined/null module');
    }
    // keep in sync with server/compose/service/event/module.go
    switch (c.Name()) {
        case 'module':
        case 'module.handle':
            return c.Match(r.handle);
        case 'module.name':
            return c.Match(r.name);
    }
    return def;
}
/**
 * Creates event for compose resource with ready-to-go-defaults
 */
function ComposeEvent(event) {
    return Object.assign({ eventType: onManual, resourceType: 'compose', match: () => true }, event);
}
/**
 * Creates namespace event with ready-to-go-defaults
 */
function NamespaceEvent(res, event) {
    var _a;
    return Object.assign(Object.assign({ eventType: onManual, resourceType: res.resourceType, match: (c) => namespaceMatcher(res, c, false) }, event), { 
        // Override the arguments at the end
        args: Object.assign({ namespace: res }, (_a = event) === null || _a === void 0 ? void 0 : _a.args) });
}
/**
 * Creates module event with ready-to-go-defaults
 */
function ModuleEvent(res, event) {
    var _a;
    return Object.assign(Object.assign({ eventType: onManual, resourceType: res.resourceType, match: (c) => namespaceMatcher(res.namespace, c, moduleMatcher(res, c, false)) }, event), { 
        // Override the arguments at the end
        args: Object.assign({ module: res, namespace: res.namespace }, (_a = event) === null || _a === void 0 ? void 0 : _a.args) });
}
/**
 * Creates record event with ready-to-go-defaults
 */
function RecordEvent(res, event) {
    var _a;
    return Object.assign(Object.assign({ eventType: onManual, resourceType: res.resourceType, match: (c) => namespaceMatcher(res.namespace, c, moduleMatcher(res.module, c, false)) }, event), { 
        // Override the arguments at the end
        args: Object.assign({ record: res, module: res.module, namespace: res.namespace }, (_a = event) === null || _a === void 0 ? void 0 : _a.args) });
}
/**
 * Returns handler that routes onManual events for server script to the compose API
 *
 * See makeAutomationScriptsRegistrator
 *
 * @param api
 * @return function
 */
function TriggerComposeServerScriptOnManual(api) {
    return (ev, script) => {
        const params = { script, args: ev.args };
        if (ev.resourceType === 'compose') {
            return api
                .automationTriggerScript(Object.assign({}, params));
        }
        if (!ev.args) {
            throw new Error('expecting args prop in event');
        }
        if (ev.resourceType === 'compose:namespace') {
            if (!IsOf(ev.args.namespace, 'namespaceID')) {
                throw new Error('expecting args.namespace in event arguments');
            }
            const { namespaceID } = ev.args.namespace;
            return api
                .namespaceTriggerScript(Object.assign({ namespaceID }, params));
        }
        if (ev.resourceType === 'compose:module') {
            if (!IsOf(ev.args.module, 'namespaceID', 'moduleID')) {
                throw new Error('expecting args.module in event arguments');
            }
            const { namespaceID, moduleID } = ev.args.module;
            return api
                .moduleTriggerScript(Object.assign({ namespaceID, moduleID }, params));
        }
        if (ev.resourceType === 'compose:record') {
            if (!IsOf(ev.args.record, 'namespaceID', 'moduleID', 'recordID')) {
                throw new Error('expecting args.record in event arguments');
            }
            const record = ev.args.record;
            const { namespaceID, moduleID, recordID, values } = record;
            return api
                .recordTriggerScript(Object.assign({ namespaceID, moduleID, recordID, values }, params))
                .then(rval => record.apply(rval));
        }
        throw Error(`cannot trigger server script: unknown resource type '${ev.resourceType}'`);
    };
}



var index$5 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Record: Record,
  Module: Module,
  Namespace: Namespace,
  Page: Page,
  RecordValidator: RecordValidator,
  getModuleFromYaml: getModuleFromYaml,
  ComposeEvent: ComposeEvent,
  NamespaceEvent: NamespaceEvent,
  ModuleEvent: ModuleEvent,
  RecordEvent: RecordEvent,
  TriggerComposeServerScriptOnManual: TriggerComposeServerScriptOnManual,
  ModuleFieldMaker: ModuleFieldMaker,
  ModuleFieldRegistry: Registry$1,
  ModuleField: ModuleField,
  ModuleFieldBool: ModuleFieldBool,
  ModuleFieldDateTime: ModuleFieldDateTime,
  ModuleFieldEmail: ModuleFieldEmail,
  ModuleFieldFile: ModuleFieldFile,
  ModuleFieldSelect: ModuleFieldSelect,
  ModuleFieldNumber: ModuleFieldNumber,
  ModuleFieldRecord: ModuleFieldRecord,
  ModuleFieldString: ModuleFieldString,
  ModuleFieldUrl: ModuleFieldUrl,
  ModuleFieldUser: ModuleFieldUser,
  PageBlockMaker: PageBlockMaker,
  PageBlockRegistry: Registry$2,
  PageBlock: PageBlock,
  PageBlockAutomation: PageBlockAutomation,
  PageBlockChart: PageBlockChart,
  PageBlockContent: PageBlockContent,
  PageBlockFile: PageBlockFile,
  PageBlockIFrame: PageBlockIFrame,
  PageBlockRecord: PageBlockRecord,
  PageBlockRecordList: PageBlockRecordList,
  PageBlockRecordOrganizer: PageBlockRecordOrganizer,
  PageBlockSocialFeed: PageBlockSocialFeed,
  PageBlockCalendar: PageBlockCalendar,
  PageBlockMetric: PageBlockMetric,
  PageBlockComment: PageBlockComment,
  chartUtil: chartUtil,
  Chart: Chart,
  FunnelChart: FunnelChart,
  GaugeChart: GaugeChart
});

const emailStyle = `
body { -ms-text-size-adjust: 100%; -webkit-text-size-adjust: 100%; color: #3A393C; font-family: Verdana,Arial,sans-serif; font-size: 14px; height: 100%; margin: 0; padding: 0; width: 100% !important; }
table { margin: 20px auto; background: #ffffff; border-collapse: collapse; max-width: 100%; }
table tr { height: 40px; }
table td { padding-top: 10px; padding-left: 20px; width:100%; max-width:100%; min-width:100%; width:100%; vertical-align: top; }
table tbody { border-top: 3px solid #808080; }
tbody tr:nth-child(even) { background-color: #F3F3F4; }
tbody td:first-child { width: 30%; color: #808080; }
tbody td:nth-child(2) { width: 70%; }
h2, p { padding: 10px 20px; }
p { text-align: justify; line-height: 1.4;}
`;
/**
 * Helpers to determine if specific object looks like the type we are interested in.
 * It does not rely on instanceof, because of bundling issues.
 */
function isRecord(o) {
    return o && !!o.recordID && o.moduleID && o.namespaceID;
}
function isModule$1(o) {
    return o && !!o.moduleID && o.namespaceID;
}
function isPage(o) {
    return o && !!o.pageID && o.namespaceID;
}
/**
 * ComposeHelper provides layer over Compose API and utilities that simplify automation script writing
 *
 * Initiated as Compose object and provides a few handy shortcuts and fallback that will enable you
 * to rapidly develop your automation scripts.
 */
class ComposeHelper {
    /**
     * @param ctx.$namespace - Current namespace
     * @param ctx.$module - Current module
     * @param ctx.$record - Current record
     */
    constructor(ctx) {
        this.ComposeAPI = ctx.ComposeAPI;
        this.$namespace = ctx.$namespace;
        this.$module = ctx.$module;
        this.$record = ctx.$record;
    }
    /**
     * Creates new Page object
     *
     * <p>
     *   Created page is "in-memory" only. To store it, use savePage() method
     * </p>
     *
     * @example
     * // Simple page creation new page on current namespace
     * let myPage = await Compose.makePage({ title: 'My Amazing Page!' })
     *
     * @param values
     * @param ns - defaults to current $namespace
     */
    makePage(values = {}, ns = this.$namespace) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.resolveNamespace(ns).then(ns => {
                return new Page(Object.assign(Object.assign({}, values), { namespaceID: ns.namespaceID }));
            });
        });
    }
    /**
     * Creates/updates Page
     *
     * @param page
     */
    savePage(page) {
        return __awaiter(this, void 0, void 0, function* () {
            return Promise.resolve(page).then(page => {
                if (!isPage(page)) {
                    throw Error('expecting Page type');
                }
                if (page.pageID && isFresh(page.pageID)) {
                    return this.ComposeAPI.pageCreate(kv(page)).then(page => new Page(page));
                }
                else {
                    return this.ComposeAPI.pageUpdate(kv(page)).then(page => new Page(page));
                }
            });
        });
    }
    /**
     * Deletes a page
     *
     * @example
     * Compose.deletePage(myPage)
     *
     * @param page
     */
    deletePage(page) {
        return __awaiter(this, void 0, void 0, function* () {
            return Promise.resolve(page).then(page => {
                if (!isPage(page)) {
                    throw Error('expecting Page type');
                }
                if (!isFresh(page.pageID)) {
                    return this.ComposeAPI.pageDelete(kv(page));
                }
            });
        });
    }
    /**
     * Searches for pages
     *
     * @private
     * @param filter
     * @param ns
     */
    findPages(filter = {}, ns = this.$namespace) {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof filter === 'string') {
                filter = { query: filter };
            }
            return this.resolveNamespace(ns).then(ns => {
                const namespaceID = extractID(ns, 'namespaceID');
                return this.ComposeAPI.pageList(Object.assign({ namespaceID }, filter)).then(res => {
                    // Casting all we got to to Page
                    res.set = res.set.map(m => new Page(m));
                    return res;
                });
            });
        });
    }
    /**
     * Finds page by ID
     *
     * @example
     * // Explicitly load page and do something with it
     * Compose.finePageByID('2039248239042').then(myPage => {
     *   // do something with myPage
     *   myPage.title = 'My More Amazing Page!'
     *   return myPage
     * }).then(Compose.savePage)
     *
     * @param page - accepts Page, pageID (when string string)
     * @param ns - namespace, defaults to current $namespace
     */
    findPageByID(page, ns = this.$namespace) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.resolveNamespace(ns).then((ns) => {
                const pageID = extractID(page, 'pageID');
                const namespaceID = extractID(ns, 'namespaceID');
                return this.ComposeAPI.pageRead({ namespaceID, pageID }).then(page => new Page(page));
            });
        });
    }
    /**
     * Creates new Record object
     *
     * <p>
     *   Created record is "in-memory" only. To store it, use saveRecord() method
     * </p>
     *
     * @example
     * // Simple record creation (new record of current module - $module)
     * let myLead = await Compose.makeRecord()
     * myLead.values.Title = 'My Lead Title'
     *
     * // Create record of type Lead and copy values from another Record
     * // This will copy only values that have the same name in both modules
     * let myLead = await Compose.makeRecord(myContact, 'Lead')
     *
     * // Or use promises:
     * Compose.makeRecord(myContact, 'Lead').then(myLead => {
     *   myLead.values.Title = 'My Lead Title'
     *
     *   // ...
     *
     *   // return record when finished
     *   return myLead
     * }).catch(err => {
     *   // solve the problem
     *   console.error(err)
     * })
     *
     * @param values
     * @param module - defaults to current $module
     */
    makeRecord(values = {}, module = null) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.resolveModule(module, this.$module).then(module => {
                const record = new Record(module);
                // Set record values
                record.setValues(values);
                return record;
            });
        });
    }
    /**
     * Saves a record
     *
     * Please note that there is no need to explicitly save (current record) on before/after events,
     * internal systems take care of that.
     *
     * @example
     * // Quick example how to make and save new Lead:
     * let mySavedLead = await Compose.saveRecord(Compose.makeRecord({Title: 'Lead title'}, 'Lead'))
     * if (mySavedLead) {
     *   console.log('Record saved, new ID', mySavedLead.recordID)
     * } else {
     *   // solve the problem
     *   console.error(err)
     * }
     *
     * // Or with promises:
     * Compose.makeRecord({Title: 'Lead title'}, 'Lead')).then(myLead => {
     *   return Compose.saveRecord(myLead)
     * }).then(mySavedLead => {
     *   console.log('Record saved, new ID', mySavedLead.recordID)
     * }).catch(err => {
     *   // solve the problem
     *   console.error(err)
     * })
     *
     * @param record
     */
    saveRecord(record) {
        return __awaiter(this, void 0, void 0, function* () {
            return Promise.resolve(record).then(record => {
                if (!isRecord(record)) {
                    throw Error('expecting Record type');
                }
                if (isFresh(record.recordID)) {
                    return this.ComposeAPI.recordCreate(kv(record)).then(r => new Record(record.module, r));
                }
                else {
                    return this.ComposeAPI.recordUpdate(kv(record)).then(r => new Record(record.module, r));
                }
            });
        });
    }
    /**
     * Deletes a record
     *
     * Please note that there is no need to explicitly delete (current record) on before/after events.
     *
     * @example
     * Compose.deleteRecord(myLead)
     *
     * @param record
     */
    deleteRecord(record) {
        return __awaiter(this, void 0, void 0, function* () {
            return Promise.resolve(record).then(record => {
                if (!isRecord(record)) {
                    throw Error('expecting Record type');
                }
                if (!isFresh(record.recordID)) {
                    return this.ComposeAPI.recordDelete(kv(record));
                }
            });
        });
    }
    /**
     * Searches for records of a specific record
     *
     * @example
     * // Find all records (of the current module)
     * Compose.findRecords()
     *
     * // Find Projects where ROI is more than 15%
     * // (assuming we have Project module with netProfit and totalInvestment numeric fields)
     * Compose.findRecords('netProfit / totalInvestment > 0.15', 'Project')
     *
     * // Find Projects where ROI is more than 15%
     * // (assuming we have Project module with netProfit and totalInvestment numeric fields)
     * Compose.findRecords('netProfit / totalInvestment > 0.15', 'Project')
     *
     * // More complex query with sorting:
     * // Returns top 5 Projects with more than 15% ROI in the last year
     * Compose.findRecords({
     *   filter: '(netProfit / totalInvestment > 0.15) AND (YEAR(createdAt) = YEAR(NOW()) - 1)'
     *   sort: 'netProfit / totalInvestment DESC',
     *   limit: 5,
     * }, 'Project')
     *
     * // Accessing returned records
     * Compose.findRecords().then(({ set, filter }) => {
     *    // set: array of records
     *    // filter: object with filter specs
     *
     *    Use internal Array functions
     *    set.forEach(r => {
     *      // r, one of the records each iteration
     *    })
     *
     *    // Or standard for-loop
     *    for (let r of set) {
     *       // r...
     *    }
     * })
     *
     * @param filter - filter object (or filtering conditions when string)
     * @property {string} filter.query - filtering conditions
     * @property {string} filter.sort - sorting rules
     * @property {number} filter.limit - number of max returned records
     * @property {number} filter.pageCursor - hashed string that retrieves a specific page
     * @param [module] - if not set, defaults to $module
     */
    findRecords(filter = '', module = this.$module) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.resolveModule(module).then(module => {
                const { moduleID, namespaceID } = module;
                let params = {
                    moduleID,
                    namespaceID,
                };
                if (typeof filter === 'string') {
                    params.query = filter;
                }
                else if (typeof filter === 'object') {
                    params = Object.assign(Object.assign({}, params), filter);
                }
                return this.ComposeAPI.recordList(params).then(res => {
                    // Casting all we got to to Record
                    res.set = res.set.map(record => new Record(module, record));
                    return res;
                });
            });
        });
    }
    /**
     * Finds last (created) record in the module
     *
     * @example
     * Compose.findLastRecord('Settings').then(lastSettingRecord => {
     *   // handle lastSettingRecord
     * })
     *
     * @param module
     */
    findLastRecord(module = this.$module) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.findRecords({ sort: 'createdAt DESC', limit: 1 }, module).then(res => {
                if (!Array.isArray(res.set) || res.set.length === 0) {
                    throw new Error('records not found');
                }
                return res.set[0];
            });
        });
    }
    /**
     * Finds first (created) record in the module
     *
     * @example
     * Compose.findFirstRecord('Settings').then(firstSettingRecord => {
     *   // handle this firstSettingRecord
     * })
     *
     * @param module
     */
    findFirstRecord(module = this.$module) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.findRecords({ sort: 'createdAt', limit: 1 }, module).then(res => {
                if (!Array.isArray(res.set) || res.set.length === 0) {
                    throw new Error('records not found');
                }
                return res.set[0];
            });
        });
    }
    /**
     * Finds one record by ID
     *
     * @example
     * Compose.findRecordByID("23957823957").then(specificRecord => {
     *   // handle this specificRecord
     * })
     *
     * @param record
     * @param module
     */
    findRecordByID(record, module = null) {
        return __awaiter(this, void 0, void 0, function* () {
            // We're handling module default a bit differently here
            // because we want to allow users to use record's module
            return this.resolveModule(module, (record || {}).module, this.$module).then((module) => {
                const { moduleID, namespaceID } = module;
                return this.ComposeAPI.recordRead({
                    moduleID,
                    namespaceID,
                    recordID: extractID(record, 'recordID'),
                }).then(r => new Record(module, r));
            });
        });
    }
    /**
     * Finds a single attachment
     *
     * @param attachment Attachment to find
     * @param ns
     */
    findAttachmentByID(attachment, ns = this.$namespace) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.resolveNamespace(ns).then(namespace => {
                const { namespaceID } = namespace;
                return this.ComposeAPI.attachmentRead({
                    kind: 'original',
                    attachmentID: extractID(attachment, 'attachmentID'),
                    namespaceID,
                }).then(att => new Attachment(att));
            });
        });
    }
    /**
     * Helper to determine field's name from it's label
     * @param label Field's label
     */
    moduleFieldNameFromLabel(label) {
        return label.split(/[^a-zA-Z0-9_]/g)
            .filter(p => !!p)
            .map(p => `${p[0].toUpperCase()}${p.slice(1)}`)
            .join('');
    }
    /**
     * Creates new Module object
     *
     * @param module
     * @param ns, defaults to current $namespace
     */
    makeModule(module = {}, ns = this.$namespace) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.resolveNamespace(ns).then((ns) => {
                return new Module(Object.assign(Object.assign({}, module), { namespaceID: ns.namespaceID }));
            });
        });
    }
    /**
     * Creates/updates Module
     *
     * @param module
     */
    saveModule(module) {
        return __awaiter(this, void 0, void 0, function* () {
            return Promise.resolve(module).then(module => {
                if (!isModule$1(module)) {
                    throw new Error('expecting Module type');
                }
                if (isFresh(module.moduleID)) {
                    return this.ComposeAPI.moduleCreate(kv(module)).then(m => new Module(m));
                }
                else {
                    return this.ComposeAPI.moduleUpdate(kv(module)).then(m => new Module(m));
                }
            });
        });
    }
    /**
     * Searches for modules
     *
     * @private
     * @param filter
     * @param ns
     */
    findModules(filter = '', ns = this.$namespace) {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof filter === 'string') {
                filter = { query: filter };
            }
            return this.resolveNamespace(ns).then((ns) => {
                const namespaceID = extractID(ns, 'namespaceID');
                return this.ComposeAPI.moduleList(Object.assign({ namespaceID }, filter)).then(res => {
                    // Casting all we got to to Module
                    res.set = res.set.map(m => new Module(m));
                    return res;
                });
            });
        });
    }
    /**
     * Finds module by ID
     *
     * @example
     * // Explicitly load module and do something with it
     * Compose.findModuleByID('2039248239042').then(myModule => {
     *   // do something with myModule
     *   return Compose.findLastRecord(myModule)
     * }).then((lastRecord) => {})
     *
     * // or
     * Compose.findLastRecord(Compose.findModuleByID('2039248239042')).then(....)
     *
     * // even shorter
     * Compose.findLastRecord('2039248239042').then(....)
     *
     * @param module - accepts Module, moduleID (when string) or Record
     * @param ns - namespace, defaults to current $namespace
     */
    findModuleByID(module, ns = this.$namespace) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.resolveNamespace(ns).then((ns) => {
                const moduleID = extractID(module, 'moduleID');
                const namespaceID = extractID(ns, 'namespaceID');
                return this.ComposeAPI.moduleRead({ namespaceID, moduleID }).then(m => new Module(m));
            });
        });
    }
    /**
     * Finds module by name
     *
     * @example
     * // Explicitly load module and do something with it
     * Compose.findModuleByName('SomeModule').then(myModule => {
     *   // do something with myModule
     *   return Compose.findLastRecord(myModule)
     * }).then((lastRecord) => {})
     *
     * // or
     * Compose.findLastRecord(Compose.findModuleByName('SomeModule')).then(....)
     *
     * // even shorter
     * Compose.findLastRecord('SomeModule').then(....)
     *
     * @param name - name of the module
     * @param ns - defaults to current $namespace
     */
    findModuleByName(name, ns = this.$namespace) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.resolveNamespace(ns).then((ns) => {
                const namespaceID = extractID(ns, 'namespaceID');
                return this.ComposeAPI.moduleList({ namespaceID, name }).then(res => {
                    if (!Array.isArray(res.set) || res.set.length === 0) {
                        throw new Error('module not found');
                    }
                    return new Module(res.set[0]);
                });
            });
        });
    }
    /**
     * Finds module by handle
     *
     * @example
     * // Explicitly load module and do something with it
     * Compose.findModuleByHandle('SomeModule').then(myModule => {
     *   // do something with myModule
     *   return Compose.findLastRecord(myModule)
     * }).then((lastRecord) => {})
     *
     * // or
     * Compose.findLastRecord(Compose.findModuleByHandle('SomeModule')).then(....)
     *
     * // even shorter
     * Compose.findLastRecord('SomeModule').then(....)
     *
     * @param handle - handle of the module
     * @param ns - defaults to current $namespace
     */
    findModuleByHandle(handle, ns = this.$namespace) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.resolveNamespace(ns).then((ns) => {
                const namespaceID = extractID(ns, 'namespaceID');
                return this.ComposeAPI.moduleList({ namespaceID, handle }).then(res => {
                    if (!Array.isArray(res.set) || res.set.length === 0) {
                        throw new Error('module not found');
                    }
                    return new Module(res.set[0]);
                });
            });
        });
    }
    /**
     * Creates new Namespace object
     *
     * @example
     * // Creates enabled (!) namespace with slug & name
     * Compose.saveNamespace(Compose.makeNamespace({
     *   slug: 'my-namespace',
     *   name: 'My Namespace',
     * }))
     *
     * @param namespace
     * @param namespace, defaults to current $namespace
     */
    makeNamespace(namespace = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Namespace(Object.assign({ name: namespace.name || namespace.slug, meta: {}, enabled: true }, namespace));
        });
    }
    /**
     * Creates/updates Namespace
     *
     * @example
     * Compose.saveNamespace(myNamespace)
     *
     * @param namespace
     */
    saveNamespace(namespace) {
        return __awaiter(this, void 0, void 0, function* () {
            return Promise.resolve(namespace).then(namespace => {
                if (!(namespace instanceof Namespace)) {
                    throw Error('expecting Namespace type');
                }
                if (isFresh(namespace.namespaceID)) {
                    return this.ComposeAPI.namespaceCreate(kv(namespace)).then(n => new Namespace(n));
                }
                else {
                    return this.ComposeAPI.namespaceUpdate(kv(namespace)).then(n => new Namespace(n));
                }
            });
        });
    }
    /**
     * Searches for namespaces
     *
     * @private
     * @param filter
     */
    findNamespaces(filter = '') {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof filter === 'string') {
                filter = { query: filter };
            }
            return this.ComposeAPI.namespaceList(Object.assign({}, filter)).then(res => {
                // Casting all we got to to Namespace
                res.set = res.set.map(m => new Namespace(m));
                return res;
            });
        });
    }
    /**
     * Finds namespace by ID
     *
     * @example
     * // Explicitly load namespace and do something with it
     * Compose.findNamespaceByID('2039248239042').then(myNamespace => {
     *   // do something with myNamespace
     *   return Compose.findModules(myNamespace)
     * }).then(modules => {})
     *
     * // even shorter
     * Compose.findModules('2039248239042').then(....)
     *
     * @param ns - accepts Namespace, namespaceID (when string string) or Record
     */
    findNamespaceByID(ns = this.$namespace) {
        return __awaiter(this, void 0, void 0, function* () {
            const namespaceID = extractID(ns, 'namespaceID');
            return this.ComposeAPI.namespaceRead({ namespaceID }).then(m => new Namespace(m));
        });
    }
    /**
     * Finds namespace by name
     *
     * @example
     * // Explicitly load namespace and do something with it
     * Compose.findNamespaceBySlug('SomeNamespace').then(myNamespace => {
     *   // do something with myNamespace
     *   return Compose.findModules(myNamespace)
     * }).then(modules => {})
     *
     * // even shorter
     * Compose.findModules('SomeNamespace').then(....)
     *
     * @param slug - name of the namespace
     */
    findNamespaceBySlug(slug) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.ComposeAPI.namespaceList({ slug }).then(res => {
                if (!Array.isArray(res.set) || res.set.length === 0) {
                    throw new Error('namespace not found');
                }
                return new Namespace(res.set[0]);
            });
        });
    }
    /**
     * Sends a simple email message
     *
     * @example
     * Compose.sendMail('some-address@domain.tld', 'subject...', { html: 'Hello!' })
     *
     * @param to - Recipient(s)
     * @param subject - Mail subject
     * @param body
     * @property {string} body.html - HTML body to be sent
     * @param Any additional addresses we want this to be sent to (carbon-copy)
     */
    sendMail(to, subject, { html = '' } = {}, { cc = [] } = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!to) {
                throw Error('expecting to email address');
            }
            if (!subject) {
                throw Error('expecting subject');
            }
            if (!html) {
                throw Error('expecting HTML body');
            }
            return this.ComposeAPI.notificationEmailSend({
                to: Array.isArray(to) ? to : [to],
                cc: Array.isArray(cc) ? cc : [cc],
                subject,
                content: { html },
            });
        });
    }
    /**
     * Generates HTML with all records fields and sends it to
     *
     * @example
     * // Simplified version, sends current email with generated
     * // subject (<module name> + 'record' +  'update'/'created')
     * Compose.sendRecordToMail('example@domain.tld')
     *
     * // Complex notification with custom subject, header and footer text and custom record
     * Compose.sendRecordToMail(
     *   'asignee@domain.tld',
     *   'New lead assigned to you',
     *   {
     *      header: '<h1>New lead was created and assigned to you</h1>',
     *      footer: 'Review and confirm',
     *      cc: [ 'sales@domain.tld' ],
     *      fields: ['name', 'country', 'amount'],
     *   },
     *   newLead
     * )
     *
     * @param to - Recipient(s)
     * @param subject - Mail subject
     * @param options - Various options for body & email
     * @property {string} options.header - Text (HTML) before the record table
     * @property {string} options.footer - Text (HTML) after the record table
     * @property {string} options.style - Custom CSS styles for the email
     * @param options.fields - List of record fields we want to output
     * @param options.header - Additional mail headers (cc)
     * @param record - record to be converted (or leave for the current $record)
     */
    sendRecordToMail(to, subject = '', _a = {}, record = this.$record) {
        var { header = '', footer = '', style = emailStyle, fields = null } = _a, mailHeader = __rest(_a, ["header", "footer", "style", "fields"]);
        return __awaiter(this, void 0, void 0, function* () {
            // Wait for the record if we got a promise
            record = yield record;
            if (!record) {
                throw Error('record undefined');
            }
            const wb = '<div style="width: 800px; margin: 20px auto;">';
            const wa = '</div>';
            header = `${wb}${header}${wa}`;
            footer = `${wb}${footer}${wa}`;
            style = `<style type="text/css">${style}</style>`;
            const html = style + header + this.recordToHTML(fields, record) + footer;
            if (!subject) {
                subject = record.module.name + ' ';
                subject += record.updatedAt ? 'record updated' : 'record created';
            }
            return this.sendMail(to, subject, { html }, Object.assign({}, mailHeader));
        });
    }
    /**
     * Walks over white listed fields.
     *
     * @param fwl - field white list; if not defined, all fields are used
     * @param record - record to be walked over
     * @param formatter
     *
     * @private
     */
    walkFields(fwl, record, formatter) {
        if (!formatter) {
            throw new Error('formatter.undefined');
        }
        if (isRecord(fwl)) {
            record = fwl;
            fwl = undefined;
        }
        if (Array.isArray(fwl) && fwl.length === 0) {
            fwl = null;
        }
        return record.module.fields
            .filter(f => !fwl || fwl.indexOf(f.name) > -1)
            .map(formatter);
    }
    /**
     * Sends a simple record report as HTML
     *
     * @example
     * // generates report for current $record with all fields
     * let report = recordToHTML()
     *
     * // generates report for current $record from a list of fields
     * let report = recordToHTML(['fieldA', 'fieldB', 'fieldC'])
     *
     *
     * @param fwl - field white list (or leave empty/null/false for all fields)
     * @param record - record to be converted (or leave for the current $record)
     */
    recordToHTML(fwl = null, record = this.$record) {
        if (!record) {
            throw Error('record undefined');
        }
        const rows = this
            .walkFields(fwl, record, (f) => {
            const { name, label } = f;
            const v = record.values[name];
            return `<tr><td>${label || name}</td><td>${(Array.isArray(v) ? v : [v]).join(', ') || '&nbsp;'}</td></tr>`;
        })
            .join('');
        return `<table width="800" cellspacing="0" cellpadding="0" border="0">${rows}</table>`;
    }
    /**
     * Represents a given record as plain text
     *
     * @example
     * // generates report for current $record with all fields
     * let report = recordToPlainText()
     *
     * // generates report for current $record from a list of fields
     * let report = recordToPlainText(['fieldA', 'fieldB', 'fieldC'])
     *
     * @param fwl - field white list (or leave empty/null/false for all fields)
     * @param record - record to be converted (or leave for the current $record)
     */
    recordToPlainText(fwl = null, record = this.$record) {
        if (!record) {
            throw Error('record undefined');
        }
        return this
            .walkFields(fwl, record, f => {
            const { name, label } = f;
            const v = record.values[name];
            return `${label || name}:\n${(Array.isArray(v) ? v : [v]).join(', ') || '/'}\n\n`;
        })
            .join('')
            .trim();
    }
    /**
     * Scans all given arguments and returns first one that resembles something like a valid module, its name or ID
     *
     * @private
     */
    resolveModule(...args) {
        return __awaiter(this, void 0, void 0, function* () {
            const strResolve = (module) => __awaiter(this, void 0, void 0, function* () {
                return this.findModuleByHandle(module)
                    .then(m => {
                    if (!m) {
                        throw new Error('ModuleNotFound');
                    }
                    return m;
                })
                    .catch(() => this.findModuleByName(module));
            });
            for (let module of args) {
                if (!module) {
                    continue;
                }
                if (typeof module === 'string') {
                    if (IsCortezaID(module)) {
                        // Looks like an ID
                        return this.findModuleByID(module).catch((err = {}) => {
                            if (err.message && err.message.indexOf('ModuleNotFound') >= 0) {
                                // Not found, let's try if we can find it by slug
                                return strResolve(module);
                            }
                            return Promise.reject(err);
                        });
                    }
                    // Assume name
                    return strResolve(module);
                }
                if (typeof module !== 'object') {
                    continue;
                }
                // resolve whatever object we have (maybe it's a promise?)
                // and wait for results
                module = yield module;
                if (isRecord(module)) {
                    const m = module;
                    return this.resolveModule(m.module, m.moduleID);
                }
                if (IsOf(module, 'set', 'filter')) {
                    // We got a result set with modules
                    module = module.set;
                }
                if (Array.isArray(module)) {
                    // We got array of modules
                    if (module.length === 0) {
                        // Empty array
                        continue;
                    }
                    else {
                        // Use first module from the list
                        module = module.shift();
                    }
                }
                if (!isModule$1(module)) {
                    // not module? is it an object with moduleID & namespaceID?
                    if (module.moduleID === undefined || module.namespaceID === undefined) {
                        break;
                    }
                    return Promise.resolve(new Module(module));
                }
                return Promise.resolve(module);
            }
            return Promise.reject(Error('unexpected input type for module resolver'));
        });
    }
    /**
     * Scans all given arguments and returns first one that resembles something like a valid namespace, its slug or ID
     *
     * @private
     */
    resolveNamespace(...args) {
        return __awaiter(this, void 0, void 0, function* () {
            for (let ns of args) {
                if (!ns) {
                    continue;
                }
                if (typeof ns === 'string') {
                    if (IsCortezaID(ns)) {
                        // Looks like an ID
                        return this.findNamespaceByID(ns).catch((err = {}) => {
                            if (err.message && err.message.indexOf('NamespaceNotFound') >= 0) {
                                // Not found, let's try if we can find it by slug
                                return this.findNamespaceBySlug(ns);
                            }
                            return Promise.reject(err);
                        });
                    }
                    // Assume namespace slug
                    return this.findNamespaceBySlug(ns);
                }
                if (typeof ns !== 'object') {
                    continue;
                }
                // resolve whatever object we have (maybe it's a promise?)
                // and wait for results
                ns = yield ns;
                if (isRecord(ns)) {
                    const n = ns;
                    return this.resolveNamespace(n.namespaceID);
                }
                if (ns.set && ns.filter) {
                    // We got a result set with modules
                    ns = ns.set;
                }
                if (Array.isArray(ns)) {
                    // We got array of modules
                    if (ns.length === 0) {
                        // Empty array
                        continue;
                    }
                    else {
                        // Use first module from the list
                        ns = ns.shift();
                    }
                }
                if (!(ns instanceof Namespace)) {
                    // not Namespace? is it an object with namespaceID?
                    if (ns.namespaceID === undefined) {
                        break;
                    }
                    return Promise.resolve(new Namespace(ns));
                }
                return Promise.resolve(ns);
            }
            return Promise.reject(Error('unexpected input type for namespace resolver'));
        });
    }
    /**
     * Allows access for the given role for the given Compose resource
     *
     * @example
     * // Allows users with `someRole` to access the newly created namespace
     * await Compose.allow({
     *    role: someRole,
     *    resource: newNamespace,
     *    operation: 'read',
     * })
     */
    allow(...pr) {
        return __awaiter(this, void 0, void 0, function* () {
            const rr = pr.map(p => ({
                role: p.role,
                resource: p.resource,
                operation: p.operation,
                access: 'allow',
            }));
            return genericPermissionUpdater(this.ComposeAPI, rr);
        });
    }
    /**
     * Denies access for the given role for the given Compose resource
     *
     * @example
     * // Denies users with `someRole` from accessing the newly created namespace
     * await Compose.deny({
     *    role: someRole,
     *    resource: newNamespace,
     *    operation: 'read',
     * })
     */
    deny(...pr) {
        return __awaiter(this, void 0, void 0, function* () {
            const rr = pr.map(p => ({
                role: p.role,
                resource: p.resource,
                operation: p.operation,
                access: 'deny',
            }));
            return genericPermissionUpdater(this.ComposeAPI, rr);
        });
    }
    /**
     * Inherits access for the given role for the given Compose resource
     *
     * @example
     * // Uses inherited permissions for the `sameRole` for the newly created namespace
     * await Compose.inherit({
     *    role: someRole,
     *    resource: newNamespace,
     *    operation: 'read',
     * })
     */
    inherit(...pr) {
        return __awaiter(this, void 0, void 0, function* () {
            const rr = pr.map(p => ({
                role: p.role,
                resource: p.resource,
                operation: p.operation,
                access: 'inherit',
            }));
            return genericPermissionUpdater(this.ComposeAPI, rr);
        });
    }
}

/**
 * Generic type caster
 *
 * Takes argument (ref to class) and returns a function that will initialize class of that type
 */
function GenericCaster(C) {
    return function (val) {
        if (!val || typeof val !== 'object') {
            return undefined;
        }
        return new C(val);
    };
}
/**
 * Generic type caster with Object.freeze
 *
 * Takes argument (ref to class) and returns a function that will initialize class of that type
 */
function GenericCasterFreezer(C) {
    return function (val) {
        if (!val || typeof val !== 'object') {
            return undefined;
        }
        return Object.freeze(new C(val));
    };
}

/* eslint-disable @typescript-eslint/ban-ts-ignore */
/**
 * Record type caster
 *
 * Record arg is a bit special, it takes 2 params (record itself + record's module)
 */
function recordCaster(val) {
    if (val) {
        try {
            return new Record(this.$module, val);
        }
        catch (e) {
            console.error(e);
        }
    }
    return undefined;
}
function recordCasterFreezer(val) {
    if (val) {
        try {
            return Object.freeze(new Record(this.$module, val));
        }
        catch (e) {
            console.error(e);
        }
    }
    return undefined;
}
/**
 * CortezaTypes map helps ExecArgs class with translation of (special) arguments
 * to their respected types
 *
 * There's noe need to set/define casters for old* arguments,
 * It's auto-magically done by Args class
 */
const CortezaTypes = new Map();
CortezaTypes.set('authUser', GenericCasterFreezer(User));
CortezaTypes.set('invoker', GenericCasterFreezer(User));
CortezaTypes.set('module', GenericCaster(Module));
CortezaTypes.set('oldModule', GenericCasterFreezer(Module));
CortezaTypes.set('page', GenericCaster(Page));
CortezaTypes.set('oldPage', GenericCasterFreezer(Page));
CortezaTypes.set('namespace', GenericCaster(Namespace));
CortezaTypes.set('oldNamespace', GenericCasterFreezer(Namespace));
CortezaTypes.set('application', GenericCaster(Application));
CortezaTypes.set('oldApplication', GenericCasterFreezer(Application));
CortezaTypes.set('user', GenericCaster(User));
CortezaTypes.set('oldUser', GenericCasterFreezer(User));
CortezaTypes.set('role', GenericCaster(Role));
CortezaTypes.set('oldRole', GenericCasterFreezer(Role));
CortezaTypes.set('record', recordCaster);
CortezaTypes.set('oldRecord', recordCasterFreezer);
CortezaTypes.set('request', GenericCasterFreezer(SinkRequest));
CortezaTypes.set('response', GenericCaster(SinkResponse));

/**
 * Handles arguments, passed to the script
 *
 * By convention variables holding "current" resources are prefixed with dollar ($) sign.
 * For example, before/after triggers for record will call registered scripts with $record, $module
 * and $namespace, holding current record, it's module and namespace.
 *
 * All these variables are casted (if passed as an argument) to proper types ($record => Record, $module => Module, ...)
 */
class Args {
    constructor(args, caster = CortezaTypes) {
        const cachedArgs = {};
        for (const arg in args) {
            if (caster && caster.has(arg)) {
                const cast = caster.get(arg);
                if (cast) {
                    Object.defineProperty(this, `$${arg}`, {
                        get: () => {
                            if (!cachedArgs[arg]) {
                                cachedArgs[arg] = cast.call(this, args[arg]);
                            }
                            return cachedArgs[arg];
                        },
                        configurable: false,
                        enumerable: true,
                    });
                }
                Object.defineProperty(this, `raw${arg.substring(0, 1).toUpperCase()}${arg.substring(1)}`, {
                    value: args[arg],
                    writable: false,
                    configurable: false,
                    enumerable: true,
                });
            }
            else {
                Object.defineProperty(this, arg, {
                    value: args[arg],
                    writable: false,
                    configurable: false,
                    enumerable: true,
                });
            }
        }
    }
}
/**
 * Handles arguments, passed to the script but preserves references to the original objects
 *
 * By convention variables holding "current" resources are prefixed with dollar ($) sign.
 * For example, before/after triggers for record will call registered scripts with $record, $module
 * and $namespace, holding current record, it's module and namespace.
 *
 * These variables are not additionally casted, since in order to preserve references they should
 * already be in the correct type.
 */
class ArgsProxy {
    constructor(args, caster = CortezaTypes) {
        for (const arg in args) {
            // For consistency only prefix args with & and raw that have a defined caster
            if (caster && caster.has(arg)) {
                Object.defineProperty(this, `$${arg}`, {
                    get: () => args[arg],
                    configurable: false,
                    enumerable: true,
                });
                Object.defineProperty(this, `raw${arg.substring(0, 1).toUpperCase()}${arg.substring(1)}`, {
                    value: args[arg],
                    writable: false,
                    configurable: false,
                    enumerable: true,
                });
            }
            else {
                Object.defineProperty(this, arg, {
                    value: args[arg],
                    writable: false,
                    configurable: false,
                    enumerable: true,
                });
            }
        }
    }
}

/* eslint-disable padded-blocks */
function stdResolve(response) {
    if (response.data.error) {
        return Promise.reject(response.data.error);
    }
    else {
        return response.data.response;
    }
}
class System {
    constructor({ baseURL, headers, accessTokenFn }) {
        this.headers = {};
        this.baseURL = baseURL;
        this.accessTokenFn = accessTokenFn;
        this.headers = {
            /**
             * All we send is JSON
             */
            'Content-Type': 'application/json',
        };
        this.setHeaders(headers);
    }
    setAccessTokenFn(fn) {
        this.accessTokenFn = fn;
        return this;
    }
    setHeaders(headers) {
        if (typeof headers === 'object') {
            this.headers = headers;
        }
        return this;
    }
    setHeader(name, value) {
        if (value === undefined) {
            delete this.headers[name];
        }
        else {
            this.headers[name] = value;
        }
        return this;
    }
    api() {
        const headers = Object.assign({}, this.headers);
        const accessToken = this.accessTokenFn ? this.accessTokenFn() : undefined;
        if (accessToken) {
            headers.Authorization = 'Bearer ' + accessToken;
        }
        return axios.create({
            withCredentials: true,
            baseURL: this.baseURL,
            headers,
        });
    }
    // Impersonate a user
    authImpersonate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { userID, } = a || {};
            if (!userID) {
                throw Error('field userID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.authImpersonateEndpoint() });
            cfg.data = {
                userID,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    authImpersonateEndpoint() {
        return '/auth/impersonate';
    }
    // List clients
    authClientList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { handle, deleted, labels, limit, pageCursor, sort, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.authClientListEndpoint() });
            cfg.params = {
                handle,
                deleted,
                labels,
                limit,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    authClientListEndpoint() {
        return '/auth/clients/';
    }
    // Create client
    authClientCreate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { handle, meta, validGrant, redirectURI, scope, trusted, enabled, validFrom, expiresAt, security, labels, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.authClientCreateEndpoint() });
            cfg.data = {
                handle,
                meta,
                validGrant,
                redirectURI,
                scope,
                trusted,
                enabled,
                validFrom,
                expiresAt,
                security,
                labels,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    authClientCreateEndpoint() {
        return '/auth/clients/';
    }
    // Update user details
    authClientUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { clientID, handle, meta, validGrant, redirectURI, scope, trusted, enabled, validFrom, expiresAt, security, labels, } = a || {};
            if (!clientID) {
                throw Error('field clientID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'put', url: this.authClientUpdateEndpoint({
                    clientID,
                }) });
            cfg.data = {
                handle,
                meta,
                validGrant,
                redirectURI,
                scope,
                trusted,
                enabled,
                validFrom,
                expiresAt,
                security,
                labels,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    authClientUpdateEndpoint(a) {
        const { clientID, } = a || {};
        return `/auth/clients/${clientID}`;
    }
    // Read client details
    authClientRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { clientID, } = a || {};
            if (!clientID) {
                throw Error('field clientID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.authClientReadEndpoint({
                    clientID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    authClientReadEndpoint(a) {
        const { clientID, } = a || {};
        return `/auth/clients/${clientID}`;
    }
    // Remove client
    authClientDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { clientID, } = a || {};
            if (!clientID) {
                throw Error('field clientID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.authClientDeleteEndpoint({
                    clientID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    authClientDeleteEndpoint(a) {
        const { clientID, } = a || {};
        return `/auth/clients/${clientID}`;
    }
    // Undelete client
    authClientUndelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { clientID, } = a || {};
            if (!clientID) {
                throw Error('field clientID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.authClientUndeleteEndpoint({
                    clientID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    authClientUndeleteEndpoint(a) {
        const { clientID, } = a || {};
        return `/auth/clients/${clientID}/undelete`;
    }
    // Regenerate client&#x27;s secret
    authClientRegenerateSecret(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { clientID, } = a || {};
            if (!clientID) {
                throw Error('field clientID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.authClientRegenerateSecretEndpoint({
                    clientID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    authClientRegenerateSecretEndpoint(a) {
        const { clientID, } = a || {};
        return `/auth/clients/${clientID}/secret`;
    }
    // Exposes client&#x27;s secret
    authClientExposeSecret(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { clientID, } = a || {};
            if (!clientID) {
                throw Error('field clientID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.authClientExposeSecretEndpoint({
                    clientID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    authClientExposeSecretEndpoint(a) {
        const { clientID, } = a || {};
        return `/auth/clients/${clientID}/secret`;
    }
    // List settings
    settingsList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { prefix, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.settingsListEndpoint() });
            cfg.params = {
                prefix,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    settingsListEndpoint() {
        return '/settings/';
    }
    // Update settings
    settingsUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { values, } = a || {};
            if (!values) {
                throw Error('field values is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'patch', url: this.settingsUpdateEndpoint() });
            cfg.data = {
                values,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    settingsUpdateEndpoint() {
        return '/settings/';
    }
    // Get a value for a key
    settingsGet(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { key, ownerID, } = a || {};
            if (!key) {
                throw Error('field key is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.settingsGetEndpoint({
                    key,
                }) });
            cfg.params = {
                ownerID,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    settingsGetEndpoint(a) {
        const { key, } = a || {};
        return `/settings/${key}`;
    }
    // Set value for specific setting
    settingsSet(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { key, upload, ownerID, } = a || {};
            if (!key) {
                throw Error('field key is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.settingsSetEndpoint({
                    key,
                }) });
            cfg.data = {
                upload,
                ownerID,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    settingsSetEndpoint(a) {
        const { key, } = a || {};
        return `/settings/${key}`;
    }
    // Current compose settings
    settingsCurrent(extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.settingsCurrentEndpoint() });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    settingsCurrentEndpoint() {
        return '/settings/current';
    }
    // List roles
    roleList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { query, memberID, deleted, archived, labels, limit, pageCursor, sort, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.roleListEndpoint() });
            cfg.params = {
                query,
                memberID,
                deleted,
                archived,
                labels,
                limit,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    roleListEndpoint() {
        return '/roles/';
    }
    // Update role details
    roleCreate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { name, handle, members, meta, labels, } = a || {};
            if (!name) {
                throw Error('field name is empty');
            }
            if (!handle) {
                throw Error('field handle is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.roleCreateEndpoint() });
            cfg.data = {
                name,
                handle,
                members,
                meta,
                labels,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    roleCreateEndpoint() {
        return '/roles/';
    }
    // Update role details
    roleUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, name, handle, members, meta, labels, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'put', url: this.roleUpdateEndpoint({
                    roleID,
                }) });
            cfg.data = {
                name,
                handle,
                members,
                meta,
                labels,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    roleUpdateEndpoint(a) {
        const { roleID, } = a || {};
        return `/roles/${roleID}`;
    }
    // Read role details and memberships
    roleRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.roleReadEndpoint({
                    roleID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    roleReadEndpoint(a) {
        const { roleID, } = a || {};
        return `/roles/${roleID}`;
    }
    // Remove role
    roleDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.roleDeleteEndpoint({
                    roleID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    roleDeleteEndpoint(a) {
        const { roleID, } = a || {};
        return `/roles/${roleID}`;
    }
    // Archive role
    roleArchive(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.roleArchiveEndpoint({
                    roleID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    roleArchiveEndpoint(a) {
        const { roleID, } = a || {};
        return `/roles/${roleID}/archive`;
    }
    // Unarchive role
    roleUnarchive(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.roleUnarchiveEndpoint({
                    roleID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    roleUnarchiveEndpoint(a) {
        const { roleID, } = a || {};
        return `/roles/${roleID}/unarchive`;
    }
    // Undelete role
    roleUndelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.roleUndeleteEndpoint({
                    roleID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    roleUndeleteEndpoint(a) {
        const { roleID, } = a || {};
        return `/roles/${roleID}/undelete`;
    }
    // Move role to different organisation
    roleMove(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, organisationID, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            if (!organisationID) {
                throw Error('field organisationID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.roleMoveEndpoint({
                    roleID,
                }) });
            cfg.data = {
                organisationID,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    roleMoveEndpoint(a) {
        const { roleID, } = a || {};
        return `/roles/${roleID}/move`;
    }
    // Merge one role into another
    roleMerge(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, destination, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            if (!destination) {
                throw Error('field destination is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.roleMergeEndpoint({
                    roleID,
                }) });
            cfg.data = {
                destination,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    roleMergeEndpoint(a) {
        const { roleID, } = a || {};
        return `/roles/${roleID}/merge`;
    }
    // Returns all role members
    roleMemberList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.roleMemberListEndpoint({
                    roleID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    roleMemberListEndpoint(a) {
        const { roleID, } = a || {};
        return `/roles/${roleID}/members`;
    }
    // Add member to a role
    roleMemberAdd(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, userID, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            if (!userID) {
                throw Error('field userID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.roleMemberAddEndpoint({
                    roleID, userID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    roleMemberAddEndpoint(a) {
        const { roleID, userID, } = a || {};
        return `/roles/${roleID}/member/${userID}`;
    }
    // Remove member from a role
    roleMemberRemove(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, userID, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            if (!userID) {
                throw Error('field userID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.roleMemberRemoveEndpoint({
                    roleID, userID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    roleMemberRemoveEndpoint(a) {
        const { roleID, userID, } = a || {};
        return `/roles/${roleID}/member/${userID}`;
    }
    // Fire system:role trigger
    roleTriggerScript(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, script, args, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            if (!script) {
                throw Error('field script is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.roleTriggerScriptEndpoint({
                    roleID,
                }) });
            cfg.data = {
                script,
                args,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    roleTriggerScriptEndpoint(a) {
        const { roleID, } = a || {};
        return `/roles/${roleID}/trigger`;
    }
    // Search users (Directory)
    userList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { userID, roleID, query, username, email, handle, kind, incDeleted, incSuspended, deleted, suspended, labels, limit, pageCursor, sort, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.userListEndpoint() });
            cfg.params = {
                userID,
                roleID,
                query,
                username,
                email,
                handle,
                kind,
                incDeleted,
                incSuspended,
                deleted,
                suspended,
                labels,
                limit,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    userListEndpoint() {
        return '/users/';
    }
    // Create user
    userCreate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { email, name, handle, kind, labels, } = a || {};
            if (!email) {
                throw Error('field email is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.userCreateEndpoint() });
            cfg.data = {
                email,
                name,
                handle,
                kind,
                labels,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    userCreateEndpoint() {
        return '/users/';
    }
    // Update user details
    userUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { userID, email, name, handle, kind, labels, } = a || {};
            if (!userID) {
                throw Error('field userID is empty');
            }
            if (!email) {
                throw Error('field email is empty');
            }
            if (!name) {
                throw Error('field name is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'put', url: this.userUpdateEndpoint({
                    userID,
                }) });
            cfg.data = {
                email,
                name,
                handle,
                kind,
                labels,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    userUpdateEndpoint(a) {
        const { userID, } = a || {};
        return `/users/${userID}`;
    }
    // Patch user (experimental)
    userPartialUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { userID, } = a || {};
            if (!userID) {
                throw Error('field userID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'patch', url: this.userPartialUpdateEndpoint({
                    userID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    userPartialUpdateEndpoint(a) {
        const { userID, } = a || {};
        return `/users/${userID}`;
    }
    // Read user details
    userRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { userID, } = a || {};
            if (!userID) {
                throw Error('field userID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.userReadEndpoint({
                    userID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    userReadEndpoint(a) {
        const { userID, } = a || {};
        return `/users/${userID}`;
    }
    // Remove user
    userDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { userID, } = a || {};
            if (!userID) {
                throw Error('field userID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.userDeleteEndpoint({
                    userID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    userDeleteEndpoint(a) {
        const { userID, } = a || {};
        return `/users/${userID}`;
    }
    // Suspend user
    userSuspend(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { userID, } = a || {};
            if (!userID) {
                throw Error('field userID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.userSuspendEndpoint({
                    userID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    userSuspendEndpoint(a) {
        const { userID, } = a || {};
        return `/users/${userID}/suspend`;
    }
    // Unsuspend user
    userUnsuspend(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { userID, } = a || {};
            if (!userID) {
                throw Error('field userID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.userUnsuspendEndpoint({
                    userID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    userUnsuspendEndpoint(a) {
        const { userID, } = a || {};
        return `/users/${userID}/unsuspend`;
    }
    // Undelete user
    userUndelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { userID, } = a || {};
            if (!userID) {
                throw Error('field userID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.userUndeleteEndpoint({
                    userID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    userUndeleteEndpoint(a) {
        const { userID, } = a || {};
        return `/users/${userID}/undelete`;
    }
    // Set&#x27;s or changes user&#x27;s password
    userSetPassword(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { userID, password, } = a || {};
            if (!userID) {
                throw Error('field userID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.userSetPasswordEndpoint({
                    userID,
                }) });
            cfg.data = {
                password,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    userSetPasswordEndpoint(a) {
        const { userID, } = a || {};
        return `/users/${userID}/password`;
    }
    // Add member to a role
    userMembershipList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { userID, } = a || {};
            if (!userID) {
                throw Error('field userID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.userMembershipListEndpoint({
                    userID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    userMembershipListEndpoint(a) {
        const { userID, } = a || {};
        return `/users/${userID}/membership`;
    }
    // Add role to a user
    userMembershipAdd(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, userID, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            if (!userID) {
                throw Error('field userID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.userMembershipAddEndpoint({
                    roleID, userID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    userMembershipAddEndpoint(a) {
        const { roleID, userID, } = a || {};
        return `/users/${userID}/membership/${roleID}`;
    }
    // Remove role from a user
    userMembershipRemove(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, userID, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            if (!userID) {
                throw Error('field userID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.userMembershipRemoveEndpoint({
                    roleID, userID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    userMembershipRemoveEndpoint(a) {
        const { roleID, userID, } = a || {};
        return `/users/${userID}/membership/${roleID}`;
    }
    // Fire system:user trigger
    userTriggerScript(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { userID, script, args, } = a || {};
            if (!userID) {
                throw Error('field userID is empty');
            }
            if (!script) {
                throw Error('field script is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.userTriggerScriptEndpoint({
                    userID,
                }) });
            cfg.data = {
                script,
                args,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    userTriggerScriptEndpoint(a) {
        const { userID, } = a || {};
        return `/users/${userID}/trigger`;
    }
    // Remove all auth sessions of user
    userSessionsRemove(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { userID, } = a || {};
            if (!userID) {
                throw Error('field userID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.userSessionsRemoveEndpoint({
                    userID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    userSessionsRemoveEndpoint(a) {
        const { userID, } = a || {};
        return `/users/${userID}/sessions`;
    }
    // List applications
    applicationList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { name, query, deleted, labels, flags, incFlags, limit, pageCursor, sort, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.applicationListEndpoint() });
            cfg.params = {
                name,
                query,
                deleted,
                labels,
                flags,
                incFlags,
                limit,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    applicationListEndpoint() {
        return '/application/';
    }
    // Create application
    applicationCreate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { name, enabled, weight, unify, config, labels, } = a || {};
            if (!name) {
                throw Error('field name is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.applicationCreateEndpoint() });
            cfg.data = {
                name,
                enabled,
                weight,
                unify,
                config,
                labels,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    applicationCreateEndpoint() {
        return '/application/';
    }
    // Update user details
    applicationUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { applicationID, name, enabled, weight, unify, config, labels, } = a || {};
            if (!applicationID) {
                throw Error('field applicationID is empty');
            }
            if (!name) {
                throw Error('field name is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'put', url: this.applicationUpdateEndpoint({
                    applicationID,
                }) });
            cfg.data = {
                name,
                enabled,
                weight,
                unify,
                config,
                labels,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    applicationUpdateEndpoint(a) {
        const { applicationID, } = a || {};
        return `/application/${applicationID}`;
    }
    // Upload application assets
    applicationUpload(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { upload, } = a || {};
            if (!upload) {
                throw Error('field upload is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.applicationUploadEndpoint() });
            cfg.data = {
                upload,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    applicationUploadEndpoint() {
        return '/application/upload';
    }
    // Flag application
    applicationFlagCreate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { applicationID, flag, ownedBy, } = a || {};
            if (!applicationID) {
                throw Error('field applicationID is empty');
            }
            if (!flag) {
                throw Error('field flag is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.applicationFlagCreateEndpoint({
                    applicationID, flag, ownedBy,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    applicationFlagCreateEndpoint(a) {
        const { applicationID, flag, ownedBy, } = a || {};
        return `/application/${applicationID}/flag/${ownedBy}/${flag}`;
    }
    // Unflag application
    applicationFlagDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { applicationID, flag, ownedBy, } = a || {};
            if (!applicationID) {
                throw Error('field applicationID is empty');
            }
            if (!flag) {
                throw Error('field flag is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.applicationFlagDeleteEndpoint({
                    applicationID, flag, ownedBy,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    applicationFlagDeleteEndpoint(a) {
        const { applicationID, flag, ownedBy, } = a || {};
        return `/application/${applicationID}/flag/${ownedBy}/${flag}`;
    }
    // Read application details
    applicationRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { applicationID, incFlags, } = a || {};
            if (!applicationID) {
                throw Error('field applicationID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.applicationReadEndpoint({
                    applicationID,
                }) });
            cfg.params = {
                incFlags,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    applicationReadEndpoint(a) {
        const { applicationID, } = a || {};
        return `/application/${applicationID}`;
    }
    // Remove application
    applicationDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { applicationID, } = a || {};
            if (!applicationID) {
                throw Error('field applicationID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.applicationDeleteEndpoint({
                    applicationID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    applicationDeleteEndpoint(a) {
        const { applicationID, } = a || {};
        return `/application/${applicationID}`;
    }
    // Undelete application
    applicationUndelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { applicationID, } = a || {};
            if (!applicationID) {
                throw Error('field applicationID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.applicationUndeleteEndpoint({
                    applicationID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    applicationUndeleteEndpoint(a) {
        const { applicationID, } = a || {};
        return `/application/${applicationID}/undelete`;
    }
    // Fire system:application trigger
    applicationTriggerScript(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { applicationID, script, args, } = a || {};
            if (!applicationID) {
                throw Error('field applicationID is empty');
            }
            if (!script) {
                throw Error('field script is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.applicationTriggerScriptEndpoint({
                    applicationID,
                }) });
            cfg.data = {
                script,
                args,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    applicationTriggerScriptEndpoint(a) {
        const { applicationID, } = a || {};
        return `/application/${applicationID}/trigger`;
    }
    // Reorder applications
    applicationReorder(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { applicationIDs, } = a || {};
            if (!applicationIDs) {
                throw Error('field applicationIDs is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.applicationReorderEndpoint() });
            cfg.data = {
                applicationIDs,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    applicationReorderEndpoint() {
        return '/application/reorder';
    }
    // Retrieve defined permissions
    permissionsList(extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.permissionsListEndpoint() });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    permissionsListEndpoint() {
        return '/permissions/';
    }
    // Effective rules for current user
    permissionsEffective(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { resource, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.permissionsEffectiveEndpoint() });
            cfg.params = {
                resource,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    permissionsEffectiveEndpoint() {
        return '/permissions/effective';
    }
    // Retrieve role permissions
    permissionsRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.permissionsReadEndpoint({
                    roleID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    permissionsReadEndpoint(a) {
        const { roleID, } = a || {};
        return `/permissions/${roleID}/rules`;
    }
    // Remove all defined role permissions
    permissionsDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.permissionsDeleteEndpoint({
                    roleID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    permissionsDeleteEndpoint(a) {
        const { roleID, } = a || {};
        return `/permissions/${roleID}/rules`;
    }
    // Update permission settings
    permissionsUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, rules, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            if (!rules) {
                throw Error('field rules is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'patch', url: this.permissionsUpdateEndpoint({
                    roleID,
                }) });
            cfg.data = {
                rules,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    permissionsUpdateEndpoint(a) {
        const { roleID, } = a || {};
        return `/permissions/${roleID}/rules`;
    }
    // Clone permission settings to a role
    permissionsClone(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, cloneToRoleID, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            if (!cloneToRoleID) {
                throw Error('field cloneToRoleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.permissionsCloneEndpoint({
                    roleID,
                }) });
            cfg.params = {
                cloneToRoleID,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    permissionsCloneEndpoint(a) {
        const { roleID, } = a || {};
        return `/permissions/${roleID}/rules/clone`;
    }
    // List/read reminders
    reminderList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { reminderID, resource, assignedTo, scheduledFrom, scheduledUntil, scheduledOnly, excludeDismissed, includeDeleted, limit, pageCursor, sort, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.reminderListEndpoint() });
            cfg.params = {
                reminderID,
                resource,
                assignedTo,
                scheduledFrom,
                scheduledUntil,
                scheduledOnly,
                excludeDismissed,
                includeDeleted,
                limit,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    reminderListEndpoint() {
        return '/reminder/';
    }
    // Add new reminder
    reminderCreate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { resource, assignedTo, payload, remindAt, } = a || {};
            if (!resource) {
                throw Error('field resource is empty');
            }
            if (!assignedTo) {
                throw Error('field assignedTo is empty');
            }
            if (!payload) {
                throw Error('field payload is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.reminderCreateEndpoint() });
            cfg.data = {
                resource,
                assignedTo,
                payload,
                remindAt,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    reminderCreateEndpoint() {
        return '/reminder/';
    }
    // Update reminder
    reminderUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { reminderID, resource, assignedTo, payload, remindAt, } = a || {};
            if (!reminderID) {
                throw Error('field reminderID is empty');
            }
            if (!resource) {
                throw Error('field resource is empty');
            }
            if (!assignedTo) {
                throw Error('field assignedTo is empty');
            }
            if (!payload) {
                throw Error('field payload is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'put', url: this.reminderUpdateEndpoint({
                    reminderID,
                }) });
            cfg.data = {
                resource,
                assignedTo,
                payload,
                remindAt,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    reminderUpdateEndpoint(a) {
        const { reminderID, } = a || {};
        return `/reminder/${reminderID}`;
    }
    // Read reminder by ID
    reminderRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { reminderID, } = a || {};
            if (!reminderID) {
                throw Error('field reminderID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.reminderReadEndpoint({
                    reminderID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    reminderReadEndpoint(a) {
        const { reminderID, } = a || {};
        return `/reminder/${reminderID}`;
    }
    // Delete reminder
    reminderDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { reminderID, } = a || {};
            if (!reminderID) {
                throw Error('field reminderID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.reminderDeleteEndpoint({
                    reminderID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    reminderDeleteEndpoint(a) {
        const { reminderID, } = a || {};
        return `/reminder/${reminderID}`;
    }
    // Dismiss reminder
    reminderDismiss(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { reminderID, } = a || {};
            if (!reminderID) {
                throw Error('field reminderID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'patch', url: this.reminderDismissEndpoint({
                    reminderID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    reminderDismissEndpoint(a) {
        const { reminderID, } = a || {};
        return `/reminder/${reminderID}/dismiss`;
    }
    // Snooze reminder
    reminderSnooze(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { reminderID, remindAt, } = a || {};
            if (!reminderID) {
                throw Error('field reminderID is empty');
            }
            if (!remindAt) {
                throw Error('field remindAt is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'patch', url: this.reminderSnoozeEndpoint({
                    reminderID,
                }) });
            cfg.data = {
                remindAt,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    reminderSnoozeEndpoint(a) {
        const { reminderID, } = a || {};
        return `/reminder/${reminderID}/snooze`;
    }
    // Attachment details
    attachmentRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { kind, attachmentID, sign, userID, } = a || {};
            if (!kind) {
                throw Error('field kind is empty');
            }
            if (!attachmentID) {
                throw Error('field attachmentID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.attachmentReadEndpoint({
                    kind, attachmentID,
                }) });
            cfg.params = {
                sign,
                userID,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    attachmentReadEndpoint(a) {
        const { kind, attachmentID, } = a || {};
        return `/attachment/${kind}/${attachmentID}`;
    }
    // Delete attachment
    attachmentDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { kind, attachmentID, sign, userID, } = a || {};
            if (!kind) {
                throw Error('field kind is empty');
            }
            if (!attachmentID) {
                throw Error('field attachmentID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.attachmentDeleteEndpoint({
                    kind, attachmentID,
                }) });
            cfg.params = {
                sign,
                userID,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    attachmentDeleteEndpoint(a) {
        const { kind, attachmentID, } = a || {};
        return `/attachment/${kind}/${attachmentID}`;
    }
    // Serves attached file
    attachmentOriginal(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { kind, attachmentID, name, sign, userID, download, } = a || {};
            if (!kind) {
                throw Error('field kind is empty');
            }
            if (!attachmentID) {
                throw Error('field attachmentID is empty');
            }
            if (!name) {
                throw Error('field name is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.attachmentOriginalEndpoint({
                    kind, attachmentID, name,
                }) });
            cfg.params = {
                sign,
                userID,
                download,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    attachmentOriginalEndpoint(a) {
        const { kind, attachmentID, name, } = a || {};
        return `/attachment/${kind}/${attachmentID}/original/${name}`;
    }
    // Serves preview of an attached file
    attachmentPreview(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { kind, attachmentID, ext, sign, userID, } = a || {};
            if (!kind) {
                throw Error('field kind is empty');
            }
            if (!attachmentID) {
                throw Error('field attachmentID is empty');
            }
            if (!ext) {
                throw Error('field ext is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.attachmentPreviewEndpoint({
                    kind, attachmentID, ext,
                }) });
            cfg.params = {
                sign,
                userID,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    attachmentPreviewEndpoint(a) {
        const { kind, attachmentID, ext, } = a || {};
        return `/attachment/${kind}/${attachmentID}/preview.${ext}`;
    }
    // List templates
    templateList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { handle, type, ownerID, partial, deleted, labels, limit, pageCursor, sort, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.templateListEndpoint() });
            cfg.params = {
                handle,
                type,
                ownerID,
                partial,
                deleted,
                labels,
                limit,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    templateListEndpoint() {
        return '/template/';
    }
    // Create template
    templateCreate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { handle, language, type, partial, meta, template, ownerID, labels, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.templateCreateEndpoint() });
            cfg.data = {
                handle,
                language,
                type,
                partial,
                meta,
                template,
                ownerID,
                labels,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    templateCreateEndpoint() {
        return '/template/';
    }
    // Read template
    templateRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { templateID, } = a || {};
            if (!templateID) {
                throw Error('field templateID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.templateReadEndpoint({
                    templateID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    templateReadEndpoint(a) {
        const { templateID, } = a || {};
        return `/template/${templateID}`;
    }
    // Update template
    templateUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { templateID, handle, language, type, partial, meta, template, ownerID, labels, } = a || {};
            if (!templateID) {
                throw Error('field templateID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'put', url: this.templateUpdateEndpoint({
                    templateID,
                }) });
            cfg.data = {
                handle,
                language,
                type,
                partial,
                meta,
                template,
                ownerID,
                labels,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    templateUpdateEndpoint(a) {
        const { templateID, } = a || {};
        return `/template/${templateID}`;
    }
    // Delete template
    templateDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { templateID, } = a || {};
            if (!templateID) {
                throw Error('field templateID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.templateDeleteEndpoint({
                    templateID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    templateDeleteEndpoint(a) {
        const { templateID, } = a || {};
        return `/template/${templateID}`;
    }
    // Undelete template
    templateUndelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { templateID, } = a || {};
            if (!templateID) {
                throw Error('field templateID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.templateUndeleteEndpoint({
                    templateID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    templateUndeleteEndpoint(a) {
        const { templateID, } = a || {};
        return `/template/${templateID}/undelete`;
    }
    // Render drivers
    templateRenderDrivers(extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.templateRenderDriversEndpoint() });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    templateRenderDriversEndpoint() {
        return '/template/render/drivers';
    }
    // Render template
    templateRender(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { templateID, filename, ext, variables, options, } = a || {};
            if (!templateID) {
                throw Error('field templateID is empty');
            }
            if (!filename) {
                throw Error('field filename is empty');
            }
            if (!ext) {
                throw Error('field ext is empty');
            }
            if (!variables) {
                throw Error('field variables is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.templateRenderEndpoint({
                    templateID, filename, ext,
                }) });
            cfg.data = {
                variables,
                options,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    templateRenderEndpoint(a) {
        const { templateID, filename, ext, } = a || {};
        return `/template/${templateID}/render/${filename}.${ext}`;
    }
    // List reports
    reportList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { handle, deleted, labels, limit, pageCursor, sort, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.reportListEndpoint() });
            cfg.params = {
                handle,
                deleted,
                labels,
                limit,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    reportListEndpoint() {
        return '/reports/';
    }
    // Create report
    reportCreate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { handle, meta, scenarios, sources, blocks, labels, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.reportCreateEndpoint() });
            cfg.data = {
                handle,
                meta,
                scenarios,
                sources,
                blocks,
                labels,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    reportCreateEndpoint() {
        return '/reports/';
    }
    // Update report
    reportUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { reportID, handle, meta, scenarios, sources, blocks, labels, } = a || {};
            if (!reportID) {
                throw Error('field reportID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'put', url: this.reportUpdateEndpoint({
                    reportID,
                }) });
            cfg.data = {
                handle,
                meta,
                scenarios,
                sources,
                blocks,
                labels,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    reportUpdateEndpoint(a) {
        const { reportID, } = a || {};
        return `/reports/${reportID}`;
    }
    // Read report details
    reportRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { reportID, } = a || {};
            if (!reportID) {
                throw Error('field reportID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.reportReadEndpoint({
                    reportID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    reportReadEndpoint(a) {
        const { reportID, } = a || {};
        return `/reports/${reportID}`;
    }
    // Remove report
    reportDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { reportID, } = a || {};
            if (!reportID) {
                throw Error('field reportID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.reportDeleteEndpoint({
                    reportID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    reportDeleteEndpoint(a) {
        const { reportID, } = a || {};
        return `/reports/${reportID}`;
    }
    // Undelete report
    reportUndelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { reportID, } = a || {};
            if (!reportID) {
                throw Error('field reportID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.reportUndeleteEndpoint({
                    reportID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    reportUndeleteEndpoint(a) {
        const { reportID, } = a || {};
        return `/reports/${reportID}/undelete`;
    }
    // Describe report
    reportDescribe(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { sources, steps, describe, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.reportDescribeEndpoint() });
            cfg.data = {
                sources,
                steps,
                describe,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    reportDescribeEndpoint() {
        return '/reports/describe';
    }
    // Run report
    reportRun(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { reportID, frames, } = a || {};
            if (!reportID) {
                throw Error('field reportID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.reportRunEndpoint({
                    reportID,
                }) });
            cfg.data = {
                frames,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    reportRunEndpoint(a) {
        const { reportID, } = a || {};
        return `/reports/${reportID}/run`;
    }
    // List system statistics
    statsList(extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.statsListEndpoint() });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    statsListEndpoint() {
        return '/stats/';
    }
    // List all available automation scripts for system resources
    automationList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { resourceTypePrefixes, resourceTypes, eventTypes, excludeInvalid, excludeClientScripts, excludeServerScripts, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.automationListEndpoint() });
            cfg.params = {
                resourceTypePrefixes,
                resourceTypes,
                eventTypes,
                excludeInvalid,
                excludeClientScripts,
                excludeServerScripts,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    automationListEndpoint() {
        return '/automation/';
    }
    // Serves client scripts bundle
    automationBundle(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { bundle, type, ext, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.automationBundleEndpoint({
                    bundle, type, ext,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    automationBundleEndpoint(a) {
        const { bundle, type, ext, } = a || {};
        return `/automation/${bundle}-${type}.${ext}`;
    }
    // Triggers execution of a specific script on a system service level
    automationTriggerScript(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { script, args, } = a || {};
            if (!script) {
                throw Error('field script is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.automationTriggerScriptEndpoint() });
            cfg.data = {
                script,
                args,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    automationTriggerScriptEndpoint() {
        return '/automation/trigger';
    }
    // Action log events
    actionlogList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { from, to, beforeActionID, resource, action, actorID, limit, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.actionlogListEndpoint() });
            cfg.params = {
                from,
                to,
                beforeActionID,
                resource,
                action,
                actorID,
                limit,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    actionlogListEndpoint() {
        return '/actionlog/';
    }
    // Messaging queues
    queuesList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { query, limit, pageCursor, sort, deleted, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.queuesListEndpoint() });
            cfg.params = {
                query,
                limit,
                pageCursor,
                sort,
                deleted,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    queuesListEndpoint() {
        return '/queues/';
    }
    // Create messaging queue
    queuesCreate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { queue, consumer, meta, } = a || {};
            if (!queue) {
                throw Error('field queue is empty');
            }
            if (!consumer) {
                throw Error('field consumer is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.queuesCreateEndpoint() });
            cfg.data = {
                queue,
                consumer,
                meta,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    queuesCreateEndpoint() {
        return '/queues';
    }
    // Messaging queue details
    queuesRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { queueID, } = a || {};
            if (!queueID) {
                throw Error('field queueID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.queuesReadEndpoint({
                    queueID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    queuesReadEndpoint(a) {
        const { queueID, } = a || {};
        return `/queues/${queueID}`;
    }
    // Update queue details
    queuesUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { queueID, queue, consumer, meta, } = a || {};
            if (!queueID) {
                throw Error('field queueID is empty');
            }
            if (!queue) {
                throw Error('field queue is empty');
            }
            if (!consumer) {
                throw Error('field consumer is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'put', url: this.queuesUpdateEndpoint({
                    queueID,
                }) });
            cfg.data = {
                queue,
                consumer,
                meta,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    queuesUpdateEndpoint(a) {
        const { queueID, } = a || {};
        return `/queues/${queueID}`;
    }
    // Messaging queue delete
    queuesDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { queueID, } = a || {};
            if (!queueID) {
                throw Error('field queueID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.queuesDeleteEndpoint({
                    queueID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    queuesDeleteEndpoint(a) {
        const { queueID, } = a || {};
        return `/queues/${queueID}`;
    }
    // Messaging queue undelete
    queuesUndelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { queueID, } = a || {};
            if (!queueID) {
                throw Error('field queueID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.queuesUndeleteEndpoint({
                    queueID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    queuesUndeleteEndpoint(a) {
        const { queueID, } = a || {};
        return `/queues/${queueID}/undelete`;
    }
    // List routes
    apigwRouteList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { routeID, query, deleted, disabled, labels, limit, pageCursor, sort, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.apigwRouteListEndpoint() });
            cfg.params = {
                routeID,
                query,
                deleted,
                disabled,
                labels,
                limit,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    apigwRouteListEndpoint() {
        return '/apigw/route/';
    }
    // Create route
    apigwRouteCreate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { endpoint, method, enabled, group, meta, } = a || {};
            if (!endpoint) {
                throw Error('field endpoint is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.apigwRouteCreateEndpoint() });
            cfg.data = {
                endpoint,
                method,
                enabled,
                group,
                meta,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    apigwRouteCreateEndpoint() {
        return '/apigw/route';
    }
    // Update route details
    apigwRouteUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { routeID, endpoint, method, enabled, group, meta, } = a || {};
            if (!routeID) {
                throw Error('field routeID is empty');
            }
            if (!endpoint) {
                throw Error('field endpoint is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'put', url: this.apigwRouteUpdateEndpoint({
                    routeID,
                }) });
            cfg.data = {
                endpoint,
                method,
                enabled,
                group,
                meta,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    apigwRouteUpdateEndpoint(a) {
        const { routeID, } = a || {};
        return `/apigw/route/${routeID}`;
    }
    // Read route details
    apigwRouteRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { routeID, } = a || {};
            if (!routeID) {
                throw Error('field routeID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.apigwRouteReadEndpoint({
                    routeID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    apigwRouteReadEndpoint(a) {
        const { routeID, } = a || {};
        return `/apigw/route/${routeID}`;
    }
    // Remove route
    apigwRouteDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { routeID, } = a || {};
            if (!routeID) {
                throw Error('field routeID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.apigwRouteDeleteEndpoint({
                    routeID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    apigwRouteDeleteEndpoint(a) {
        const { routeID, } = a || {};
        return `/apigw/route/${routeID}`;
    }
    // Undelete route
    apigwRouteUndelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { routeID, } = a || {};
            if (!routeID) {
                throw Error('field routeID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.apigwRouteUndeleteEndpoint({
                    routeID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    apigwRouteUndeleteEndpoint(a) {
        const { routeID, } = a || {};
        return `/apigw/route/${routeID}/undelete`;
    }
    // List filters
    apigwFilterList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { routeID, deleted, disabled, limit, pageCursor, sort, } = a || {};
            if (!routeID) {
                throw Error('field routeID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.apigwFilterListEndpoint() });
            cfg.params = {
                routeID,
                deleted,
                disabled,
                limit,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    apigwFilterListEndpoint() {
        return '/apigw/filter/';
    }
    // Create filter
    apigwFilterCreate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { routeID, weight, kind, ref, enabled, params, } = a || {};
            if (!routeID) {
                throw Error('field routeID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'put', url: this.apigwFilterCreateEndpoint() });
            cfg.data = {
                routeID,
                weight,
                kind,
                ref,
                enabled,
                params,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    apigwFilterCreateEndpoint() {
        return '/apigw/filter';
    }
    // Update filter details
    apigwFilterUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { filterID, routeID, weight, kind, ref, enabled, params, } = a || {};
            if (!filterID) {
                throw Error('field filterID is empty');
            }
            if (!routeID) {
                throw Error('field routeID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.apigwFilterUpdateEndpoint({
                    filterID,
                }) });
            cfg.data = {
                routeID,
                weight,
                kind,
                ref,
                enabled,
                params,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    apigwFilterUpdateEndpoint(a) {
        const { filterID, } = a || {};
        return `/apigw/filter/${filterID}`;
    }
    // Read filter details
    apigwFilterRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { filterID, } = a || {};
            if (!filterID) {
                throw Error('field filterID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.apigwFilterReadEndpoint({
                    filterID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    apigwFilterReadEndpoint(a) {
        const { filterID, } = a || {};
        return `/apigw/filter/${filterID}`;
    }
    // Remove filter
    apigwFilterDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { filterID, } = a || {};
            if (!filterID) {
                throw Error('field filterID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.apigwFilterDeleteEndpoint({
                    filterID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    apigwFilterDeleteEndpoint(a) {
        const { filterID, } = a || {};
        return `/apigw/filter/${filterID}`;
    }
    // Undelete filter
    apigwFilterUndelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { filterID, } = a || {};
            if (!filterID) {
                throw Error('field filterID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.apigwFilterUndeleteEndpoint({
                    filterID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    apigwFilterUndeleteEndpoint(a) {
        const { filterID, } = a || {};
        return `/apigw/filter/${filterID}/undelete`;
    }
    // Filter definitions
    apigwFilterDefFilter(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { kind, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.apigwFilterDefFilterEndpoint() });
            cfg.params = {
                kind,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    apigwFilterDefFilterEndpoint() {
        return '/apigw/filter/def';
    }
    // Proxy auth definitions
    apigwFilterDefProxyAuth(extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.apigwFilterDefProxyAuthEndpoint() });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    apigwFilterDefProxyAuthEndpoint() {
        return '/apigw/filter/proxy_auth/def';
    }
    // List resources translations
    localeListResource(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { lang, resource, resourceType, ownerID, deleted, limit, pageCursor, sort, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.localeListResourceEndpoint() });
            cfg.params = {
                lang,
                resource,
                resourceType,
                ownerID,
                deleted,
                limit,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    localeListResourceEndpoint() {
        return '/locale/resource';
    }
    // Create resource translation
    localeCreateResource(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { lang, resource, key, place, message, ownerID, } = a || {};
            if (!lang) {
                throw Error('field lang is empty');
            }
            if (!resource) {
                throw Error('field resource is empty');
            }
            if (!key) {
                throw Error('field key is empty');
            }
            if (!message) {
                throw Error('field message is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.localeCreateResourceEndpoint() });
            cfg.data = {
                lang,
                resource,
                key,
                place,
                message,
                ownerID,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    localeCreateResourceEndpoint() {
        return '/locale/resource';
    }
    // Update resource translation
    localeUpdateResource(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { translationID, lang, resource, key, place, message, ownerID, } = a || {};
            if (!translationID) {
                throw Error('field translationID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'put', url: this.localeUpdateResourceEndpoint({
                    translationID,
                }) });
            cfg.data = {
                lang,
                resource,
                key,
                place,
                message,
                ownerID,
            };
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    localeUpdateResourceEndpoint(a) {
        const { translationID, } = a || {};
        return `/locale/resource/${translationID}`;
    }
    // Read resource translation details
    localeReadResource(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { translationID, } = a || {};
            if (!translationID) {
                throw Error('field translationID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.localeReadResourceEndpoint({
                    translationID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    localeReadResourceEndpoint(a) {
        const { translationID, } = a || {};
        return `/locale/resource/${translationID}`;
    }
    // Remove resource translation
    localeDeleteResource(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { translationID, } = a || {};
            if (!translationID) {
                throw Error('field translationID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.localeDeleteResourceEndpoint({
                    translationID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    localeDeleteResourceEndpoint(a) {
        const { translationID, } = a || {};
        return `/locale/resource/${translationID}`;
    }
    // Undelete resource translation
    localeUndeleteResource(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { translationID, } = a || {};
            if (!translationID) {
                throw Error('field translationID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.localeUndeleteResourceEndpoint({
                    translationID,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    localeUndeleteResourceEndpoint(a) {
        const { translationID, } = a || {};
        return `/locale/resource/${translationID}/undelete`;
    }
    // List all available languages
    localeList(extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.localeListEndpoint() });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    localeListEndpoint() {
        return '/locale/';
    }
    // List all available translation in a language for a specific webapp
    localeGet(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { lang, application, } = a || {};
            if (!lang) {
                throw Error('field lang is empty');
            }
            if (!application) {
                throw Error('field application is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.localeGetEndpoint({
                    lang, application,
                }) });
            return this.api().request(cfg).then(result => stdResolve(result));
        });
    }
    localeGetEndpoint(a) {
        const { lang, application, } = a || {};
        return `/locale/${lang}/${application}`;
    }
}

/* eslint-disable padded-blocks */
function stdResolve$1(response) {
    if (response.data.error) {
        return Promise.reject(response.data.error);
    }
    else {
        return response.data.response;
    }
}
class Compose {
    constructor({ baseURL, headers, accessTokenFn }) {
        this.headers = {};
        this.baseURL = baseURL;
        this.accessTokenFn = accessTokenFn;
        this.headers = {
            /**
             * All we send is JSON
             */
            'Content-Type': 'application/json',
        };
        this.setHeaders(headers);
    }
    setAccessTokenFn(fn) {
        this.accessTokenFn = fn;
        return this;
    }
    setHeaders(headers) {
        if (typeof headers === 'object') {
            this.headers = headers;
        }
        return this;
    }
    setHeader(name, value) {
        if (value === undefined) {
            delete this.headers[name];
        }
        else {
            this.headers[name] = value;
        }
        return this;
    }
    api() {
        const headers = Object.assign({}, this.headers);
        const accessToken = this.accessTokenFn ? this.accessTokenFn() : undefined;
        if (accessToken) {
            headers.Authorization = 'Bearer ' + accessToken;
        }
        return axios.create({
            withCredentials: true,
            baseURL: this.baseURL,
            headers,
        });
    }
    // List namespaces
    namespaceList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { query, slug, limit, labels, pageCursor, sort, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.namespaceListEndpoint() });
            cfg.params = {
                query,
                slug,
                limit,
                labels,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    namespaceListEndpoint() {
        return '/namespace/';
    }
    // Create namespace
    namespaceCreate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { name, labels, slug, enabled, meta, } = a || {};
            if (!name) {
                throw Error('field name is empty');
            }
            if (!meta) {
                throw Error('field meta is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.namespaceCreateEndpoint() });
            cfg.data = {
                name,
                labels,
                slug,
                enabled,
                meta,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    namespaceCreateEndpoint() {
        return '/namespace/';
    }
    // Read namespace
    namespaceRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.namespaceReadEndpoint({
                    namespaceID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    namespaceReadEndpoint(a) {
        const { namespaceID, } = a || {};
        return `/namespace/${namespaceID}`;
    }
    // Update namespace
    namespaceUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, name, slug, enabled, meta, labels, updatedAt, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!name) {
                throw Error('field name is empty');
            }
            if (!meta) {
                throw Error('field meta is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.namespaceUpdateEndpoint({
                    namespaceID,
                }) });
            cfg.data = {
                name,
                slug,
                enabled,
                meta,
                labels,
                updatedAt,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    namespaceUpdateEndpoint(a) {
        const { namespaceID, } = a || {};
        return `/namespace/${namespaceID}`;
    }
    // Delete namespace
    namespaceDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.namespaceDeleteEndpoint({
                    namespaceID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    namespaceDeleteEndpoint(a) {
        const { namespaceID, } = a || {};
        return `/namespace/${namespaceID}`;
    }
    // Upload namespace assets
    namespaceUpload(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { upload, } = a || {};
            if (!upload) {
                throw Error('field upload is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.namespaceUploadEndpoint() });
            cfg.data = {
                upload,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    namespaceUploadEndpoint() {
        return '/namespace/upload';
    }
    // Clone compose namespace
    namespaceClone(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, name, slug, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!name) {
                throw Error('field name is empty');
            }
            if (!slug) {
                throw Error('field slug is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.namespaceCloneEndpoint({
                    namespaceID,
                }) });
            cfg.data = {
                name,
                slug,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    namespaceCloneEndpoint(a) {
        const { namespaceID, } = a || {};
        return `/namespace/${namespaceID}/clone`;
    }
    // Export compose namespace
    namespaceExport(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, filename, ext, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!filename) {
                throw Error('field filename is empty');
            }
            if (!ext) {
                throw Error('field ext is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.namespaceExportEndpoint({
                    namespaceID, filename, ext,
                }) });
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    namespaceExportEndpoint(a) {
        const { namespaceID, filename, ext, } = a || {};
        return `/namespace/${namespaceID}/export/${filename}.zip`;
    }
    // Initiate namespace import session
    namespaceImportInit(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { upload, } = a || {};
            if (!upload) {
                throw Error('field upload is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.namespaceImportInitEndpoint() });
            cfg.data = {
                upload,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    namespaceImportInitEndpoint() {
        return '/namespace/import';
    }
    // Run namespace import
    namespaceImportRun(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { sessionID, name, slug, } = a || {};
            if (!sessionID) {
                throw Error('field sessionID is empty');
            }
            if (!name) {
                throw Error('field name is empty');
            }
            if (!slug) {
                throw Error('field slug is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.namespaceImportRunEndpoint({
                    sessionID,
                }) });
            cfg.data = {
                name,
                slug,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    namespaceImportRunEndpoint(a) {
        const { sessionID, } = a || {};
        return `/namespace/import/${sessionID}`;
    }
    // Fire compose:namespace trigger
    namespaceTriggerScript(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, script, args, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!script) {
                throw Error('field script is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.namespaceTriggerScriptEndpoint({
                    namespaceID,
                }) });
            cfg.data = {
                script,
                args,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    namespaceTriggerScriptEndpoint(a) {
        const { namespaceID, } = a || {};
        return `/namespace/${namespaceID}/trigger`;
    }
    // List translation
    namespaceListTranslations(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.namespaceListTranslationsEndpoint({
                    namespaceID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    namespaceListTranslationsEndpoint(a) {
        const { namespaceID, } = a || {};
        return `/namespace/${namespaceID}/translation`;
    }
    // Update translation
    namespaceUpdateTranslations(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, translations, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!translations) {
                throw Error('field translations is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'patch', url: this.namespaceUpdateTranslationsEndpoint({
                    namespaceID,
                }) });
            cfg.data = {
                translations,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    namespaceUpdateTranslationsEndpoint(a) {
        const { namespaceID, } = a || {};
        return `/namespace/${namespaceID}/translation`;
    }
    // List available pages
    pageList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, selfID, moduleID, query, handle, labels, limit, pageCursor, sort, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.pageListEndpoint({
                    namespaceID,
                }) });
            cfg.params = {
                selfID,
                moduleID,
                query,
                handle,
                labels,
                limit,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    pageListEndpoint(a) {
        const { namespaceID, } = a || {};
        return `/namespace/${namespaceID}/page/`;
    }
    // Create page
    pageCreate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, selfID, moduleID, title, handle, description, weight, labels, visible, blocks, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!title) {
                throw Error('field title is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.pageCreateEndpoint({
                    namespaceID,
                }) });
            cfg.data = {
                selfID,
                moduleID,
                title,
                handle,
                description,
                weight,
                labels,
                visible,
                blocks,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    pageCreateEndpoint(a) {
        const { namespaceID, } = a || {};
        return `/namespace/${namespaceID}/page/`;
    }
    // Get page details
    pageRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, pageID, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!pageID) {
                throw Error('field pageID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.pageReadEndpoint({
                    namespaceID, pageID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    pageReadEndpoint(a) {
        const { namespaceID, pageID, } = a || {};
        return `/namespace/${namespaceID}/page/${pageID}`;
    }
    // Get page all (non-record) pages, hierarchically
    pageTree(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.pageTreeEndpoint({
                    namespaceID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    pageTreeEndpoint(a) {
        const { namespaceID, } = a || {};
        return `/namespace/${namespaceID}/page/tree`;
    }
    // Update page
    pageUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, pageID, selfID, moduleID, title, handle, description, weight, labels, visible, blocks, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!pageID) {
                throw Error('field pageID is empty');
            }
            if (!title) {
                throw Error('field title is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.pageUpdateEndpoint({
                    namespaceID, pageID,
                }) });
            cfg.data = {
                selfID,
                moduleID,
                title,
                handle,
                description,
                weight,
                labels,
                visible,
                blocks,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    pageUpdateEndpoint(a) {
        const { namespaceID, pageID, } = a || {};
        return `/namespace/${namespaceID}/page/${pageID}`;
    }
    // Reorder pages
    pageReorder(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, selfID, pageIDs, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!selfID) {
                throw Error('field selfID is empty');
            }
            if (!pageIDs) {
                throw Error('field pageIDs is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.pageReorderEndpoint({
                    namespaceID, selfID,
                }) });
            cfg.data = {
                pageIDs,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    pageReorderEndpoint(a) {
        const { namespaceID, selfID, } = a || {};
        return `/namespace/${namespaceID}/page/${selfID}/reorder`;
    }
    // Delete page
    pageDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, pageID, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!pageID) {
                throw Error('field pageID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.pageDeleteEndpoint({
                    namespaceID, pageID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    pageDeleteEndpoint(a) {
        const { namespaceID, pageID, } = a || {};
        return `/namespace/${namespaceID}/page/${pageID}`;
    }
    // Uploads attachment to page
    pageUpload(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, pageID, upload, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!pageID) {
                throw Error('field pageID is empty');
            }
            if (!upload) {
                throw Error('field upload is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.pageUploadEndpoint({
                    namespaceID, pageID,
                }) });
            cfg.data = {
                upload,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    pageUploadEndpoint(a) {
        const { namespaceID, pageID, } = a || {};
        return `/namespace/${namespaceID}/page/${pageID}/attachment`;
    }
    // Fire compose:page trigger
    pageTriggerScript(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, pageID, script, args, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!pageID) {
                throw Error('field pageID is empty');
            }
            if (!script) {
                throw Error('field script is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.pageTriggerScriptEndpoint({
                    namespaceID, pageID,
                }) });
            cfg.data = {
                script,
                args,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    pageTriggerScriptEndpoint(a) {
        const { namespaceID, pageID, } = a || {};
        return `/namespace/${namespaceID}/page/${pageID}/trigger`;
    }
    // List page translation
    pageListTranslations(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, pageID, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!pageID) {
                throw Error('field pageID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.pageListTranslationsEndpoint({
                    namespaceID, pageID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    pageListTranslationsEndpoint(a) {
        const { namespaceID, pageID, } = a || {};
        return `/namespace/${namespaceID}/page/${pageID}/translation`;
    }
    // Update page translation
    pageUpdateTranslations(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, pageID, translations, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!pageID) {
                throw Error('field pageID is empty');
            }
            if (!translations) {
                throw Error('field translations is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'patch', url: this.pageUpdateTranslationsEndpoint({
                    namespaceID, pageID,
                }) });
            cfg.data = {
                translations,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    pageUpdateTranslationsEndpoint(a) {
        const { namespaceID, pageID, } = a || {};
        return `/namespace/${namespaceID}/page/${pageID}/translation`;
    }
    // List modules
    moduleList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, query, name, handle, limit, pageCursor, labels, sort, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.moduleListEndpoint({
                    namespaceID,
                }) });
            cfg.params = {
                query,
                name,
                handle,
                limit,
                pageCursor,
                labels,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    moduleListEndpoint(a) {
        const { namespaceID, } = a || {};
        return `/namespace/${namespaceID}/module/`;
    }
    // Create module
    moduleCreate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, name, handle, fields, meta, labels, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!name) {
                throw Error('field name is empty');
            }
            if (!fields) {
                throw Error('field fields is empty');
            }
            if (!meta) {
                throw Error('field meta is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.moduleCreateEndpoint({
                    namespaceID,
                }) });
            cfg.data = {
                name,
                handle,
                fields,
                meta,
                labels,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    moduleCreateEndpoint(a) {
        const { namespaceID, } = a || {};
        return `/namespace/${namespaceID}/module/`;
    }
    // Read module
    moduleRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.moduleReadEndpoint({
                    namespaceID, moduleID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    moduleReadEndpoint(a) {
        const { namespaceID, moduleID, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}`;
    }
    // Update module
    moduleUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, name, handle, fields, meta, updatedAt, labels, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            if (!name) {
                throw Error('field name is empty');
            }
            if (!fields) {
                throw Error('field fields is empty');
            }
            if (!meta) {
                throw Error('field meta is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.moduleUpdateEndpoint({
                    namespaceID, moduleID,
                }) });
            cfg.data = {
                name,
                handle,
                fields,
                meta,
                updatedAt,
                labels,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    moduleUpdateEndpoint(a) {
        const { namespaceID, moduleID, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}`;
    }
    // Delete module
    moduleDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.moduleDeleteEndpoint({
                    namespaceID, moduleID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    moduleDeleteEndpoint(a) {
        const { namespaceID, moduleID, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}`;
    }
    // Fire compose:module trigger
    moduleTriggerScript(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, script, args, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            if (!script) {
                throw Error('field script is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.moduleTriggerScriptEndpoint({
                    namespaceID, moduleID,
                }) });
            cfg.data = {
                script,
                args,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    moduleTriggerScriptEndpoint(a) {
        const { namespaceID, moduleID, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}/trigger`;
    }
    // List moudle translation
    moduleListTranslations(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.moduleListTranslationsEndpoint({
                    namespaceID, moduleID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    moduleListTranslationsEndpoint(a) {
        const { namespaceID, moduleID, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}/translation`;
    }
    // Update module translation
    moduleUpdateTranslations(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, translations, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            if (!translations) {
                throw Error('field translations is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'patch', url: this.moduleUpdateTranslationsEndpoint({
                    namespaceID, moduleID,
                }) });
            cfg.data = {
                translations,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    moduleUpdateTranslationsEndpoint(a) {
        const { namespaceID, moduleID, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}/translation`;
    }
    // Generates report from module records
    recordReport(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, metrics, dimensions, filter, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            if (!dimensions) {
                throw Error('field dimensions is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.recordReportEndpoint({
                    namespaceID, moduleID,
                }) });
            cfg.params = {
                metrics,
                dimensions,
                filter,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    recordReportEndpoint(a) {
        const { namespaceID, moduleID, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}/record/report`;
    }
    // List/read records from module section
    recordList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, query, labels, deleted, limit, incTotal, incPageNavigation, pageCursor, sort, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.recordListEndpoint({
                    namespaceID, moduleID,
                }) });
            cfg.params = {
                query,
                labels,
                deleted,
                limit,
                incTotal,
                incPageNavigation,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    recordListEndpoint(a) {
        const { namespaceID, moduleID, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}/record/`;
    }
    // Initiate record import session
    recordImportInit(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, upload, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            if (!upload) {
                throw Error('field upload is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.recordImportInitEndpoint({
                    namespaceID, moduleID,
                }) });
            cfg.data = {
                upload,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    recordImportInitEndpoint(a) {
        const { namespaceID, moduleID, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}/record/import`;
    }
    // Run record import
    recordImportRun(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, sessionID, fields, onError, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            if (!sessionID) {
                throw Error('field sessionID is empty');
            }
            if (!fields) {
                throw Error('field fields is empty');
            }
            if (!onError) {
                throw Error('field onError is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'patch', url: this.recordImportRunEndpoint({
                    namespaceID, moduleID, sessionID,
                }) });
            cfg.data = {
                fields,
                onError,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    recordImportRunEndpoint(a) {
        const { namespaceID, moduleID, sessionID, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}/record/import/${sessionID}`;
    }
    // Get import progress
    recordImportProgress(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, sessionID, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            if (!sessionID) {
                throw Error('field sessionID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.recordImportProgressEndpoint({
                    namespaceID, moduleID, sessionID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    recordImportProgressEndpoint(a) {
        const { namespaceID, moduleID, sessionID, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}/record/import/${sessionID}`;
    }
    // Exports records that match
    recordExport(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, filename, ext, filter, fields, timezone, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            if (!ext) {
                throw Error('field ext is empty');
            }
            if (!fields) {
                throw Error('field fields is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.recordExportEndpoint({
                    namespaceID, moduleID, filename, ext,
                }) });
            cfg.params = {
                filter,
                fields,
                timezone,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    recordExportEndpoint(a) {
        const { namespaceID, moduleID, filename, ext, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}/record/export${filename}.${ext}`;
    }
    // Executes server-side procedure over one or more module records
    recordExec(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, procedure, args, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            if (!procedure) {
                throw Error('field procedure is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.recordExecEndpoint({
                    namespaceID, moduleID, procedure,
                }) });
            cfg.data = {
                args,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    recordExecEndpoint(a) {
        const { namespaceID, moduleID, procedure, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}/record/exec/${procedure}`;
    }
    // Create record in module section
    recordCreate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, values, records, labels, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.recordCreateEndpoint({
                    namespaceID, moduleID,
                }) });
            cfg.data = {
                values,
                records,
                labels,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    recordCreateEndpoint(a) {
        const { namespaceID, moduleID, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}/record/`;
    }
    // Read records by ID from module section
    recordRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, recordID, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            if (!recordID) {
                throw Error('field recordID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.recordReadEndpoint({
                    namespaceID, moduleID, recordID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    recordReadEndpoint(a) {
        const { namespaceID, moduleID, recordID, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}/record/${recordID}`;
    }
    // Update records in module section
    recordUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, recordID, values, records, labels, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            if (!recordID) {
                throw Error('field recordID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.recordUpdateEndpoint({
                    namespaceID, moduleID, recordID,
                }) });
            cfg.data = {
                values,
                records,
                labels,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    recordUpdateEndpoint(a) {
        const { namespaceID, moduleID, recordID, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}/record/${recordID}`;
    }
    // Delete record row from module section
    recordBulkDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, recordIDs, truncate, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.recordBulkDeleteEndpoint({
                    namespaceID, moduleID,
                }) });
            cfg.data = {
                recordIDs,
                truncate,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    recordBulkDeleteEndpoint(a) {
        const { namespaceID, moduleID, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}/record/`;
    }
    // Delete record row from module section
    recordDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, recordID, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            if (!recordID) {
                throw Error('field recordID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.recordDeleteEndpoint({
                    namespaceID, moduleID, recordID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    recordDeleteEndpoint(a) {
        const { namespaceID, moduleID, recordID, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}/record/${recordID}`;
    }
    // Uploads attachment and validates it against record field requirements
    recordUpload(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, recordID, fieldName, upload, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            if (!fieldName) {
                throw Error('field fieldName is empty');
            }
            if (!upload) {
                throw Error('field upload is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.recordUploadEndpoint({
                    namespaceID, moduleID,
                }) });
            cfg.data = {
                recordID,
                fieldName,
                upload,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    recordUploadEndpoint(a) {
        const { namespaceID, moduleID, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}/record/attachment`;
    }
    // Fire compose:record trigger
    recordTriggerScript(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, recordID, script, values, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            if (!recordID) {
                throw Error('field recordID is empty');
            }
            if (!script) {
                throw Error('field script is empty');
            }
            if (!values) {
                throw Error('field values is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.recordTriggerScriptEndpoint({
                    namespaceID, moduleID, recordID,
                }) });
            cfg.data = {
                script,
                values,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    recordTriggerScriptEndpoint(a) {
        const { namespaceID, moduleID, recordID, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}/record/${recordID}/trigger`;
    }
    // Fire compose:record trigger
    recordTriggerScriptOnList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, moduleID, script, args, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            if (!script) {
                throw Error('field script is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.recordTriggerScriptOnListEndpoint({
                    namespaceID, moduleID,
                }) });
            cfg.data = {
                script,
                args,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    recordTriggerScriptOnListEndpoint(a) {
        const { namespaceID, moduleID, } = a || {};
        return `/namespace/${namespaceID}/module/${moduleID}/record/trigger`;
    }
    // List/read charts
    chartList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, query, handle, labels, limit, pageCursor, sort, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.chartListEndpoint({
                    namespaceID,
                }) });
            cfg.params = {
                query,
                handle,
                labels,
                limit,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    chartListEndpoint(a) {
        const { namespaceID, } = a || {};
        return `/namespace/${namespaceID}/chart/`;
    }
    // List/read charts
    chartCreate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, config, name, handle, labels, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!config) {
                throw Error('field config is empty');
            }
            if (!name) {
                throw Error('field name is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.chartCreateEndpoint({
                    namespaceID,
                }) });
            cfg.data = {
                config,
                name,
                handle,
                labels,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    chartCreateEndpoint(a) {
        const { namespaceID, } = a || {};
        return `/namespace/${namespaceID}/chart/`;
    }
    // Read charts by ID
    chartRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, chartID, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!chartID) {
                throw Error('field chartID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.chartReadEndpoint({
                    namespaceID, chartID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    chartReadEndpoint(a) {
        const { namespaceID, chartID, } = a || {};
        return `/namespace/${namespaceID}/chart/${chartID}`;
    }
    // Add/update charts
    chartUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, chartID, config, name, handle, labels, updatedAt, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!chartID) {
                throw Error('field chartID is empty');
            }
            if (!config) {
                throw Error('field config is empty');
            }
            if (!name) {
                throw Error('field name is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.chartUpdateEndpoint({
                    namespaceID, chartID,
                }) });
            cfg.data = {
                config,
                name,
                handle,
                labels,
                updatedAt,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    chartUpdateEndpoint(a) {
        const { namespaceID, chartID, } = a || {};
        return `/namespace/${namespaceID}/chart/${chartID}`;
    }
    // Delete chart
    chartDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { namespaceID, chartID, } = a || {};
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!chartID) {
                throw Error('field chartID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.chartDeleteEndpoint({
                    namespaceID, chartID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    chartDeleteEndpoint(a) {
        const { namespaceID, chartID, } = a || {};
        return `/namespace/${namespaceID}/chart/${chartID}`;
    }
    // Send email from the Compose
    notificationEmailSend(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { to, cc, replyTo, subject, content, remoteAttachments, } = a || {};
            if (!to) {
                throw Error('field to is empty');
            }
            if (!content) {
                throw Error('field content is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.notificationEmailSendEndpoint() });
            cfg.data = {
                to,
                cc,
                replyTo,
                subject,
                content,
                remoteAttachments,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    notificationEmailSendEndpoint() {
        return '/notification/email';
    }
    // List, filter all page attachments
    attachmentList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { kind, namespaceID, sign, userID, pageID, moduleID, recordID, fieldName, limit, pageCursor, } = a || {};
            if (!kind) {
                throw Error('field kind is empty');
            }
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.attachmentListEndpoint({
                    kind, namespaceID,
                }) });
            cfg.params = {
                sign,
                userID,
                pageID,
                moduleID,
                recordID,
                fieldName,
                limit,
                pageCursor,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    attachmentListEndpoint(a) {
        const { kind, namespaceID, } = a || {};
        return `/namespace/${namespaceID}/attachment/${kind}/`;
    }
    // Attachment details
    attachmentRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { kind, namespaceID, attachmentID, sign, userID, } = a || {};
            if (!kind) {
                throw Error('field kind is empty');
            }
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!attachmentID) {
                throw Error('field attachmentID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.attachmentReadEndpoint({
                    kind, namespaceID, attachmentID,
                }) });
            cfg.params = {
                sign,
                userID,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    attachmentReadEndpoint(a) {
        const { kind, namespaceID, attachmentID, } = a || {};
        return `/namespace/${namespaceID}/attachment/${kind}/${attachmentID}`;
    }
    // Delete attachment
    attachmentDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { kind, namespaceID, attachmentID, sign, userID, } = a || {};
            if (!kind) {
                throw Error('field kind is empty');
            }
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!attachmentID) {
                throw Error('field attachmentID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.attachmentDeleteEndpoint({
                    kind, namespaceID, attachmentID,
                }) });
            cfg.params = {
                sign,
                userID,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    attachmentDeleteEndpoint(a) {
        const { kind, namespaceID, attachmentID, } = a || {};
        return `/namespace/${namespaceID}/attachment/${kind}/${attachmentID}`;
    }
    // Serves attached file
    attachmentOriginal(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { kind, namespaceID, attachmentID, name, sign, userID, download, } = a || {};
            if (!kind) {
                throw Error('field kind is empty');
            }
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!attachmentID) {
                throw Error('field attachmentID is empty');
            }
            if (!name) {
                throw Error('field name is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.attachmentOriginalEndpoint({
                    kind, namespaceID, attachmentID, name,
                }) });
            cfg.params = {
                sign,
                userID,
                download,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    attachmentOriginalEndpoint(a) {
        const { kind, namespaceID, attachmentID, name, } = a || {};
        return `/namespace/${namespaceID}/attachment/${kind}/${attachmentID}/original/${name}`;
    }
    // Serves preview of an attached file
    attachmentPreview(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { kind, namespaceID, attachmentID, ext, sign, userID, } = a || {};
            if (!kind) {
                throw Error('field kind is empty');
            }
            if (!namespaceID) {
                throw Error('field namespaceID is empty');
            }
            if (!attachmentID) {
                throw Error('field attachmentID is empty');
            }
            if (!ext) {
                throw Error('field ext is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.attachmentPreviewEndpoint({
                    kind, namespaceID, attachmentID, ext,
                }) });
            cfg.params = {
                sign,
                userID,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    attachmentPreviewEndpoint(a) {
        const { kind, namespaceID, attachmentID, ext, } = a || {};
        return `/namespace/${namespaceID}/attachment/${kind}/${attachmentID}/preview.${ext}`;
    }
    // Retrieve defined permissions
    permissionsList(extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.permissionsListEndpoint() });
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    permissionsListEndpoint() {
        return '/permissions/';
    }
    // Effective rules for current user
    permissionsEffective(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { resource, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.permissionsEffectiveEndpoint() });
            cfg.params = {
                resource,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    permissionsEffectiveEndpoint() {
        return '/permissions/effective';
    }
    // Retrieve role permissions
    permissionsRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.permissionsReadEndpoint({
                    roleID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    permissionsReadEndpoint(a) {
        const { roleID, } = a || {};
        return `/permissions/${roleID}/rules`;
    }
    // Remove all defined role permissions
    permissionsDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.permissionsDeleteEndpoint({
                    roleID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    permissionsDeleteEndpoint(a) {
        const { roleID, } = a || {};
        return `/permissions/${roleID}/rules`;
    }
    // Update permission settings
    permissionsUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, rules, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            if (!rules) {
                throw Error('field rules is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'patch', url: this.permissionsUpdateEndpoint({
                    roleID,
                }) });
            cfg.data = {
                rules,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    permissionsUpdateEndpoint(a) {
        const { roleID, } = a || {};
        return `/permissions/${roleID}/rules`;
    }
    // List all available automation scripts for compose resources
    automationList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { resourceTypePrefixes, resourceTypes, eventTypes, excludeInvalid, excludeClientScripts, excludeServerScripts, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.automationListEndpoint() });
            cfg.params = {
                resourceTypePrefixes,
                resourceTypes,
                eventTypes,
                excludeInvalid,
                excludeClientScripts,
                excludeServerScripts,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    automationListEndpoint() {
        return '/automation/';
    }
    // Serves client scripts bundle
    automationBundle(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { bundle, type, ext, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.automationBundleEndpoint({
                    bundle, type, ext,
                }) });
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    automationBundleEndpoint(a) {
        const { bundle, type, ext, } = a || {};
        return `/automation/${bundle}-${type}.${ext}`;
    }
    // Triggers execution of a specific script on a system service level
    automationTriggerScript(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { script, args, } = a || {};
            if (!script) {
                throw Error('field script is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.automationTriggerScriptEndpoint() });
            cfg.data = {
                script,
                args,
            };
            return this.api().request(cfg).then(result => stdResolve$1(result));
        });
    }
    automationTriggerScriptEndpoint() {
        return '/automation/trigger';
    }
}

/* eslint-disable padded-blocks */
function stdResolve$2(response) {
    if (response.data.error) {
        return Promise.reject(response.data.error);
    }
    else {
        return response.data.response;
    }
}
class Federation {
    constructor({ baseURL, headers, accessTokenFn }) {
        this.headers = {};
        this.baseURL = baseURL;
        this.accessTokenFn = accessTokenFn;
        this.headers = {
            /**
             * All we send is JSON
             */
            'Content-Type': 'application/json',
        };
        this.setHeaders(headers);
    }
    setAccessTokenFn(fn) {
        this.accessTokenFn = fn;
        return this;
    }
    setHeaders(headers) {
        if (typeof headers === 'object') {
            this.headers = headers;
        }
        return this;
    }
    setHeader(name, value) {
        if (value === undefined) {
            delete this.headers[name];
        }
        else {
            this.headers[name] = value;
        }
        return this;
    }
    api() {
        const headers = Object.assign({}, this.headers);
        const accessToken = this.accessTokenFn ? this.accessTokenFn() : undefined;
        if (accessToken) {
            headers.Authorization = 'Bearer ' + accessToken;
        }
        return axios.create({
            withCredentials: true,
            baseURL: this.baseURL,
            headers,
        });
    }
    // Initialize the handshake step with node B
    nodeHandshakeInitialize(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, pairToken, sharedNodeID, authToken, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            if (!pairToken) {
                throw Error('field pairToken is empty');
            }
            if (!sharedNodeID) {
                throw Error('field sharedNodeID is empty');
            }
            if (!authToken) {
                throw Error('field authToken is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.nodeHandshakeInitializeEndpoint({
                    nodeID,
                }) });
            cfg.data = {
                pairToken,
                sharedNodeID,
                authToken,
            };
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    nodeHandshakeInitializeEndpoint(a) {
        const { nodeID, } = a || {};
        return `/nodes/${nodeID}/handshake`;
    }
    // Search federated nodes
    nodeSearch(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { query, status, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.nodeSearchEndpoint() });
            cfg.params = {
                query,
                status,
            };
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    nodeSearchEndpoint() {
        return '/nodes/';
    }
    // Create a new federation node
    nodeCreate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { baseURL, name, contact, pairingURI, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.nodeCreateEndpoint() });
            cfg.data = {
                baseURL,
                name,
                contact,
                pairingURI,
            };
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    nodeCreateEndpoint() {
        return '/nodes/';
    }
    // Read a federation node
    nodeRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.nodeReadEndpoint({
                    nodeID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    nodeReadEndpoint(a) {
        const { nodeID, } = a || {};
        return `/nodes/${nodeID}`;
    }
    // Creates new sharable federation URI
    nodeGenerateUri(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.nodeGenerateUriEndpoint({
                    nodeID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    nodeGenerateUriEndpoint(a) {
        const { nodeID, } = a || {};
        return `/nodes/${nodeID}/uri`;
    }
    // Updates existing node
    nodeUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, name, contact, baseURL, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.nodeUpdateEndpoint({
                    nodeID,
                }) });
            cfg.data = {
                name,
                contact,
                baseURL,
            };
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    nodeUpdateEndpoint(a) {
        const { nodeID, } = a || {};
        return `/nodes/${nodeID}`;
    }
    // Deletes node
    nodeDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.nodeDeleteEndpoint({
                    nodeID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    nodeDeleteEndpoint(a) {
        const { nodeID, } = a || {};
        return `/nodes/${nodeID}`;
    }
    // Undeletes a node
    nodeUndelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.nodeUndeleteEndpoint({
                    nodeID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    nodeUndeleteEndpoint(a) {
        const { nodeID, } = a || {};
        return `/nodes/${nodeID}/undelete`;
    }
    // Initialize the pairing process between the two nodes
    nodePair(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.nodePairEndpoint({
                    nodeID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    nodePairEndpoint(a) {
        const { nodeID, } = a || {};
        return `/nodes/${nodeID}/pair`;
    }
    // Confirm the requested handshake
    nodeHandshakeConfirm(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.nodeHandshakeConfirmEndpoint({
                    nodeID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    nodeHandshakeConfirmEndpoint(a) {
        const { nodeID, } = a || {};
        return `/nodes/${nodeID}/handshake-confirm`;
    }
    // Complete the handshake
    nodeHandshakeComplete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, authToken, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            if (!authToken) {
                throw Error('field authToken is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.nodeHandshakeCompleteEndpoint({
                    nodeID,
                }) });
            cfg.data = {
                authToken,
            };
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    nodeHandshakeCompleteEndpoint(a) {
        const { nodeID, } = a || {};
        return `/nodes/${nodeID}/handshake-complete`;
    }
    // Exposed settings for module
    manageStructureReadExposed(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, moduleID, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.manageStructureReadExposedEndpoint({
                    nodeID, moduleID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    manageStructureReadExposedEndpoint(a) {
        const { nodeID, moduleID, } = a || {};
        return `/nodes/${nodeID}/modules/${moduleID}/exposed`;
    }
    // Add module to federation
    manageStructureCreateExposed(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, composeModuleID, composeNamespaceID, name, handle, fields, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            if (!composeModuleID) {
                throw Error('field composeModuleID is empty');
            }
            if (!composeNamespaceID) {
                throw Error('field composeNamespaceID is empty');
            }
            if (!name) {
                throw Error('field name is empty');
            }
            if (!handle) {
                throw Error('field handle is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'put', url: this.manageStructureCreateExposedEndpoint({
                    nodeID,
                }) });
            cfg.data = {
                composeModuleID,
                composeNamespaceID,
                name,
                handle,
                fields,
            };
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    manageStructureCreateExposedEndpoint(a) {
        const { nodeID, } = a || {};
        return `/nodes/${nodeID}/modules/`;
    }
    // Update already exposed module
    manageStructureUpdateExposed(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, moduleID, composeModuleID, composeNamespaceID, name, handle, fields, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            if (!composeModuleID) {
                throw Error('field composeModuleID is empty');
            }
            if (!composeNamespaceID) {
                throw Error('field composeNamespaceID is empty');
            }
            if (!name) {
                throw Error('field name is empty');
            }
            if (!handle) {
                throw Error('field handle is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.manageStructureUpdateExposedEndpoint({
                    nodeID, moduleID,
                }) });
            cfg.data = {
                composeModuleID,
                composeNamespaceID,
                name,
                handle,
                fields,
            };
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    manageStructureUpdateExposedEndpoint(a) {
        const { nodeID, moduleID, } = a || {};
        return `/nodes/${nodeID}/modules/${moduleID}/exposed`;
    }
    // Remove from federation
    manageStructureRemoveExposed(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, moduleID, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.manageStructureRemoveExposedEndpoint({
                    nodeID, moduleID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    manageStructureRemoveExposedEndpoint(a) {
        const { nodeID, moduleID, } = a || {};
        return `/nodes/${nodeID}/modules/${moduleID}/exposed`;
    }
    // Shared settings for module
    manageStructureReadShared(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, moduleID, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.manageStructureReadSharedEndpoint({
                    nodeID, moduleID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    manageStructureReadSharedEndpoint(a) {
        const { nodeID, moduleID, } = a || {};
        return `/nodes/${nodeID}/modules/${moduleID}/shared`;
    }
    // Add fields mappings to federated module
    manageStructureCreateMappings(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, moduleID, composeModuleID, composeNamespaceID, fields, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            if (!composeModuleID) {
                throw Error('field composeModuleID is empty');
            }
            if (!composeNamespaceID) {
                throw Error('field composeNamespaceID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'put', url: this.manageStructureCreateMappingsEndpoint({
                    nodeID, moduleID,
                }) });
            cfg.data = {
                composeModuleID,
                composeNamespaceID,
                fields,
            };
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    manageStructureCreateMappingsEndpoint(a) {
        const { nodeID, moduleID, } = a || {};
        return `/nodes/${nodeID}/modules/${moduleID}/mapped`;
    }
    // Fields mappings for module
    manageStructureReadMappings(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, moduleID, composeModuleID, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.manageStructureReadMappingsEndpoint({
                    nodeID, moduleID,
                }) });
            cfg.params = {
                composeModuleID,
            };
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    manageStructureReadMappingsEndpoint(a) {
        const { nodeID, moduleID, } = a || {};
        return `/nodes/${nodeID}/modules/${moduleID}/mapped`;
    }
    // List of shared/exposed/mapped modules
    manageStructureListAll(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, shared, exposed, mapped, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.manageStructureListAllEndpoint({
                    nodeID,
                }) });
            cfg.params = {
                shared,
                exposed,
                mapped,
            };
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    manageStructureListAllEndpoint(a) {
        const { nodeID, } = a || {};
        return `/nodes/${nodeID}/modules/`;
    }
    // List all exposed modules changes
    syncStructureReadExposedInternal(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, lastSync, query, limit, pageCursor, sort, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.syncStructureReadExposedInternalEndpoint({
                    nodeID,
                }) });
            cfg.params = {
                lastSync,
                query,
                limit,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    syncStructureReadExposedInternalEndpoint(a) {
        const { nodeID, } = a || {};
        return `/nodes/${nodeID}/modules/exposed/`;
    }
    // List all exposed modules changes in activity streams format
    syncStructureReadExposedSocial(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, lastSync, query, limit, pageCursor, sort, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.syncStructureReadExposedSocialEndpoint({
                    nodeID,
                }) });
            cfg.params = {
                lastSync,
                query,
                limit,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    syncStructureReadExposedSocialEndpoint(a) {
        const { nodeID, } = a || {};
        return `/nodes/${nodeID}/modules/exposed/activity-stream`;
    }
    // List all record changes
    syncDataReadExposedAll(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, lastSync, query, limit, pageCursor, sort, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.syncDataReadExposedAllEndpoint({
                    nodeID,
                }) });
            cfg.params = {
                lastSync,
                query,
                limit,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    syncDataReadExposedAllEndpoint(a) {
        const { nodeID, } = a || {};
        return `/nodes/${nodeID}/modules/exposed/records/`;
    }
    // List all records per module
    syncDataReadExposedInternal(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, moduleID, lastSync, query, limit, pageCursor, sort, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.syncDataReadExposedInternalEndpoint({
                    nodeID, moduleID,
                }) });
            cfg.params = {
                lastSync,
                query,
                limit,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    syncDataReadExposedInternalEndpoint(a) {
        const { nodeID, moduleID, } = a || {};
        return `/nodes/${nodeID}/modules/${moduleID}/records/`;
    }
    // List all records per module in activitystreams format
    syncDataReadExposedSocial(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nodeID, moduleID, lastSync, query, limit, pageCursor, sort, } = a || {};
            if (!nodeID) {
                throw Error('field nodeID is empty');
            }
            if (!moduleID) {
                throw Error('field moduleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.syncDataReadExposedSocialEndpoint({
                    nodeID, moduleID,
                }) });
            cfg.params = {
                lastSync,
                query,
                limit,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    syncDataReadExposedSocialEndpoint(a) {
        const { nodeID, moduleID, } = a || {};
        return `/nodes/${nodeID}/modules/${moduleID}/records/activity-stream/`;
    }
    // Retrieve defined permissions
    permissionsList(extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.permissionsListEndpoint() });
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    permissionsListEndpoint() {
        return '/permissions/';
    }
    // Effective rules for current user
    permissionsEffective(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { resource, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.permissionsEffectiveEndpoint() });
            cfg.params = {
                resource,
            };
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    permissionsEffectiveEndpoint() {
        return '/permissions/effective';
    }
    // Retrieve role permissions
    permissionsRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.permissionsReadEndpoint({
                    roleID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    permissionsReadEndpoint(a) {
        const { roleID, } = a || {};
        return `/permissions/${roleID}/rules`;
    }
    // Remove all defined role permissions
    permissionsDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.permissionsDeleteEndpoint({
                    roleID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    permissionsDeleteEndpoint(a) {
        const { roleID, } = a || {};
        return `/permissions/${roleID}/rules`;
    }
    // Update permission settings
    permissionsUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, rules, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            if (!rules) {
                throw Error('field rules is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'patch', url: this.permissionsUpdateEndpoint({
                    roleID,
                }) });
            cfg.data = {
                rules,
            };
            return this.api().request(cfg).then(result => stdResolve$2(result));
        });
    }
    permissionsUpdateEndpoint(a) {
        const { roleID, } = a || {};
        return `/permissions/${roleID}/rules`;
    }
}

/* eslint-disable padded-blocks */
function stdResolve$3(response) {
    if (response.data.error) {
        return Promise.reject(response.data.error);
    }
    else {
        return response.data.response;
    }
}
class Automation {
    constructor({ baseURL, headers, accessTokenFn }) {
        this.headers = {};
        this.baseURL = baseURL;
        this.accessTokenFn = accessTokenFn;
        this.headers = {
            /**
             * All we send is JSON
             */
            'Content-Type': 'application/json',
        };
        this.setHeaders(headers);
    }
    setAccessTokenFn(fn) {
        this.accessTokenFn = fn;
        return this;
    }
    setHeaders(headers) {
        if (typeof headers === 'object') {
            this.headers = headers;
        }
        return this;
    }
    setHeader(name, value) {
        if (value === undefined) {
            delete this.headers[name];
        }
        else {
            this.headers[name] = value;
        }
        return this;
    }
    api() {
        const headers = Object.assign({}, this.headers);
        const accessToken = this.accessTokenFn ? this.accessTokenFn() : undefined;
        if (accessToken) {
            headers.Authorization = 'Bearer ' + accessToken;
        }
        return axios.create({
            withCredentials: true,
            baseURL: this.baseURL,
            headers,
        });
    }
    // List workflows
    workflowList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { workflowID, query, deleted, disabled, labels, limit, pageCursor, sort, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.workflowListEndpoint() });
            cfg.params = {
                workflowID,
                query,
                deleted,
                disabled,
                labels,
                limit,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    workflowListEndpoint() {
        return '/workflows/';
    }
    // Create workflow
    workflowCreate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { handle, labels, meta, enabled, trace, keepSessions, scope, steps, paths, runAs, ownedBy, } = a || {};
            if (!handle) {
                throw Error('field handle is empty');
            }
            if (!runAs) {
                throw Error('field runAs is empty');
            }
            if (!ownedBy) {
                throw Error('field ownedBy is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.workflowCreateEndpoint() });
            cfg.data = {
                handle,
                labels,
                meta,
                enabled,
                trace,
                keepSessions,
                scope,
                steps,
                paths,
                runAs,
                ownedBy,
            };
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    workflowCreateEndpoint() {
        return '/workflows/';
    }
    // Update triger details
    workflowUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { workflowID, handle, labels, meta, enabled, trace, keepSessions, scope, steps, paths, runAs, ownedBy, } = a || {};
            if (!workflowID) {
                throw Error('field workflowID is empty');
            }
            if (!handle) {
                throw Error('field handle is empty');
            }
            if (!runAs) {
                throw Error('field runAs is empty');
            }
            if (!ownedBy) {
                throw Error('field ownedBy is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'put', url: this.workflowUpdateEndpoint({
                    workflowID,
                }) });
            cfg.data = {
                handle,
                labels,
                meta,
                enabled,
                trace,
                keepSessions,
                scope,
                steps,
                paths,
                runAs,
                ownedBy,
            };
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    workflowUpdateEndpoint(a) {
        const { workflowID, } = a || {};
        return `/workflows/${workflowID}`;
    }
    // Read workflow details
    workflowRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { workflowID, } = a || {};
            if (!workflowID) {
                throw Error('field workflowID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.workflowReadEndpoint({
                    workflowID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    workflowReadEndpoint(a) {
        const { workflowID, } = a || {};
        return `/workflows/${workflowID}`;
    }
    // Remove workflow
    workflowDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { workflowID, } = a || {};
            if (!workflowID) {
                throw Error('field workflowID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.workflowDeleteEndpoint({
                    workflowID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    workflowDeleteEndpoint(a) {
        const { workflowID, } = a || {};
        return `/workflows/${workflowID}`;
    }
    // Undelete workflow
    workflowUndelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { workflowID, } = a || {};
            if (!workflowID) {
                throw Error('field workflowID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.workflowUndeleteEndpoint({
                    workflowID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    workflowUndeleteEndpoint(a) {
        const { workflowID, } = a || {};
        return `/workflows/${workflowID}/undelete`;
    }
    // Test workflow details
    workflowTest(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { workflowID, scope, runAs, } = a || {};
            if (!workflowID) {
                throw Error('field workflowID is empty');
            }
            if (!runAs) {
                throw Error('field runAs is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.workflowTestEndpoint({
                    workflowID,
                }) });
            cfg.data = {
                scope,
                runAs,
            };
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    workflowTestEndpoint(a) {
        const { workflowID, } = a || {};
        return `/workflows/${workflowID}/test`;
    }
    // Executes workflow on a specific step (must be orphan step and connected to &#x27;onManual&#x27; trigger)
    workflowExec(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { workflowID, stepID, input, trace, wait, async, } = a || {};
            if (!workflowID) {
                throw Error('field workflowID is empty');
            }
            if (!stepID) {
                throw Error('field stepID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.workflowExecEndpoint({
                    workflowID,
                }) });
            cfg.data = {
                stepID,
                input,
                trace,
                wait,
                async,
            };
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    workflowExecEndpoint(a) {
        const { workflowID, } = a || {};
        return `/workflows/${workflowID}/exec`;
    }
    // List triggers
    triggerList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { triggerID, workflowID, deleted, disabled, eventType, resourceType, query, labels, limit, pageCursor, sort, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.triggerListEndpoint() });
            cfg.params = {
                triggerID,
                workflowID,
                deleted,
                disabled,
                eventType,
                resourceType,
                query,
                labels,
                limit,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    triggerListEndpoint() {
        return '/triggers/';
    }
    // Create trigger
    triggerCreate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { eventType, resourceType, enabled, workflowID, workflowStepID, input, labels, meta, constraints, ownedBy, } = a || {};
            if (!eventType) {
                throw Error('field eventType is empty');
            }
            if (!resourceType) {
                throw Error('field resourceType is empty');
            }
            if (!workflowID) {
                throw Error('field workflowID is empty');
            }
            if (!workflowStepID) {
                throw Error('field workflowStepID is empty');
            }
            if (!ownedBy) {
                throw Error('field ownedBy is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.triggerCreateEndpoint() });
            cfg.data = {
                eventType,
                resourceType,
                enabled,
                workflowID,
                workflowStepID,
                input,
                labels,
                meta,
                constraints,
                ownedBy,
            };
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    triggerCreateEndpoint() {
        return '/triggers/';
    }
    // Update trigger details
    triggerUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { triggerID, eventType, resourceType, enabled, workflowID, workflowStepID, input, labels, meta, constraints, ownedBy, } = a || {};
            if (!triggerID) {
                throw Error('field triggerID is empty');
            }
            if (!eventType) {
                throw Error('field eventType is empty');
            }
            if (!resourceType) {
                throw Error('field resourceType is empty');
            }
            if (!workflowID) {
                throw Error('field workflowID is empty');
            }
            if (!workflowStepID) {
                throw Error('field workflowStepID is empty');
            }
            if (!ownedBy) {
                throw Error('field ownedBy is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'put', url: this.triggerUpdateEndpoint({
                    triggerID,
                }) });
            cfg.data = {
                eventType,
                resourceType,
                enabled,
                workflowID,
                workflowStepID,
                input,
                labels,
                meta,
                constraints,
                ownedBy,
            };
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    triggerUpdateEndpoint(a) {
        const { triggerID, } = a || {};
        return `/triggers/${triggerID}`;
    }
    // Read trigger details
    triggerRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { triggerID, } = a || {};
            if (!triggerID) {
                throw Error('field triggerID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.triggerReadEndpoint({
                    triggerID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    triggerReadEndpoint(a) {
        const { triggerID, } = a || {};
        return `/triggers/${triggerID}`;
    }
    // Remove trigger
    triggerDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { triggerID, } = a || {};
            if (!triggerID) {
                throw Error('field triggerID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.triggerDeleteEndpoint({
                    triggerID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    triggerDeleteEndpoint(a) {
        const { triggerID, } = a || {};
        return `/triggers/${triggerID}`;
    }
    // Undelete trigger
    triggerUndelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { triggerID, } = a || {};
            if (!triggerID) {
                throw Error('field triggerID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.triggerUndeleteEndpoint({
                    triggerID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    triggerUndeleteEndpoint(a) {
        const { triggerID, } = a || {};
        return `/triggers/${triggerID}/undelete`;
    }
    // List sessions
    sessionList(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { sessionID, workflowID, createdBy, completed, status, eventType, resourceType, limit, pageCursor, sort, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.sessionListEndpoint() });
            cfg.params = {
                sessionID,
                workflowID,
                createdBy,
                completed,
                status,
                eventType,
                resourceType,
                limit,
                pageCursor,
                sort,
            };
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    sessionListEndpoint() {
        return '/sessions/';
    }
    // Read session details
    sessionRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { sessionID, } = a || {};
            if (!sessionID) {
                throw Error('field sessionID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.sessionReadEndpoint({
                    sessionID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    sessionReadEndpoint(a) {
        const { sessionID, } = a || {};
        return `/sessions/${sessionID}`;
    }
    // Read session trace info
    sessionTrace(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { sessionID, } = a || {};
            if (!sessionID) {
                throw Error('field sessionID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.sessionTraceEndpoint({
                    sessionID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    sessionTraceEndpoint(a) {
        const { sessionID, } = a || {};
        return `/sessions/${sessionID}/trace`;
    }
    // Remove session
    sessionDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { sessionID, } = a || {};
            if (!sessionID) {
                throw Error('field sessionID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.sessionDeleteEndpoint({
                    sessionID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    sessionDeleteEndpoint(a) {
        const { sessionID, } = a || {};
        return `/sessions/${sessionID}`;
    }
    // Returns pending prompts from all sessions
    sessionListPrompts(extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.sessionListPromptsEndpoint() });
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    sessionListPromptsEndpoint() {
        return '/sessions/prompts';
    }
    // Resume session
    sessionResumeState(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { sessionID, stateID, input, } = a || {};
            if (!sessionID) {
                throw Error('field sessionID is empty');
            }
            if (!stateID) {
                throw Error('field stateID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'post', url: this.sessionResumeStateEndpoint({
                    sessionID, stateID,
                }) });
            cfg.data = {
                input,
            };
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    sessionResumeStateEndpoint(a) {
        const { sessionID, stateID, } = a || {};
        return `/sessions/${sessionID}/state/${stateID}`;
    }
    // Cancel session&#x27;s state
    sessionDeleteState(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { sessionID, stateID, } = a || {};
            if (!sessionID) {
                throw Error('field sessionID is empty');
            }
            if (!stateID) {
                throw Error('field stateID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.sessionDeleteStateEndpoint({
                    sessionID, stateID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    sessionDeleteStateEndpoint(a) {
        const { sessionID, stateID, } = a || {};
        return `/sessions/${sessionID}/state/${stateID}`;
    }
    // Available workflow functions
    functionList(extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.functionListEndpoint() });
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    functionListEndpoint() {
        return '/functions/';
    }
    // Available workflow types
    typeList(extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.typeListEndpoint() });
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    typeListEndpoint() {
        return '/types/';
    }
    // Available workflow types
    eventTypesList(extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.eventTypesListEndpoint() });
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    eventTypesListEndpoint() {
        return '/event-types/';
    }
    // Retrieve defined permissions
    permissionsList(extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.permissionsListEndpoint() });
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    permissionsListEndpoint() {
        return '/permissions/';
    }
    // Effective rules for current user
    permissionsEffective(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { resource, } = a || {};
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.permissionsEffectiveEndpoint() });
            cfg.params = {
                resource,
            };
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    permissionsEffectiveEndpoint() {
        return '/permissions/effective';
    }
    // Retrieve role permissions
    permissionsRead(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'get', url: this.permissionsReadEndpoint({
                    roleID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    permissionsReadEndpoint(a) {
        const { roleID, } = a || {};
        return `/permissions/${roleID}/rules`;
    }
    // Remove all defined role permissions
    permissionsDelete(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'delete', url: this.permissionsDeleteEndpoint({
                    roleID,
                }) });
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    permissionsDeleteEndpoint(a) {
        const { roleID, } = a || {};
        return `/permissions/${roleID}/rules`;
    }
    // Update permission settings
    permissionsUpdate(a, extra = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { roleID, rules, } = a || {};
            if (!roleID) {
                throw Error('field roleID is empty');
            }
            if (!rules) {
                throw Error('field rules is empty');
            }
            const cfg = Object.assign(Object.assign({}, extra), { method: 'patch', url: this.permissionsUpdateEndpoint({
                    roleID,
                }) });
            cfg.data = {
                rules,
            };
            return this.api().request(cfg).then(result => stdResolve$3(result));
        });
    }
    permissionsUpdateEndpoint(a) {
        const { roleID, } = a || {};
        return `/permissions/${roleID}/rules`;
    }
}



var index$6 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  System: System,
  Compose: Compose,
  Federation: Federation,
  Automation: Automation
});

/**
 * Handles script execution context
 *
 * Context accepts pre-assembled *API props or it construct them fly from passed config
 *
 * Naming convention for properties:
 *  - Corteza classes, high-level helpers, API clients are upper cased
 *  - low-level helpers are lower cased
 *  - simple scalar are lower cased
 *  - $authUser is the only one prefixed with the dollar sign for historical reasons
 */
class Ctx {
    constructor(args, logger, a) {
        this.args = args;
        this.logger = logger;
        if (a) {
            Object.assign(this, a);
        }
    }
    /**
     * Alias for log, to make developer's life easier <3
     */
    get console() {
        return this.logger;
    }
    /**
     * Alias for log, to make developer's life easier <3
     */
    get log() {
        return this.logger;
    }
    /**
     * Returns promise with the current user (if authToken argument was given)
     *
     * This is a temporary solution that decodes the userID from the access token (JWT)
     * and fetches the user info
     *
     * @returns {Promise<User>}
     */
    get $authUser() {
        const [, payload] = this.args.authToken.split('.');
        // eslint-disable-next-line node/no-deprecated-api
        const buf = new Buffer(payload, 'base64');
        const { sub: userID } = JSON.parse(buf.toString('ascii'));
        return this.SystemAPI.userRead({ userID }).then(r => new User(r));
    }
    /**
     * Configures and returns system API client
     */
    get SystemAPI() {
        var _a, _b;
        if (!this.systemAPI) {
            if (!((_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.cServers) === null || _b === void 0 ? void 0 : _b.system)) {
                throw new Error('configuration for corteza system server missing');
            }
            this.systemAPI = new System({
                baseURL: this.config.cServers.system.apiBaseURL,
                accessTokenFn: () => this.args.authToken,
            });
        }
        return this.systemAPI;
    }
    /**
     * Configures and returns compose API client
     */
    get ComposeAPI() {
        var _a, _b;
        if (!this.composeAPI) {
            if (!((_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.cServers) === null || _b === void 0 ? void 0 : _b.compose)) {
                throw new Error('configuration for corteza compose server missing');
            }
            this.composeAPI = new Compose({
                baseURL: this.config.cServers.compose.apiBaseURL,
                accessTokenFn: () => this.args.authToken,
            });
        }
        return this.composeAPI;
    }
    /**
     * Configures and returns system helper
     */
    get System() {
        return new SystemHelper(Object.assign({ SystemAPI: this.SystemAPI }, this.args));
    }
    /**
     * Configures and returns compose helper
     */
    get Compose() {
        return new ComposeHelper(Object.assign({ ComposeAPI: this.ComposeAPI }, this.args));
    }
    /**
     *
     */
    get frontendBaseURL() {
        var _a, _b;
        return (_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.frontend) === null || _b === void 0 ? void 0 : _b.baseURL;
    }
}



var index$7 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Args: Args,
  ArgsProxy: ArgsProxy,
  CortezaTypes: CortezaTypes,
  Ctx: Ctx,
  Exec: Exec,
  SystemHelper: SystemHelper,
  ComposeHelper: ComposeHelper
});

class Workflow {
    constructor(w) {
        this.workflowID = NoID;
        this.handle = '';
        this.enabled = false;
        this.labels = {};
        this.meta = {};
        this.runAs = NoID;
        this.ownedBy = NoID;
        this.createdBy = NoID;
        this.createdAt = undefined;
        this.updatedAt = undefined;
        this.deletedAt = undefined;
        this.apply(w);
    }
    apply(w) {
        Apply(this, w, CortezaID, 'workflowID');
        Apply(this, w, String, 'handle');
        Apply(this, w, Boolean, 'enabled');
        Apply(this, w, ISO8601Date, 'createdAt', 'updatedAt', 'deletedAt');
        Apply(this, w, CortezaID, 'runAs', 'ownedBy', 'createdBy');
        if (IsOf(w, 'meta')) {
            this.meta = Object.assign({}, w.meta);
        }
        if (IsOf(w, 'labels')) {
            this.labels = Object.assign({}, w.labels);
        }
    }
    /**
     * Returns resource ID
     */
    get resourceID() {
        return `${this.resourceType}:${this.workflowID}`;
    }
    /**
     * Resource type
     */
    get resourceType() {
        return 'automation:workflow';
    }
}

class Param {
    constructor(u) {
        this.name = '';
        this.types = [];
        this.required = false;
        this.isArray = false;
        this.meta = {};
        this.apply(u);
    }
    apply(u) {
        var _a, _b;
        Apply(this, u, String, 'name');
        Apply(this, u, Boolean, 'required', 'isArray');
        if ((_a = u) === null || _a === void 0 ? void 0 : _a.types) {
            this.types = u.types;
        }
        if ((_b = u) === null || _b === void 0 ? void 0 : _b.meta) {
            this.meta = Object.assign({}, u.meta);
        }
    }
}

class Function$1 {
    constructor(u) {
        this.ref = '';
        this.kind = '';
        this.meta = {};
        this.parameters = [];
        this.results = [];
        this.labels = {};
        this.apply(u);
    }
    apply(u) {
        var _a, _b, _c, _d;
        Apply(this, u, String, 'ref', 'kind');
        if ((_a = u) === null || _a === void 0 ? void 0 : _a.parameters) {
            this.parameters = u.parameters.map(p => new Param(p));
        }
        if ((_b = u) === null || _b === void 0 ? void 0 : _b.results) {
            this.results = u.results.map(p => new Param(p));
        }
        if ((_c = u) === null || _c === void 0 ? void 0 : _c.meta) {
            this.meta = Object.assign({}, u.meta);
        }
        if ((_d = u) === null || _d === void 0 ? void 0 : _d.labels) {
            this.labels = Object.assign({}, u.labels);
        }
    }
}

class Prompt {
    constructor(u) {
        this.ref = '';
        this.sessionID = NoID;
        this.stateID = NoID;
        this.createdAt = undefined;
        this.payload = undefined;
        this.apply(u);
    }
    apply(u) {
        var _a;
        Apply(this, u, CortezaID, 'sessionID', 'stateID');
        Apply(this, u, String, 'ref');
        Apply(this, u, ISO8601Date, 'createdAt');
        if ((_a = u) === null || _a === void 0 ? void 0 : _a.payload) {
            this.payload = u.payload;
        }
    }
}

function IsTyped(a) {
    return typeof a &&
        a === 'object' &&
        Object.prototype.hasOwnProperty.call(a, '@type') &&
        Object.prototype.hasOwnProperty.call(a, '@value');
}
function unwrap(v) {
    return IsTyped(v) ? v['@value'] : v;
}
function cast(v) {
    return { '@value': unwrap(v), '@type': guessType(v) };
}
function guessType(v) {
    switch (typeof v) {
        case 'boolean':
            return 'Boolean';
        case 'string':
            return 'String';
        case 'number':
            return Number(v) === v && v % 1 === 0 ? 'Float' : 'Integer';
        case 'object':
            if (v.resourceType) {
                // converts foo:bar into FooBar
                return v.resourceType.split(':').map(capitalize).join('');
            }
            return 'Any';
        default:
            return 'Any';
    }
}
/**
 *
 * @param any
 * @constructor
 */
function Encode(input) {
    const output = {};
    for (const key in input) {
        output[key] = IsTyped(input[key]) ? input[key] : cast(input[key]);
    }
    return output;
}



var index$8 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Workflow: Workflow,
  Param: Param,
  Function: Function$1,
  Prompt: Prompt,
  IsTyped: IsTyped,
  Encode: Encode
});

export { NoID, index$6 as apiClients, index$8 as automation, index$5 as compose, index$7 as corredor, index as eventbus, index$4 as fmt, index$1 as reporter, index$3 as shared, index$2 as system, validator };
//# sourceMappingURL=index.es.js.map
